import React, { useState, useRef, useEffect, useCallback } from 'react'
import danMillerPhoto from '@/imports/dan-miller-organ-8-2-691x800.jpg'
import connectLogo from '@/imports/connect_5_3x.png'
import hectorPhoto from '@/imports/hector.jpeg'
import janPhoto from '@/imports/jan.jpeg'
import rodgersLogo from '@/imports/rodgers-logo.png'
import img367 from '@/imports/367---banner-1.png'
import img489 from '@/imports/361-knee-high-banner-2.png'
import img359 from '@/imports/knee-banner-6.png'
import img241 from '@/imports/241-banner.png'
import img239 from '@/imports/knee-banner-4.png'
import img234 from '@/imports/227-banner-6.png'
import img240 from '@/imports/233-banner-3.png'
import img352 from '@/imports/inspire-series-343-13.png'
import img232 from '@/imports/american-232.png'
import img342 from '@/imports/american-342.png'
import imgHybrid from '@/imports/hybrid-organs.png'
import logoModel367 from '@/imports/367-transparent-WHITE-1.png'
import logoModel489 from '@/imports/489-Transparent-WHITE__1_-1.png'
import logoSeriesImagine from '@/imports/Imagine-Rodgers-1.png'
import logoSeriesInfinity from '@/imports/Infinity_Series-White-1.png'
import logoSeriesInspire from '@/imports/Inspire_Sereis-WHT-1.png'
import heroOrganistPhoto from '@/imports/image-5.png'
import organConsolePhoto from '@/imports/image-2.png'
import rodgersPlayLogo from '@/imports/rplayblk.png'

const MODEL_IMAGES: Record<string, string> = {
  'Infinity Series 367': img367,
  'Infinity Series 489': img489,
  'Imagine Series 359': img359,
  'Imagine Series 241': img241,
  'Imagine Series 239': img239,
  'Inspire Series 234': img234,
  'Inspire Series 240': img240,
  'Inspire Series 352': img352,
  'The American Series 232': img232,
  'The American Series 342': img342,
  'Rodgers Hybrid Organs': imgHybrid,
}

// Series wordmark logos (white transparent PNGs — for dark backgrounds)
const SERIES_LOGOS: Record<string, string> = {
  'Infinity':        logoSeriesInfinity,
  'Imagine':         logoSeriesImagine,
  'Inspire':         logoSeriesInspire,
}

// Model-specific lockup logos (white transparent PNGs)
const MODEL_LOGOS: Record<string, string> = {
  '367': logoModel367,
  '489': logoModel489,
}

type Category = 'All' | 'Preludes / Offertories' | 'Postludes' | 'Hymns and Reharmonizations' | 'Concert Repertoire' | 'Contemporary' | 'Liturgy' | 'Other'

type RodgersInstrument = {
  id: string
  series: 'Inspire' | 'Imagine' | 'Infinity'
  model: string
  nickname: string
  church: string
  softwareVersion: string
  primary: boolean
}

type Profile = {
  id: string
  name: string
  email: string
  musicalRole: string
  organModel: string
  location: string
  church: string
  avatar: string
  yearsPlaying: string
  interests: string[]
  series: string
  instruments?: RodgersInstrument[]
}

// In-memory mailing list — persists for the session
const mailingList: string[] = []

// Baked in at build time (see frontend/Dockerfile's VITE_SHOW_EXPERIMENTAL build arg,
// wired per-environment in docker-compose.yml). True on dev, false on staging/prod. Any
// UI that isn't backed by a real endpoint yet -- most of the Community/Monthly/Featured
// content imported from Figma -- gets wrapped in <Experimental> rather than shown
// unconditionally, so a new design drop is invisible outside dev by default until its
// backend is actually built and wired up, instead of relying on remembering to hide it.
const SHOW_EXPERIMENTAL = import.meta.env.VITE_SHOW_EXPERIMENTAL === 'true'

const Experimental = ({ children }: { children: React.ReactNode }) => (
  SHOW_EXPERIMENTAL ? <>{children}</> : null
)

// --- Backend auth ---
const TOKEN_KEY = 'rc_token'

type BackendUser = {
  id: string
  email: string
  display_name: string
  is_admin: boolean
  musical_role: string
  organ_model: string
  location: string
  church: string
  years_playing: string
  avatar: string
}

function backendUserToProfile(u: BackendUser): Profile {
  return {
    id: u.id,
    name: u.display_name,
    email: u.email,
    musicalRole: u.musical_role || MUSICAL_ROLES[0],
    organModel: u.organ_model || '',
    location: u.location,
    church: u.church,
    avatar: u.avatar,
    yearsPlaying: u.years_playing,
    interests: [],
    series: '',
    instruments: [],
  }
}

class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

async function apiSignup(email: string, password: string, displayName: string, avatar?: string) {
  const res = await fetch('/api/auth/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, display_name: displayName, avatar: avatar || '' }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body?.detail || 'Signup failed')
  }
  return res.json() as Promise<{ access_token: string; user: BackendUser }>
}

async function apiLogin(email: string, password: string) {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ username: email, password }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body?.detail || 'Incorrect email or password')
  }
  return res.json() as Promise<{ access_token: string; user: BackendUser }>
}

async function apiForgotPassword(email: string) {
  const res = await fetch('/api/auth/forgot-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  // Always succeeds from the caller's perspective (the backend intentionally returns the
  // same response whether or not that email is registered, so this can't be used to check
  // who has an account) -- only a real request failure (network, 5xx) throws.
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body?.detail || 'Something went wrong. Please try again.')
  }
}

async function apiResetPassword(token: string, newPassword: string) {
  const res = await fetch('/api/auth/reset-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body?.detail || 'Could not reset your password.')
  }
}

/** Try to create an account; if the email is already registered, fall back to logging in
 *  with the entered password. Lets one form serve both new and returning users — `isNewAccount`
 *  tells the caller which happened, so a returning user can skip onboarding they've already done. */
async function signupOrLogin(email: string, password: string, displayName: string, avatar?: string): Promise<{ user: BackendUser; isNewAccount: boolean }> {
  try {
    const { access_token, user } = await apiSignup(email, password, displayName, avatar)
    localStorage.setItem(TOKEN_KEY, access_token)
    return { user, isNewAccount: true }
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      const { access_token, user } = await apiLogin(email, password)
      localStorage.setItem(TOKEN_KEY, access_token)
      return { user, isNewAccount: false }
    }
    throw err
  }
}

async function apiFetchMe(token: string) {
  const res = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
  if (!res.ok) throw new ApiError(res.status, 'Session expired')
  return res.json() as Promise<BackendUser>
}

async function apiLogout(token: string) {
  // Best-effort — the endpoint is a no-op anyway (JWT is stateless), so a network hiccup
  // here shouldn't stop the local logout from proceeding.
  await fetch('/api/auth/logout', { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).catch(() => {})
}

type ProfileUpdateFields = {
  display_name: string
  musical_role: string
  organ_model: string
  location: string
  church: string
  years_playing: string
  avatar: string
}

async function apiUpdateProfile(token: string, fields: ProfileUpdateFields) {
  const res = await fetch('/api/auth/me', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(fields),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body?.detail || 'Could not save changes')
  }
  return res.json() as Promise<BackendUser>
}

async function apiDeleteAccount(token: string) {
  const res = await fetch('/api/auth/me', { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
  if (!res.ok) throw new ApiError(res.status, 'Could not delete account')
}

type BackendRecording = {
  id: string
  title: string
  composer: string
  category: string
  description: string
  organ_model: string
  tags: string
  download_count: number
  uploaded_at: string
  owner_id: string
  owner_display_name: string
  audio_filename: string
  audio_format: string
  audio_size_bytes: number
  midi_filename: string
  midi_size_bytes: number
  pdf_filename: string
  pdf_size_bytes: number
  rpl_filename: string
  rpl_size_bytes: number
}

/** Uploads a performance carrying any combination of audio/midi/pdf/rpl -- at least one
 *  of the four files is required, enforced backend-side. */
async function apiUploadFile(
  token: string,
  files: { audio?: File; midi?: File; pdf?: File; rpl?: File },
  fields: { title: string; composer: string; category: string; description: string; organModel?: string; tags?: string },
) {
  const body = new FormData()
  if (files.audio) body.append('audio', files.audio)
  if (files.midi) body.append('midi', files.midi)
  if (files.pdf) body.append('pdf', files.pdf)
  if (files.rpl) body.append('rpl', files.rpl)
  body.append('title', fields.title)
  body.append('composer', fields.composer)
  body.append('category', fields.category)
  body.append('description', fields.description)
  if (fields.organModel) body.append('organ_model', fields.organModel)
  if (fields.tags) body.append('tags', fields.tags)
  const res = await fetch('/api/files/upload', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body })
  if (!res.ok) {
    const errBody = await res.json().catch(() => null)
    throw new ApiError(res.status, errBody?.detail || 'Upload failed')
  }
  return res.json() as Promise<BackendRecording>
}

async function apiDownloadUrl(fileId: string, kind: 'audio' | 'midi' | 'pdf' | 'rpl') {
  const res = await fetch(`/api/files/${fileId}/download?kind=${kind}`)
  if (!res.ok) throw new ApiError(res.status, 'Could not get download link')
  const body = await res.json() as { download_url: string }
  return body.download_url
}

/** Whether the signed-in user (by real backend id) owns this file. Prefers the real
 *  ownerId comparison whenever it's available (any real backend-uploaded file); falls back
 *  to the local "uploader === 'You'" sentinel only for the still-mock, no-backend-format
 *  video-link-only submissions that never get a real ownerId at all. The
 *  sentinel-only check is what let a file uploaded under one account keep showing as
 *  "mine" after logging into a different account in the same browser session -- it doesn't
 *  know WHICH "you" uploaded it, ownerId does. */
function isOwnFile(file: FileEntry, myProfileId: string | undefined): boolean {
  if (file.ownerId) return file.ownerId === myProfileId
  return file.uploader === 'You'
}

/** Whether a file can be piped into an <audio> element at all -- a real backend file always
 *  gets a remoteId regardless of format (PDFs and MIDI included), so format has to be
 *  checked too, not just "is there something to fetch". */
function isPlayableAudio(file: FileEntry): boolean {
  if (file.format !== 'MP3' && file.format !== 'WAV') return false
  return !!file.src || !!file.remoteId
}

/** Resolves a file to something an <audio> element (or a download link) can actually use.
 *  For a real backend file this fetches a fresh presigned URL at the point of use rather
 *  than caching one -- those expire in 5 minutes. Falls back to the local blob/bundled URL
 *  for still-mock entries. */
async function resolvePlaybackSrc(file: FileEntry): Promise<string | undefined> {
  if (file.src) return file.src
  if (file.remoteId) {
    try { return await apiDownloadUrl(file.remoteId, 'audio') } catch { return undefined }
  }
  return undefined
}

async function apiDeleteFile(token: string, fileId: string) {
  const res = await fetch(`/api/files/${fileId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
  if (!res.ok) throw new ApiError(res.status, 'Could not delete file')
}

type PublicProfile = {
  id: string
  display_name: string
  musical_role: string
  organ_model: string
  location: string
  church: string
  years_playing: string
  avatar: string
}

async function apiFetchPublicProfile(userId: string) {
  const res = await fetch(`/api/users/${userId}`)
  if (!res.ok) throw new ApiError(res.status, 'Profile not found')
  return res.json() as Promise<PublicProfile>
}

/** Finds people by display name -- separate from music search, which only ever matches a
 *  recording's own title/composer/description, never its uploader's name. */
async function apiSearchUsers(q: string) {
  const res = await fetch(`/api/users?q=${encodeURIComponent(q)}`)
  if (!res.ok) throw new ApiError(res.status, 'Could not search users')
  return res.json() as Promise<PublicProfile[]>
}

type PublicRecording = {
  id: string
  title: string
  composer: string
  format: string
  size_bytes: number
}

async function apiSearchFilesByOwner(ownerId: string) {
  const res = await fetch(`/api/files/search?owner_id=${encodeURIComponent(ownerId)}`)
  if (!res.ok) throw new ApiError(res.status, 'Could not load files')
  return res.json() as Promise<PublicRecording[]>
}

/** General Discover Library feed — every uploader's files, newest first (backend caps at 100). */
async function apiSearchAllFiles() {
  const res = await fetch('/api/files/search')
  if (!res.ok) throw new ApiError(res.status, 'Could not load files')
  return res.json() as Promise<BackendRecording[]>
}

type SongComment = {
  id: number
  fileId: number
  author: string
  avatar: string
  text: string
  date: string
}

type Member = {
  name: string
  avatar: string
  musicalRole: string
  organModel: string
  church: string
  location: string
  yearsPlaying: number
  bio: string
  featured?: boolean
  monthlyGuest?: boolean
  monthlyGuestMonth?: string
}

const MEMBERS: Record<string, Member> = {
  'Michael Bauer': {
    name: 'Michael Bauer',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&h=120&fit=crop&auto=format',
    musicalRole: 'Organist',
    organModel: 'Infinity Series 489',
    church: 'St. Peter\'s Lutheran Church',
    location: 'Stuttgart, Germany',
    yearsPlaying: 24,
    bio: 'Trained at the Hochschule für Musik Stuttgart, Michael has served as Kantor at St. Peter\'s for over two decades. He specializes in the North German Baroque repertoire and is a regular contributor to the Rodgers Connect archive.',
  },
  'Catherine Reinhardt': {
    name: 'Catherine Reinhardt',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&fit=crop&auto=format',
    musicalRole: 'Organist',
    organModel: 'Infinity Series 367',
    church: 'Grace Cathedral',
    location: 'San Francisco, CA',
    yearsPlaying: 18,
    bio: 'Cathedral organist and recording artist based in San Francisco. Catherine holds a Doctor of Musical Arts from the San Francisco Conservatory and is known for her interpretations of French Romantic organ works.',
  },
  'Marcus Kessler': {
    name: 'Marcus Kessler',
    avatar: 'https://images.unsplash.com/photo-1651684215020-f7a5b6610f23?w=120&h=120&fit=crop&auto=format',
    musicalRole: 'Choir Director',
    organModel: 'Infinity Series 489',
    church: 'Thomaskirche',
    location: 'Leipzig, Germany',
    yearsPlaying: 31,
    bio: 'Thomaskantor and professor at the Hochschule für Musik und Theater Leipzig. Marcus follows in the footsteps of Bach himself and has released fourteen recordings of Baroque organ literature.',
  },
  'Sophie Lefebvre': {
    name: 'Sophie Lefebvre',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=120&h=120&fit=crop&auto=format',
    musicalRole: 'Organist',
    organModel: 'Imagine Series 359',
    church: 'Cathédrale Notre-Dame de Paris',
    location: 'Paris, France',
    yearsPlaying: 22,
    bio: 'Associate titular organist at Notre-Dame de Paris, Sophie studied under Olivier Latry and is a laureate of the Grand Prix de Chartres. Her interpretations of Messiaen and Duruflé have earned international recognition.',
  },
  'Anna Vogel': {
    name: 'Anna Vogel',
    avatar: 'https://images.unsplash.com/photo-1562337404-3044c84ac061?w=120&h=120&fit=crop&auto=format',
    musicalRole: 'Worship Leader',
    organModel: 'Inspire Series 240',
    church: 'First Presbyterian Church',
    location: 'Nashville, TN',
    yearsPlaying: 12,
    bio: 'Church musician and hymn specialist based in Nashville. Anna brings warmth and accessibility to the organ repertoire, with a particular passion for introducing new listeners to the instrument.',
  },
  'Thomas Brandt': {
    name: 'Thomas Brandt',
    avatar: 'https://images.unsplash.com/photo-1625241152315-4a698f74ceb7?w=120&h=120&fit=crop&auto=format',
    musicalRole: 'Organist',
    organModel: 'Infinity Series 367',
    church: 'Hauptkirche St. Michaelis',
    location: 'Hamburg, Germany',
    yearsPlaying: 27,
    bio: 'Chief organist at St. Michaelis Hamburg — one of the most famous Lutheran churches in the world. Thomas is a sought-after recitalist and educator, celebrated for his Bach performances.',
  },
  'Isabelle Moreau': {
    name: 'Isabelle Moreau',
    avatar: 'https://images.unsplash.com/photo-1630939687530-241d630735df?w=120&h=120&fit=crop&auto=format',
    musicalRole: 'Organist',
    organModel: 'Imagine Series 359',
    church: 'Basilique Saint-Denis',
    location: 'Paris, France',
    yearsPlaying: 19,
    bio: 'Titular organist at Saint-Denis, Isabelle has built a reputation for meticulous scholarship and expressive depth. She is currently completing a monograph on the symphonic organ works of Charles-Marie Widor.',
  },
  'Klaus Fischer': {
    name: 'Klaus Fischer',
    avatar: 'https://images.unsplash.com/photo-1757744705465-ea08b0ddc38a?w=120&h=120&fit=crop&auto=format',
    musicalRole: 'Organist',
    organModel: 'Inspire Series 234',
    church: 'Dom zu Passau',
    location: 'Passau, Germany',
    yearsPlaying: 38,
    bio: 'Domorganist at Passau Cathedral, home to one of the world\'s largest cathedral organs. With nearly four decades at the console, Klaus is one of the most experienced organists in this community.',
  },
  'Robert Thompson': {
    name: 'Robert Thompson',
    avatar: 'https://images.unsplash.com/photo-1764112781095-24e1bf17eec9?w=120&h=120&fit=crop&auto=format',
    musicalRole: 'Worship Leader',
    organModel: 'Imagine Series 241',
    church: 'First Baptist Church',
    location: 'Atlanta, GA',
    yearsPlaying: 15,
    bio: 'Music director and organist at First Baptist Atlanta. Robert brings a gospel-influenced energy to classical repertoire and has championed the accessibility of organ music for younger audiences in the American South.',
  },
  'Elizabeth Hart': {
    name: 'Elizabeth Hart',
    avatar: 'https://images.unsplash.com/photo-1745434159123-4908d0b9df94?w=120&h=120&fit=crop&auto=format',
    musicalRole: 'Teacher',
    organModel: 'Inspire Series 352',
    church: 'Christ Church Cathedral',
    location: 'Oxford, UK',
    yearsPlaying: 21,
    bio: 'Sub-organist at Christ Church Oxford and lecturer in music at the University of Oxford. Elizabeth specialises in English cathedral repertoire and early music performance practice.',
  },
  'Hector Olivera': {
    name: 'Hector Olivera',
    avatar: hectorPhoto,
    musicalRole: 'Performance Artist',
    organModel: 'Infinity Series 489',
    church: '',
    location: 'United States',
    yearsPlaying: 45,
    bio: 'World-renowned theatre organist and recording artist, Hector Olivera is celebrated for his dazzling improvisational skill and extraordinary dynamic range. He has performed to sold-out audiences in concert halls around the globe and is a beloved ambassador for the pipe organ tradition.',
    monthlyGuest: true,
    monthlyGuestMonth: 'September 2026',
  },
  'Monty Musgrave': {
    name: 'Monty Musgrave',
    avatar: 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=120&h=120&fit=crop&auto=format',
    musicalRole: 'Composer',
    organModel: 'Imagine Series 359',
    church: 'Community Church of Music',
    location: 'Nashville, TN',
    yearsPlaying: 28,
    bio: 'Organist, composer, and educator, Monty Musgrave has built a devoted following through his accessible and joyful approach to the instrument. Known for blending classical technique with contemporary sensibility, he is a popular clinician at workshops and conventions nationwide.',
    monthlyGuest: true,
    monthlyGuestMonth: 'October 2026',
  },
  'Jan Kraybill': {
    name: 'Jan Kraybill',
    avatar: janPhoto,
    musicalRole: 'Performance Artist',
    organModel: 'Infinity Series 367',
    church: 'The Organ at Powell Hall',
    location: 'Kansas City, MO',
    yearsPlaying: 35,
    bio: "International concert organist and recording artist Jan Kraybill is known for her inspired programming and interpretive depth. A laureate of multiple international competitions, she brings warmth, precision, and genuine artistry to every performance.",
    monthlyGuest: true,
    monthlyGuestMonth: 'November 2026',
  },
  'Tedde Gibson': {
    name: 'Tedde Gibson',
    avatar: 'https://images.unsplash.com/photo-1738147473885-f28c7ac7d976?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=120&h=120&fit=crop',
    musicalRole: 'Composer',
    organModel: 'Infinity Series 489',
    church: 'Seattle Paramount Theatre',
    location: 'Pacific Northwest / East Coast',
    yearsPlaying: 30,
    bio: "Tedde Gibson is a silent film composer, organist, educator, and organ tonal consultant — and former Chairman of the Board of Directors of the American Theatre Organ Society. His classical studies included Dr. J. Melvin Butler at the University of Washington, David Dahl (Organist Emeritus, Pacific Lutheran University) in improvisation, and Dr. Carol Terry at UW. Theatre organ coaching came from internationally recognized artists Jonas Nordwall of Portland and the late Andy Crow of Olympia. Before moving east, Tedde served as organist at First Assembly of God/Life Center in Tacoma and First AME Church in Seattle. He later studied with Dr. Mickey Thomas Terry of Howard University and attended the Peabody Conservatory, where he worked in the organ studio of Dr. John Walker with additional coaching from Donald Sutherland. He is celebrated for original silent film scores — including Metropolis and The Phantom of the Opera — performed at historic venues including the Seattle Paramount Theatre, Longwood Gardens, and the Granada Theatre.",
  },
}

type FileEntry = {
  id: number
  remoteId?: string
  ownerId?: string
  title: string
  composer: string
  period: string
  category: Category
  // Single back-compat "primary" format for displays that only ever show one badge/icon
  // (FormatBadge, the homepage format quick-filter) -- derived with audio > midi > pdf >
  // rpl priority from `attachments` below. Anything that needs to know about every file a
  // performance actually carries should read `attachments`, not this.
  format: 'MP3' | 'WAV' | 'PDF' | 'MIDI' | 'RPL'
  size: string
  uploader: string
  organModel: string
  date: string
  downloads: number
  description: string
  key?: string | null
  src?: string
  midi?: string
  hasPdf?: boolean
  duration?: string
  level?: 'Beginner' | 'Intermediate' | 'Advanced'
  subcategory?: string
  registrationUsed?: string
  // External video link (YouTube/Vimeo), entered alongside -- or instead of -- any of the
  // file attachments. Purely client-side today, not backend-stored.
  videoUrl?: string
  // Free-text, uploader-entered, separate from the single required Category. Parsed from
  // the backend's comma-separated string into a trimmed, non-empty list.
  tags?: string[]
  // Authoritative record of which of the real, backend-stored attachment kinds this
  // performance carries -- a performance may have any combination of these. `format`/
  // `src`/`midi`/`hasPdf` above stay in sync with this for the many single-format display
  // spots that predate multi-attachment uploads.
  attachments?: { audio?: { format: 'MP3' | 'WAV' }; midi?: boolean; pdf?: boolean; rpl?: boolean }
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`
  // Short recordings can be a few hundred bytes -- show bytes rather than "0 KB".
  return `${bytes} B`
}

/** "Easter, Wedding,  , Christmas" -> ['Easter', 'Wedding', 'Christmas'] -- trims each tag
 *  and drops empty ones (a trailing/doubled comma shouldn't produce a blank chip). */
function parseTags(raw: string): string[] {
  return raw.split(',').map(t => t.trim()).filter(Boolean)
}

/** Converts a real backend recording (from apiSearchAllFiles/GET /api/files/search) into the
 *  shape the Discover Library already renders. organModel is only ever what the uploader
 *  (or RodgersPlay, from the file's own embedded tag) actually sent -- period still isn't
 *  tracked per recording, so that stays blank rather than guessed.
 *
 *  A performance may carry any combination of audio/midi/pdf attachments; `format` (and the
 *  legacy `src`/`midi`/`hasPdf` fields) reflect only the "primary" one -- audio, then midi,
 *  then pdf -- for the many spots that only ever show a single format badge. `attachments`
 *  is the authoritative record of everything actually attached. */
function backendRecordingToFileEntry(r: BackendRecording): FileEntry {
  const attachments: FileEntry['attachments'] = {}
  if (r.audio_filename) attachments!.audio = { format: r.audio_format === 'WAV' ? 'WAV' : 'MP3' }
  if (r.midi_filename) attachments!.midi = true
  if (r.pdf_filename) attachments!.pdf = true
  if (r.rpl_filename) attachments!.rpl = true

  const format: FileEntry['format'] = attachments!.audio
    ? attachments!.audio.format
    : attachments!.midi ? 'MIDI' : attachments!.pdf ? 'PDF' : 'RPL'
  const totalBytes = r.audio_size_bytes + r.midi_size_bytes + r.pdf_size_bytes + r.rpl_size_bytes

  return {
    id: new Date(r.uploaded_at).getTime() || Date.now(),
    remoteId: r.id,
    ownerId: r.owner_id,
    title: r.title,
    composer: r.composer,
    period: '',
    category: (r.category as Category) || 'All',
    format,
    size: formatSize(totalBytes),
    uploader: r.owner_display_name,
    organModel: r.organ_model,
    date: new Date(r.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    downloads: r.download_count,
    description: r.description,
    key: null,
    midi: attachments!.midi ? r.midi_filename : undefined,
    hasPdf: !!attachments!.pdf,
    attachments,
    tags: parseTags(r.tags),
  }
}


const CATEGORIES: Category[] = ['All', 'Preludes / Offertories', 'Postludes', 'Hymns and Reharmonizations', 'Concert Repertoire', 'Contemporary', 'Liturgy', 'Other']

const CATEGORY_ICONS: Record<Exclude<Category, 'All'>, React.ReactElement> = {
  'Preludes / Offertories': <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  Postludes: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  'Hymns and Reharmonizations': <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>,
  'Concert Repertoire': <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none"/></svg>,
  Contemporary: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
  Liturgy: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><line x1="6" y1="7" x2="18" y2="7"/></svg>,
  Other: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none"/><circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none"/></svg>,
}

const RODGERS_MODELS = [
  'Not specified',
  // Infinity Series
  'Infinity Series 367',
  'Infinity Series 489',
  // Imagine Series
  'Imagine Series 359',
  'Imagine Series 241',
  'Imagine Series 239',
  // Inspire Series
  'Inspire Series 234',
  'Inspire Series 240',
  'Inspire Series 352',
  // Other
  'Rodgers Hybrid Organs',
  'Other / Vintage Rodgers',
]

const MUSICAL_ROLES = [
  'Organist',
  'Teacher',
  'Choir Director',
  'Worship Leader',
  'Performance Artist',
  'Composer',
  'Student',
  'Musician / Instrumentalist',
]

function rodgersModelLabel(model: string): string {
  if (!model) return ''
  if (model.startsWith('Rodgers ')) return model
  return `Rodgers ${model}`
}

/** Whether an organModel value is worth showing to a viewer -- excludes both blank
 *  (never set) and the literal "Not specified" placeholder (RODGERS_MODELS[0], the
 *  default a profile's model dropdown starts on before anyone picks a real one), which
 *  a recording can end up carrying verbatim if its uploader never set a real model.
 *  Profile displays have always excluded "Not specified" already; recordings hadn't. */
function hasRealModel(model: string | undefined | null): model is string {
  return !!model && model !== 'Not specified'
}

const FORMAT_COLORS: Record<string, string> = {
  MP3: '#2a4a8c',
  WAV: '#2a7a5c',
  PDF: '#8c2a2a',
  RPL: '#7C3AED',
}

function FormatBadge({ format }: { format: string }) {
  const color = FORMAT_COLORS[format] ?? '#737373'
  return (
    <span
      className="inline-block text-[11px] font-semibold px-3 py-0.5 uppercase"
      style={{
        color,
        border: `1px solid ${color}`,
        borderRadius: 2,
        letterSpacing: '0.15em',
      }}
    >
      {format}
    </span>
  )
}

function TripleDivider() {
  return (
    <div className="flex items-center justify-center gap-2 my-6" style={{ color: '#cacaca' }}>
      <span>*</span><span>*</span><span>*</span>
    </div>
  )
}

// Shown at signup (required, checkbox-gated) and linked from the footer. Distinct from
// the per-upload rights-confirmation checkbox in UploadModal -- that one is about legal
// ownership of a specific file; this one is about how members treat each other and covers
// the whole account, agreed to once.
const COMMUNITY_GUIDELINES = [
  'Be respectful. No harassment, hate speech, or personal attacks toward other members, in comments or anywhere else on the site.',
  "Only share what's yours to share. Upload recordings, sheet music, and registrations you own the rights to, or that are genuinely in the public domain -- the same standard as the rights confirmation you'll see at upload time.",
  'Keep it on topic. This is a place for organ music, performances, and registrations -- not spam, solicitation, or unrelated promotion.',
  'Give credit where it’s due. Note the composer or arranger when you know it.',
  'No illegal content, of any kind.',
  'We may remove content or restrict accounts that don’t follow these guidelines.',
]

function CommunityGuidelinesModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="w-full max-w-md" style={{ background: '#111', border: '1px solid #222', borderRadius: 4, maxHeight: '85vh', overflowY: 'auto' }}>
        <div className="flex items-start justify-between px-8 pt-8 pb-5">
          <h2 className="text-white text-xl font-bold">Community Guidelines</h2>
          <button onClick={onClose} className="text-2xl leading-none transition-opacity hover:opacity-70" style={{ color: '#8a8a8a' }}>×</button>
        </div>
        <ul className="px-8 pb-8 flex flex-col gap-3">
          {COMMUNITY_GUIDELINES.map((g, i) => (
            <li key={i} className="text-sm" style={{ color: '#c8c8c8', lineHeight: 1.55 }}>{g}</li>
          ))}
        </ul>
      </div>
    </div>
  )
}

const ORGAN_SERIES = [
  { id: 'infinity', label: 'Infinity', desc: 'Premier flagship series' },
  { id: 'imagine', label: 'Imagine', desc: 'Professional sanctuary series' },
  { id: 'inspire', label: 'Inspire', desc: 'Versatile worship series' },
  { id: 'american', label: 'American Series', desc: 'Classic American organ sound' },
  { id: 'hybrid', label: 'Hybrid Organs', desc: 'Pipe + digital hybrid' },
  { id: 'none', label: "I don't own a Rodgers yet", desc: '' },
]

const MUSIC_GENRES = [
  'Classical/Traditional', 'Hymns/Accompaniment', 'Sacred', 'Contemporary',
  'Special Events', 'Jazz/Blues', 'Gospel', 'Theater', 'Historic Performance',
]

// --- Rodgers instrument catalog (for Add Instrument modal) ---
const INSTRUMENT_SERIES = ['Inspire', 'Imagine', 'Infinity'] as const
const SERIES_MODELS: Record<string, string[]> = {
  'Inspire':  ['234', '240', '352'],
  'Imagine':  ['239', '241', '359'],
  'Infinity': ['367', '489'],
}

// --- Add / Edit Instrument Modal ---
function AddInstrumentModal({
  onSave,
  onClose,
  existing,
}: {
  onSave: (inst: RodgersInstrument) => void
  onClose: () => void
  existing?: RodgersInstrument
}) {
  const [step, setStep] = useState<1 | 2>(existing ? 2 : 1)
  const [series, setSeries] = useState<RodgersInstrument['series']>(existing?.series ?? 'Infinity')
  const [model, setModel] = useState(existing?.model ?? '')
  const [nickname, setNickname] = useState(existing?.nickname ?? '')
  const [church, setChurch] = useState(existing?.church ?? '')
  const [softwareVersion, setSoftwareVersion] = useState(existing?.softwareVersion ?? '')
  const [primary, setPrimary] = useState(existing?.primary ?? false)

  const models = SERIES_MODELS[series] ?? []

  function handleSave() {
    if (!model) return
    onSave({
      id: existing?.id ?? `inst-${Date.now()}`,
      series, model, nickname, church, softwareVersion, primary,
    })
    onClose()
  }

  const seriesIcons: Record<string, string> = {
    'American': 'A', 'Inspire': 'In', 'Imagine': 'Im', 'Infinity': '∞',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-lg mx-4 flex flex-col" style={{ background: '#fff', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6" style={{ borderBottom: '1px solid #e8e5e1' }}>
          <div>
            <p className="text-[10px] uppercase font-bold mb-0.5" style={{ color: '#e63412', letterSpacing: '0.4em' }}>
              {existing ? 'Edit Instrument' : `Step ${step} of 2`}
            </p>
            <h2 className="font-bold uppercase" style={{ fontSize: 15, letterSpacing: '0.12em', color: '#1a1a1a' }}>
              {step === 1 ? 'Select Your Series' : 'Instrument Details'}
            </h2>
          </div>
          <button onClick={onClose} className="text-xs uppercase font-semibold transition-opacity hover:opacity-60" style={{ color: '#a0a0a0', letterSpacing: '0.15em' }}>✕</button>
        </div>

        <div className="px-8 py-7 flex flex-col gap-6">
          {step === 1 ? (
            /* Step 1: Series */
            <div className="grid grid-cols-2 gap-3">
              {INSTRUMENT_SERIES.map(s => {
                const seriesKey = s
                const logo = SERIES_LOGOS[seriesKey]
                const isSelected = series === s
                return (
                  <button
                    key={s}
                    onClick={() => { setSeries(s); setModel(''); setStep(2) }}
                    className="flex flex-col items-start text-left transition-all hover:border-[#e63412] overflow-hidden"
                    style={{
                      border: `2px solid ${isSelected ? '#e63412' : '#e8e5e1'}`,
                      background: isSelected ? '#111' : '#111',
                      minHeight: 88,
                      position: 'relative',
                    }}
                  >
                    {/* Dark background panel with logo */}
                    <div className="w-full flex items-center justify-center px-5" style={{ height: 64 }}>
                      {logo ? (
                        <img
                          src={logo}
                          alt={s}
                          className="w-full object-contain"
                          style={{ maxHeight: 36, opacity: isSelected ? 1 : 0.65 }}
                        />
                      ) : (
                        <span className="font-bold uppercase text-white text-xs" style={{ letterSpacing: '0.18em', opacity: isSelected ? 1 : 0.65 }}>{s}</span>
                      )}
                    </div>
                    {isSelected && (
                      <div className="absolute bottom-0 left-0 right-0" style={{ height: 2, background: '#e63412' }} />
                    )}
                  </button>
                )
              })}
            </div>
          ) : (
            /* Step 2: Model + fields */
            <>
              {/* Series reminder + back */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center px-3 shrink-0" style={{ background: '#111', height: 36, minWidth: 90 }}>
                    {SERIES_LOGOS[series] ? (
                      <img
                        src={SERIES_LOGOS[series]}
                        alt={series}
                        style={{ height: 22, objectFit: 'contain' }}
                      />
                    ) : (
                      <span className="text-white font-bold uppercase text-[10px]" style={{ letterSpacing: '0.15em' }}>{series}</span>
                    )}
                  </div>
                </div>
                {!existing && (
                  <button onClick={() => setStep(1)} className="text-[10px] uppercase font-semibold transition-opacity hover:opacity-60" style={{ color: '#e63412', letterSpacing: '0.18em' }}>
                    ← Change
                  </button>
                )}
              </div>

              {/* Model grid */}
              <div>
                <p className="text-[10px] uppercase font-bold mb-3" style={{ color: '#8a8a8a', letterSpacing: '0.25em' }}>Select Model</p>
                <div className="flex flex-wrap gap-2">
                  {models.map(m => (
                    <button
                      key={m}
                      onClick={() => setModel(m)}
                      className="px-4 py-2 text-xs font-bold uppercase transition-all"
                      style={{
                        letterSpacing: '0.15em',
                        background: model === m ? '#e63412' : '#f5f4f1',
                        color: model === m ? '#fff' : '#3a3a3a',
                        border: `1px solid ${model === m ? '#e63412' : '#dddbd7'}`,
                      }}
                    >
                      {`${series} ${m}`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Optional fields */}
              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-[10px] uppercase font-bold mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.25em' }}>
                    Instrument Nickname <span style={{ color: '#c0c0c0', fontWeight: 400 }}>(optional)</span>
                  </label>
                  <input
                    value={nickname}
                    onChange={e => setNickname(e.target.value)}
                    placeholder="e.g. Sanctuary Organ"
                    className="w-full px-4 py-2.5 text-sm outline-none"
                    style={{ border: '1px solid #dddbd7', background: '#fafafa', color: '#1a1a1a' }}
                  />
                </div>

                <div>
                  <label className="block text-[10px] uppercase font-bold mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.25em' }}>
                    Church or Location <span style={{ color: '#c0c0c0', fontWeight: 400 }}>(optional)</span>
                  </label>
                  <input
                    value={church}
                    onChange={e => setChurch(e.target.value)}
                    placeholder="e.g. First Lutheran Church, Nashville"
                    className="w-full px-4 py-2.5 text-sm outline-none"
                    style={{ border: '1px solid #dddbd7', background: '#fafafa', color: '#1a1a1a' }}
                  />
                </div>

                <div>
                  <label className="block text-[10px] uppercase font-bold mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.25em' }}>
                    Software Version <span style={{ color: '#c0c0c0', fontWeight: 400 }}>(private)</span>
                  </label>
                  <input
                    value={softwareVersion}
                    onChange={e => setSoftwareVersion(e.target.value)}
                    placeholder="e.g. 2.4.1"
                    className="w-full px-4 py-2.5 text-sm outline-none"
                    style={{ border: '1px solid #dddbd7', background: '#fafafa', color: '#1a1a1a' }}
                  />
                </div>

                <div className="flex flex-col gap-3">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <button
                      type="button"
                      onClick={() => setPrimary(v => !v)}
                      className="w-5 h-5 flex items-center justify-center shrink-0 transition-colors"
                      style={{ border: `2px solid ${primary ? '#e63412' : '#c0c0c0'}`, background: primary ? '#e63412' : '#fff' }}
                    >
                      {primary && <span className="text-white" style={{ fontSize: 10 }}>✓</span>}
                    </button>
                    <span className="text-xs font-semibold uppercase" style={{ letterSpacing: '0.12em', color: '#3a3a3a' }}>Make This My Primary Instrument</span>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {step === 2 && (
          <div className="px-8 pb-8 flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={!model}
              className="flex-1 py-3 font-bold text-xs uppercase transition-all hover:opacity-90 disabled:opacity-40"
              style={{ background: '#e63412', color: '#fff', letterSpacing: '0.2em' }}
            >
              {existing ? 'Save Changes' : 'Add Instrument'}
            </button>
            <button onClick={onClose} className="py-3 px-5 text-xs uppercase font-semibold transition-opacity hover:opacity-60" style={{ color: '#8a8a8a', border: '1px solid #e8e5e1', letterSpacing: '0.15em' }}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// --- Instrument Card ---
function InstrumentCard({
  instrument,
  onView,
  onEdit,
  isOwn,
}: {
  instrument: RodgersInstrument
  onView?: () => void
  onEdit?: () => void
  isOwn?: boolean
}) {
  const seriesKey = instrument.series
  const fullModelName = `${seriesKey} ${instrument.model}`
  const artImg = Object.entries(MODEL_IMAGES).find(([k]) =>
    k.toLowerCase().includes(instrument.model.toLowerCase())
  )?.[1]
  const seriesLogo = SERIES_LOGOS[seriesKey]
  const modelLogo = MODEL_LOGOS[instrument.model]

  return (
    <div className="flex flex-col overflow-hidden" style={{ background: '#fafafa', border: '1px solid #e8e5e1' }}>
      {/* Photo panel */}
      <div className="w-full overflow-hidden shrink-0 relative" style={{ height: 140, background: '#111' }}>
        {artImg && (
          <img src={artImg} alt={fullModelName} className="w-full h-full object-cover" style={{ opacity: 0.35, mixBlendMode: 'luminosity' }} />
        )}
        {/* Logos layered over photo */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-4">
          {seriesLogo && (
            <img src={seriesLogo} alt={`${seriesKey} Series`} className="object-contain" style={{ maxHeight: 22, maxWidth: '80%', opacity: 0.7 }} />
          )}
          {modelLogo && (
            <img src={modelLogo} alt={instrument.model} className="object-contain" style={{ maxHeight: 28, maxWidth: '70%' }} />
          )}
          {!seriesLogo && !modelLogo && (
            <span className="text-white font-bold uppercase text-xs opacity-40" style={{ letterSpacing: '0.18em' }}>{fullModelName}</span>
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0" style={{ height: 2, background: '#e63412' }} />
        {instrument.primary && (
          <div className="absolute top-2.5 left-2.5 px-2 py-0.5" style={{ background: '#e63412' }}>
            <span className="text-[9px] font-bold uppercase text-white" style={{ letterSpacing: '0.18em' }}>Primary</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="px-4 pt-4 pb-4 flex flex-col flex-1">
        <p className="text-[10px] uppercase font-bold mb-0.5" style={{ color: '#e63412', letterSpacing: '0.3em' }}>{instrument.series} Series</p>
        <p className="font-bold uppercase mb-1" style={{ fontSize: 13, letterSpacing: '0.12em', color: '#1a1a1a' }}>
          {seriesKey} {instrument.model}
        </p>
        {instrument.nickname && (
          <p className="text-xs mb-1" style={{ color: '#6a6a6a' }}>{instrument.nickname}</p>
        )}
        {instrument.church && (
          <p className="text-[11px] mb-2" style={{ color: '#9a9a9a' }}>{instrument.church}</p>
        )}

        <div className="flex items-center gap-1.5 flex-wrap mb-4">
        </div>

        <div className="flex items-center gap-2 mt-auto">
          {onView && (
            <button
              onClick={onView}
              className="flex-1 py-2 text-[10px] font-bold uppercase transition-all hover:opacity-80"
              style={{ border: '1px solid #1a1a1a', color: '#1a1a1a', letterSpacing: '0.18em' }}
            >
              View Profile →
            </button>
          )}
          {isOwn && onEdit && (
            <button
              onClick={onEdit}
              className="py-2 px-3 text-[10px] font-bold uppercase transition-opacity hover:opacity-60"
              style={{ border: '1px solid #dddbd7', color: '#8a8a8a', letterSpacing: '0.15em' }}
            >
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Instrument Profile Page ---
function InstrumentProfilePage({
  instrument,
  files,
  ownerName,
  isOwn,
  onBack,
  onEdit,
  onPlay,
  nowPlayingId,
  onSelectFile,
}: {
  instrument: RodgersInstrument
  files: FileEntry[]
  ownerName: string
  isOwn?: boolean
  onBack: () => void
  onEdit?: () => void
  onPlay?: (f: FileEntry) => void
  nowPlayingId?: number | null
  onSelectFile?: (f: FileEntry) => void
}) {
  const [activeTab, setActiveTab] = useState<'overview' | 'performances' | 'compatible' | 'downloads'>('overview')
  const seriesKey = instrument.series
  const fullModel = `${seriesKey} ${instrument.model}`
  const fullModelKey = Object.keys(MODEL_IMAGES).find(k => k.toLowerCase().includes(instrument.model.toLowerCase()))
  const artImg = fullModelKey ? MODEL_IMAGES[fullModelKey] : null
  const seriesLogo = SERIES_LOGOS[seriesKey]
  const modelLogo = MODEL_LOGOS[instrument.model]

  const performances = files.filter(f => f.uploader === ownerName && f.organModel?.includes(instrument.model))
  const compatible = files.filter(f => f.organModel?.includes(instrument.model) && f.uploader !== ownerName)

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'performances', label: `Performances (${performances.length})` },
    { key: 'compatible', label: `Compatible Music (${compatible.length})` },
    { key: 'downloads', label: 'Downloads' },
  ] as const

  return (
    <div className="min-h-screen" style={{ background: '#F4F3F0' }}>
      {/* Nav */}
      <nav style={{ background: '#0d0d0d', borderBottom: '1px solid #1a1a1a' }}>
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-xs uppercase font-semibold transition-opacity hover:opacity-70" style={{ color: '#8a8a8a', letterSpacing: '0.2em' }}>
            ← Back
          </button>
          <p className="text-[10px] uppercase font-semibold" style={{ color: '#5a5a5a', letterSpacing: '0.2em' }}>
            Profile / My Instruments / {fullModel}
          </p>
        </div>
      </nav>

      {/* Header */}
      <div style={{ background: '#111' }}>
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row gap-10 items-start">
            {/* Instrument photo with logo overlay */}
            <div className="shrink-0 overflow-hidden" style={{ width: 300, height: 220, background: '#0d0d0d', position: 'relative' }}>
              {artImg && (
                <img src={artImg} alt={fullModel} className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.28, mixBlendMode: 'luminosity' }} />
              )}
              {/* Logo stack centred */}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-8">
                {seriesLogo && (
                  <img src={seriesLogo} alt={`${seriesKey} Series`} className="object-contain w-full" style={{ maxHeight: 32, opacity: 0.75 }} />
                )}
                {modelLogo && (
                  <img src={modelLogo} alt={instrument.model} className="object-contain w-full" style={{ maxHeight: 40 }} />
                )}
                {!seriesLogo && !modelLogo && (
                  <span className="text-white font-bold uppercase text-sm opacity-40" style={{ letterSpacing: '0.2em' }}>{fullModel}</span>
                )}
              </div>
              <div className="absolute bottom-0 left-0 right-0" style={{ height: 3, background: 'linear-gradient(90deg, #e63412, #ff5533)' }} />
            </div>

            {/* Info */}
            <div className="flex-1">
              {seriesLogo ? (
                <img src={seriesLogo} alt={`${seriesKey} Series`} className="object-contain mb-3" style={{ maxHeight: 20, maxWidth: 160 }} />
              ) : (
                <p className="text-[10px] uppercase font-bold mb-2" style={{ color: '#e63412', letterSpacing: '0.4em' }}>{instrument.series} Series</p>
              )}
              {modelLogo ? (
                <img src={modelLogo} alt={instrument.model} className="object-contain mb-3" style={{ maxHeight: 44, maxWidth: 200 }} />
              ) : (
                <h1 className="text-white font-bold uppercase mb-2" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', letterSpacing: '-0.01em' }}>
                  {fullModel}
                </h1>
              )}
              {instrument.nickname && (
                <p className="text-base mb-4" style={{ color: '#7a7a7a' }}>"{instrument.nickname}"</p>
              )}

              <div className="flex items-center gap-3 flex-wrap mb-6">
                {instrument.primary && (
                  <span className="text-[9px] font-bold uppercase px-2 py-1" style={{ background: '#e63412', color: '#fff', letterSpacing: '0.18em' }}>Primary Instrument</span>
                )}
                <span className="text-[9px] font-bold uppercase px-2 py-1" style={{ border: '1px solid #2a2a2a', color: '#6a6a6a', letterSpacing: '0.18em' }}>Digital</span>
              </div>

              {instrument.church && (
                <p className="text-sm mb-1" style={{ color: '#7a7a7a' }}>{instrument.church}</p>
              )}

              <div className="flex gap-3 flex-wrap mt-6">
                <button
                  onClick={() => setActiveTab('compatible')}
                  className="px-5 py-2.5 font-bold text-xs uppercase transition-all hover:opacity-90"
                  style={{ background: '#e63412', color: '#fff', letterSpacing: '0.18em' }}
                >
                  Browse Music for This Model
                </button>
                {isOwn && onEdit && (
                  <button
                    onClick={onEdit}
                    className="px-5 py-2.5 font-bold text-xs uppercase transition-opacity hover:opacity-70"
                    style={{ border: '1px solid #2a2a2a', color: '#8a8a8a', letterSpacing: '0.18em' }}
                  >
                    Edit Instrument
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e8e5e1' }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-end gap-0">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="px-5 py-4 text-xs font-bold uppercase transition-colors"
                style={{
                  letterSpacing: '0.15em',
                  color: activeTab === tab.key ? '#e63412' : '#8a8a8a',
                  borderBottom: `2px solid ${activeTab === tab.key ? '#e63412' : 'transparent'}`,
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-10">
            {/* Music for this instrument */}
            <div>
              <p className="text-xs uppercase font-bold mb-5" style={{ color: '#1a1a1a', letterSpacing: '0.25em' }}>Music for Your Instrument</p>
              <div className="flex flex-col gap-2">
                {[
                  { icon: '♩', label: 'Sheet Music', sub: 'PDF scores & parts' },
                  { icon: '♫', label: 'MP3 Recordings', sub: 'Audio performances' },
                  { icon: '⊕', label: 'MIDI Files', sub: 'Playback & practice' },
                ].map(item => (
                  <button
                    key={item.label}
                    onClick={() => setActiveTab('compatible')}
                    className="flex items-center gap-4 px-5 py-4 text-left transition-all hover:border-[#e63412] group"
                    style={{ background: '#fff', border: '1px solid #e8e5e1' }}
                  >
                    <span className="w-9 h-9 flex items-center justify-center shrink-0 font-bold text-white text-sm" style={{ background: '#1a1a1a' }}>{item.icon}</span>
                    <div>
                      <p className="text-xs font-bold uppercase group-hover:text-[#e63412] transition-colors" style={{ letterSpacing: '0.12em', color: '#1a1a1a' }}>{item.label}</p>
                      <p className="text-[10px]" style={{ color: '#9a9a9a' }}>{item.sub}</p>
                    </div>
                    <span className="ml-auto text-[10px] font-bold uppercase" style={{ color: '#e63412', letterSpacing: '0.18em' }}>→</span>
                  </button>
                ))}
              </div>
              <p className="text-xs mt-3" style={{ color: '#9a9a9a' }}>
                Compatible with <strong style={{ color: '#1a1a1a' }}>{fullModel}</strong>
              </p>
            </div>

            {/* Instrument resources */}
            <div>
              <p className="text-xs uppercase font-bold mb-5" style={{ color: '#1a1a1a', letterSpacing: '0.25em' }}>Instrument Resources</p>
              <div className="flex flex-col gap-2">
                {[
                  { label: "Owner's Manual", icon: '📄' },
                  { label: 'Software Updates', icon: '⬆' },
                  { label: 'Video Tutorials', icon: '▶' },
                  { label: 'Model-Specific Support', icon: '◎' },
                  { label: 'Contact a Dealer', icon: '⊕' },
                ].map(r => (
                  <button
                    key={r.label}
                    className="flex items-center gap-3 px-5 py-3 text-left transition-all hover:border-[#e63412] group"
                    style={{ background: '#fff', border: '1px solid #e8e5e1' }}
                  >
                    <span className="text-sm w-6 text-center shrink-0">{r.icon}</span>
                    <span className="text-xs font-semibold uppercase group-hover:text-[#e63412] transition-colors" style={{ letterSpacing: '0.12em', color: '#3a3a3a' }}>{r.label}</span>
                    <span className="ml-auto text-[10px]" style={{ color: '#c0c0c0' }}>→</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {(activeTab === 'performances' || activeTab === 'compatible') && (() => {
          const list = activeTab === 'performances' ? performances : compatible
          return (
            <div>
              <p className="text-xs uppercase font-bold mb-5" style={{ color: '#1a1a1a', letterSpacing: '0.25em' }}>
                {activeTab === 'performances' ? `Performed on ${fullModel}` : `Compatible with ${fullModel}`}
              </p>
              {list.length === 0 ? (
                <div className="py-16 text-center" style={{ border: '1px dashed #dddbd7' }}>
                  <p className="text-sm" style={{ color: '#9a9a9a' }}>No {activeTab === 'performances' ? 'performances' : 'compatible music'} yet.</p>
                </div>
              ) : (
                <div className="flex flex-col" style={{ border: '1px solid #e8e5e1' }}>
                  {list.map((file, i) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-4 px-5 py-4 group"
                      style={{ borderBottom: i < list.length - 1 ? '1px solid #f0ede9' : 'none', background: '#fff' }}
                    >
                      <button
                        onClick={() => onPlay?.(file)}
                        className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-all"
                        style={{ background: nowPlayingId === file.id ? '#e63412' : '#f5f4f1', border: '1px solid #dddbd7' }}
                      >
                        {nowPlayingId === file.id
                          ? <span className="text-white" style={{ fontSize: 8, letterSpacing: 1 }}>■■</span>
                          : <span style={{ fontSize: 10, marginLeft: 2, color: '#1a1a1a' }}>▶</span>
                        }
                      </button>
                      <button className="flex-1 min-w-0 text-left" onClick={() => onSelectFile?.(file)}>
                        <p className="text-sm font-bold uppercase truncate" style={{ letterSpacing: '0.1em', color: '#1a1a1a' }}>{file.title}</p>
                        <p className="text-xs truncate" style={{ color: '#8a8a8a' }}>{file.composer} · {file.uploader}</p>
                      </button>
                      <div className="hidden sm:flex items-center gap-2 shrink-0">
                        <span className="text-[10px] uppercase font-semibold px-2 py-0.5" style={{ background: 'rgba(230,52,18,0.06)', color: '#e63412', border: '1px solid rgba(230,52,18,0.15)', letterSpacing: '0.1em' }}>
                          {activeTab === 'compatible' ? `Compatible` : `Recorded`}
                        </span>
                        <span className="text-xs" style={{ color: '#9a9a9a' }}>{file.duration}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })()}

        {activeTab === 'downloads' && (
          <div className="py-16 text-center" style={{ border: '1px dashed #dddbd7' }}>
            <p className="text-sm mb-1 font-bold uppercase" style={{ color: '#1a1a1a', letterSpacing: '0.15em' }}>Downloads</p>
            <p className="text-sm" style={{ color: '#9a9a9a' }}>Registrations and additional downloads coming soon.</p>
          </div>
        )}
      </div>
    </div>
  )
}

// --- Profile Setup Screen (4-step onboarding) ---
function ProfileSetup({ onComplete, onSkip, mode = 'signup' }: { onComplete: (p: Profile) => void; onSkip?: () => void; mode?: 'signup' | 'login' }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [guidelinesAccepted, setGuidelinesAccepted] = useState(false)
  const [showGuidelines, setShowGuidelines] = useState(false)
  const [authError, setAuthError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [apiUser, setApiUser] = useState<BackendUser | null>(null)
  const [musicalRole, setMusicalRole] = useState(MUSICAL_ROLES[0])
  const [organModel, setOrganModel] = useState(RODGERS_MODELS[0])
  const [location, setLocation] = useState('')
  const [church, setChurch] = useState('')
  const [yearsPlaying, setYearsPlaying] = useState('')
  const [avatar, setAvatar] = useState<string>('')
  const [avatarError, setAvatarError] = useState(false)
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0)
  const [series, setSeries] = useState('')
  const [interests, setInterests] = useState<string[]>([])
  const [followed, setFollowed] = useState<Set<string>>(new Set())
  const fileRef = useRef<HTMLInputElement>(null)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false)
  const [forgotPasswordSubmitting, setForgotPasswordSubmitting] = useState(false)

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setForgotPasswordSubmitting(true)
    setAuthError('')
    try {
      await apiForgotPassword(email.trim())
      setForgotPasswordSent(true)
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setForgotPasswordSubmitting(false)
    }
  }

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { setAvatarError(true); return }
    setAvatarError(false)
    const reader = new FileReader()
    reader.onload = (ev) => setAvatar(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (mode === 'signup' && !name.trim()) return
    if (!email.trim() || !password) return
    setSubmitting(true)
    setAuthError('')
    try {
      // Login is email + password only — no name field to collect. In the rare case this
      // turns out to be a brand new account (a login attempt on an email that was never
      // registered), fall back to the email's local part as a starter display name rather
      // than force a name field into what's supposed to be a minimal sign-in form.
      const displayName = mode === 'login' ? (email.split('@')[0] || 'New Member') : name.trim()
      const { user, isNewAccount } = await signupOrLogin(email.trim(), password, displayName, avatar)
      if (!mailingList.includes(email)) mailingList.push(email)
      if (isNewAccount) {
        if (SHOW_EXPERIMENTAL) {
          setApiUser(user)
          setStep(1)
        } else {
          // The organ-series/interests/follow-organists steps below write into local
          // Profile fields (interests/series/followed) that the backend doesn't store —
          // they'd just be silently wiped on the next real profile save. Skip straight to
          // done rather than collect data that's discarded.
          onComplete(backendUserToProfile(user))
        }
      } else {
        // Returning user — they've already been through onboarding, so skip straight in
        // instead of re-asking for organ/interests/follow-suggestions on every login.
        onComplete(backendUserToProfile(user))
      }
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleFinish = async () => {
    if (!apiUser) return
    const chosenSeries = ORGAN_SERIES.find(s => s.id === series)
    const seriesModelPrefix = series === 'infinity' ? 'Infinity Series' : series === 'imagine' ? 'Imagine Series' : series === 'inspire' ? 'Inspire Series' : ''
    const matchedModel = seriesModelPrefix ? RODGERS_MODELS.find(m => m.startsWith(seriesModelPrefix)) ?? RODGERS_MODELS[0] : RODGERS_MODELS[0]
    const currentProfile = backendUserToProfile(apiUser)

    // Actually persist the organ choice this time -- it used to only ever update local
    // state (onComplete below), so it looked saved for the rest of the session but the
    // backend never stored it (organ_model stayed ""). interests/series/followed still
    // have no backend column at all, so those genuinely can't be saved yet.
    let savedUser = apiUser
    if (matchedModel && !currentProfile.organModel) {
      const token = localStorage.getItem(TOKEN_KEY)
      if (token) {
        setSubmitting(true)
        try {
          savedUser = await apiUpdateProfile(token, {
            display_name: currentProfile.name,
            musical_role: currentProfile.musicalRole,
            organ_model: matchedModel,
            location: currentProfile.location,
            church: currentProfile.church,
            years_playing: currentProfile.yearsPlaying,
            avatar: avatar || currentProfile.avatar,
          })
        } catch {
          // Not worth blocking onboarding over -- they can still set it from Edit
          // Profile afterward (that path is already known to work).
        } finally {
          setSubmitting(false)
        }
      }
    }

    const savedProfile = backendUserToProfile(savedUser)
    onComplete({
      ...savedProfile,
      avatar: avatar || savedProfile.avatar,
      organModel: savedProfile.organModel || matchedModel,
      interests,
      series: chosenSeries?.label ?? '',
    })
  }

  // Non-experimental builds never advance past step 0 (see handleAccountSubmit above) — a
  // single-step progress bar would be pointless, so only the full 4-step version applies
  // when the rest of onboarding is actually reachable.
  const STEP_LABELS = SHOW_EXPERIMENTAL ? ['Account', 'Your Organ', 'Music', 'Community'] : ['Account']

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0d0d0d' }}>
      {/* Nav */}
      <nav style={{ background: '#111', borderBottom: '1px solid #222' }}>
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center gap-3">
          <a href="https://www.rodgersinstruments.com/" target="_blank" rel="noopener noreferrer" className="flex items-center shrink-0">
            <img src={connectLogo} alt="Rodgers Connect" className="h-7 w-auto" />
          </a>
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg">
          {/* Step progress bar — a login is a single action, not a multi-step wizard, so
              only show this for signup (and once a login-mode submission turns out to be a
              fresh account, step will already have advanced past 0 by the time this would
              otherwise flash into view). */}
          {(mode === 'signup' || step > 0) && (
            <div className="flex items-center justify-center gap-2 mb-10">
              {STEP_LABELS.map((label, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all"
                      style={{
                        background: step >= i ? '#e63412' : '#2a2a2a',
                        color: step >= i ? '#fff' : '#555',
                      }}
                    >
                      {step > i ? '✓' : i + 1}
                    </div>
                    <span className="text-[9px] uppercase font-semibold" style={{ color: step >= i ? '#e63412' : '#444', letterSpacing: '0.15em' }}>
                      {label}
                    </span>
                  </div>
                  {i < 3 && <div className="w-12 h-px mb-4" style={{ background: step > i ? '#e63412' : '#2a2a2a' }} />}
                </div>
              ))}
            </div>
          )}

          {/* Step 0: Create account */}
          {step === 0 && showForgotPassword ? (
            <div style={{ background: '#111', border: '1px solid #222', borderRadius: 4 }}>
              <div className="px-8 pt-8 pb-4">
                <p className="text-xs font-bold uppercase mb-2" style={{ color: '#e63412', letterSpacing: '0.3em' }}>Reset your password</p>
                <h2 className="text-white text-2xl font-bold">{forgotPasswordSent ? 'Check your email.' : 'Forgot your password?'}</h2>
                <p className="text-sm mt-2" style={{ color: '#8a8a8a' }}>
                  {forgotPasswordSent
                    ? "If an account exists for that email, we've sent a link to reset your password. It expires in 1 hour."
                    : "Enter your email and we'll send you a link to reset your password."}
                </p>
              </div>
              {!forgotPasswordSent && (
                <form onSubmit={handleForgotPasswordSubmit} className="px-8 pb-8 pt-4 space-y-5">
                  <div>
                    <label className="block text-xs font-semibold uppercase mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.2em' }}>Email Address</label>
                    <input required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="e.g. margaret@stpauls.org"
                      className="w-full px-4 py-3 text-sm outline-none" style={{ background: '#1a1a1a', border: '1px solid #333', color: '#fff', borderRadius: 3 }} />
                  </div>
                  {authError && (
                    <p className="text-xs" style={{ color: '#e63412' }}>{authError}</p>
                  )}
                  <button type="submit" disabled={forgotPasswordSubmitting} className="btn w-full mt-2" style={forgotPasswordSubmitting ? { opacity: 0.6 } : undefined}>
                    {forgotPasswordSubmitting ? 'Sending…' : 'Send Reset Link'}
                  </button>
                </form>
              )}
              <div className="px-8 pb-8" style={{ paddingTop: forgotPasswordSent ? 16 : 0 }}>
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(false); setForgotPasswordSent(false); setAuthError('') }}
                  className="w-full text-center text-xs font-semibold uppercase transition-opacity hover:opacity-70"
                  style={{ color: '#8a8a8a', letterSpacing: '0.2em' }}
                >
                  ← Back to Sign In
                </button>
              </div>
            </div>
          ) : step === 0 && (
            <div style={{ background: '#111', border: '1px solid #222', borderRadius: 4 }}>
              <div className="px-8 pt-8 pb-4">
                <p className="text-xs font-bold uppercase mb-2" style={{ color: '#e63412', letterSpacing: '0.3em' }}>
                  {mode === 'login' ? 'Sign in to RodgersConnect' : 'Create your RodgersConnect account'}
                </p>
                <h2 className="text-white text-2xl font-bold">{mode === 'login' ? 'Welcome back.' : 'Welcome to the community.'}</h2>
                <p className="text-sm mt-2" style={{ color: '#8a8a8a' }}>
                  {mode === 'login'
                    ? "Enter your email and password to sign back in — or a new email to create an account."
                    : 'Share recordings, connect with organists, and play new music on your Rodgers.'}
                </p>
              </div>
              <form
                onSubmit={handleAccountSubmit}
                className="px-8 pb-8 pt-4 space-y-5"
              >
                {mode === 'signup' && (
                  <>
                    {/* Avatar */}
                    <div className="flex items-center gap-5">
                      <div
                        className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center cursor-pointer shrink-0 transition-all"
                        style={{ border: `2px solid ${avatar ? '#e63412' : '#333'}`, background: '#1a1a1a' }}
                        onClick={() => fileRef.current?.click()}
                      >
                        {avatar
                          ? <img src={avatar} alt="Profile" className="w-full h-full object-cover" />
                          : <span className="text-xl" style={{ color: '#999' }}>+</span>
                        }
                      </div>
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                      <div>
                        <button type="button" onClick={() => fileRef.current?.click()} className="text-xs font-bold uppercase transition-opacity hover:opacity-70" style={{ color: '#e63412', letterSpacing: '0.15em' }}>
                          {avatar ? 'Change Photo' : 'Add Profile Photo'}
                        </button>
                        <p className="text-[10px] mt-1" style={{ color: '#8a8a8a' }}>Optional — add it any time</p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold uppercase mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.2em' }}>Full Name</label>
                      <input required value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Margaret Davies"
                        className="w-full px-4 py-3 text-sm outline-none" style={{ background: '#1a1a1a', border: '1px solid #333', color: '#fff', borderRadius: 3 }} />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-xs font-semibold uppercase mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.2em' }}>Email Address</label>
                  <input required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="e.g. margaret@stpauls.org"
                    className="w-full px-4 py-3 text-sm outline-none" style={{ background: '#1a1a1a', border: '1px solid #333', color: '#fff', borderRadius: 3 }} />
                  {mode === 'signup' && (
                    <p className="text-[10px] mt-1.5" style={{ color: '#8a8a8a' }}>You will be added to the Rodgers Connect mailing list.</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.2em' }}>Password</label>
                  <input required type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters"
                    className="w-full px-4 py-3 text-sm outline-none" style={{ background: '#1a1a1a', border: '1px solid #333', color: '#fff', borderRadius: 3 }} />
                  {mode === 'signup' && (
                    <p className="text-[10px] mt-1.5" style={{ color: '#8a8a8a' }}>Already have an account? Enter your email and password to sign back in.</p>
                  )}
                  {mode === 'login' && (
                    <button
                      type="button"
                      onClick={() => { setShowForgotPassword(true); setAuthError('') }}
                      className="mt-1.5 text-[10px] font-semibold uppercase transition-opacity hover:opacity-70"
                      style={{ color: '#e63412', letterSpacing: '0.14em' }}
                    >
                      Forgot password?
                    </button>
                  )}
                </div>

                {mode === 'signup' && (
                  <label className="flex items-start gap-2" style={{ fontSize: 12, color: '#8a8a8a', lineHeight: 1.5, cursor: 'pointer' }}>
                    <input
                      required
                      type="checkbox"
                      checked={guidelinesAccepted}
                      onChange={e => setGuidelinesAccepted(e.target.checked)}
                      style={{ marginTop: 2, flexShrink: 0 }}
                    />
                    <span>
                      I agree to the{' '}
                      <button type="button" onClick={() => setShowGuidelines(true)} className="font-semibold" style={{ color: '#e63412', textDecoration: 'underline' }}>
                        Community Guidelines
                      </button>
                      .
                    </span>
                  </label>
                )}

                {authError && (
                  <p className="text-xs" style={{ color: '#e63412' }}>{authError}</p>
                )}

                <button type="submit" disabled={submitting} className="btn w-full mt-2" style={submitting ? { opacity: 0.6 } : undefined}>
                  {submitting ? 'Please wait…' : 'Continue'}
                </button>
                {onSkip && (
                  <button type="button" onClick={onSkip} className="w-full text-center text-xs font-semibold uppercase transition-opacity hover:opacity-70" style={{ color: '#8a8a8a', letterSpacing: '0.2em' }}>
                    Skip for now
                  </button>
                )}
              </form>
            </div>
          )}

          {showGuidelines && <CommunityGuidelinesModal onClose={() => setShowGuidelines(false)} />}

          {/* Step 1: What do you play? — writes into Profile.series, which the backend
              doesn't store; only reachable on experimental builds (see handleAccountSubmit). */}
          {step === 1 && (
            <Experimental>
            <div style={{ background: '#111', border: '1px solid #222', borderRadius: 4 }}>
              <div className="px-8 pt-8 pb-5">
                <p className="text-xs font-bold uppercase mb-2" style={{ color: '#e63412', letterSpacing: '0.3em' }}>Step 1 of 3</p>
                <h2 className="text-white text-2xl font-bold">What do you play?</h2>
                <p className="text-sm mt-1" style={{ color: '#8a8a8a' }}>{"We'll show you music recorded on your instrument."}</p>
              </div>
              <div className="px-8 pb-8 grid grid-cols-2 gap-3">
                {ORGAN_SERIES.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => { setSeries(s.id); setTimeout(() => setStep(2), 180) }}
                    className="text-left px-4 py-4 transition-all hover:opacity-90"
                    style={{
                      background: series === s.id ? 'rgba(230,52,18,0.1)' : '#1a1a1a',
                      border: `1px solid ${series === s.id ? '#e63412' : '#2a2a2a'}`,
                      borderRadius: 4,
                    }}
                  >
                    <p className="font-bold text-sm text-white">{s.label}</p>
                    {s.desc && <p className="text-[11px] mt-0.5" style={{ color: '#8a8a8a' }}>{s.desc}</p>}
                  </button>
                ))}
              </div>
            </div>
            </Experimental>
          )}

          {/* Step 2: What music interests you? — writes into Profile.interests, which the
              backend doesn't store; only reachable on experimental builds. */}
          {step === 2 && (
            <Experimental>
            <div style={{ background: '#111', border: '1px solid #222', borderRadius: 4 }}>
              <div className="px-8 pt-8 pb-5">
                <p className="text-xs font-bold uppercase mb-2" style={{ color: '#e63412', letterSpacing: '0.3em' }}>Step 2 of 3</p>
                <h2 className="text-white text-2xl font-bold">What genres do you play?</h2>
                <p className="text-sm mt-1" style={{ color: '#8a8a8a' }}>Select all that apply. You can change these any time.</p>
              </div>
              <div className="px-8 pb-4 flex flex-wrap gap-2">
                {MUSIC_GENRES.map(g => {
                  const active = interests.includes(g)
                  return (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setInterests(prev => active ? prev.filter(x => x !== g) : [...prev, g])}
                      className="px-4 py-2 text-sm font-semibold transition-all"
                      style={{
                        background: active ? '#e63412' : '#1a1a1a',
                        color: active ? '#fff' : '#737373',
                        border: `1px solid ${active ? '#e63412' : '#2a2a2a'}`,
                        borderRadius: 24,
                      }}
                    >
                      {g}
                    </button>
                  )
                })}
              </div>
              <div className="px-8 py-6 flex gap-3">
                <button type="button" onClick={() => setStep(1)} className="btn btn-outline" style={{ borderColor: '#333', color: '#8a8a8a' }}>Back</button>
                <button type="button" onClick={() => setStep(3)} className="btn flex-1">
                  {interests.length > 0 ? `Continue with ${interests.length} selected` : 'Skip this step'}
                </button>
              </div>
            </div>
            </Experimental>
          )}

          {/* Step 3: Follow organists — writes into local `followed` state that's never sent
              to the backend; only reachable on experimental builds. */}
          {step === 3 && (
            <Experimental>
            <div style={{ background: '#111', border: '1px solid #222', borderRadius: 4 }}>
              <div className="px-8 pt-8 pb-5">
                <p className="text-xs font-bold uppercase mb-2" style={{ color: '#e63412', letterSpacing: '0.3em' }}>Step 3 of 3</p>
                <h2 className="text-white text-2xl font-bold">Follow a few organists.</h2>
                <p className="text-sm mt-1" style={{ color: '#8a8a8a' }}>{"Your feed starts filling up the moment you follow someone."}</p>
              </div>
              <div className="px-8 pb-4 space-y-3">
                {Object.values(MEMBERS).slice(0, 6).map(m => (
                  <div key={m.name} className="flex items-center gap-4 py-3 border-b" style={{ borderColor: '#1e1e1e' }}>
                    <img src={m.avatar} alt={m.name} className="w-10 h-10 rounded-full object-cover shrink-0" style={{ border: '2px solid #2a2a2a' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{m.name}</p>
                      <p className="text-[11px] truncate" style={{ color: '#8a8a8a' }}>{m.organModel} · {m.location}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFollowed(prev => {
                        const n = new Set(prev)
                        n.has(m.name) ? n.delete(m.name) : n.add(m.name)
                        return n
                      })}
                      className="px-4 py-1.5 text-xs font-bold uppercase transition-all shrink-0"
                      style={{
                        background: followed.has(m.name) ? '#2a2a2a' : '#e63412',
                        color: followed.has(m.name) ? '#737373' : '#fff',
                        border: `1px solid ${followed.has(m.name) ? '#333' : '#e63412'}`,
                        borderRadius: 3,
                        letterSpacing: '0.1em',
                      }}
                    >
                      {followed.has(m.name) ? 'Following' : 'Follow'}
                    </button>
                  </div>
                ))}
              </div>
              <div className="px-8 py-6 flex gap-3">
                <button type="button" onClick={() => setStep(2)} className="btn btn-outline" style={{ borderColor: '#333', color: '#8a8a8a' }}>Back</button>
                <button type="button" onClick={handleFinish} disabled={submitting} className="btn flex-1" style={{ opacity: submitting ? 0.6 : 1 }}>
                  {submitting ? 'Saving…' : followed.size > 0 ? `Start — following ${followed.size}` : "Start — I'll follow later"}
                </button>
              </div>
            </div>
            </Experimental>
          )}

        </div>
      </div>
    </div>
  )
}

// --- Upload Modal ---
// Audio, MIDI, and sheet music used to be gated behind picking a single contribution
// "type" up front. They're now all just independent, optional attachment slots shown side
// by side -- an upload carries any combination of them (plus an optional video link, a
// plain text field rather than a file, and a Rodgers Play .rpl playlist), not just one.
//
// `accept` lists every real-world MIME variant a browser/OS might report for these
// extensions, not just the "canonical" one -- WAV in particular gets reported as
// audio/wav, audio/wave, audio/x-wav, or audio/vnd.wave depending on OS and browser, and
// a narrower list can make a native file-picker flag a perfectly valid file as the wrong
// type (the picker still lets it through, since accept is advisory, but it's a scary
// false alarm). `hint` is shown directly under the label so "what formats does this
// accept" never has to be guessed.
//
// `experimental` slots (audio, sheet music) are only offered when SHOW_EXPERIMENTAL is on
// (dev) -- on staging/prod the upload form is MIDI (plus playlist) only. The backend still
// accepts all four; this is a UI-level gate, not a server-side restriction.
type UploadFileKind = { key: 'audio' | 'midi' | 'pdf' | 'rpl'; label: string; hint: string; accept: string; icon: string; experimental?: boolean }
const UPLOAD_FILE_KINDS = ([
  { key: 'audio', label: 'Audio', hint: 'MP3 or WAV', accept: '.mp3,.wav,audio/mpeg,audio/mp3,audio/wav,audio/wave,audio/x-wav,audio/vnd.wave', icon: '🎵', experimental: true },
  { key: 'midi', label: 'MIDI', hint: '.mid or .midi', accept: '.mid,.midi,audio/midi,audio/x-midi', icon: '🎹' },
  { key: 'pdf', label: 'Sheet Music', hint: 'PDF', accept: '.pdf,application/pdf', icon: '📄', experimental: true },
  { key: 'rpl', label: 'Playlist', hint: 'Rodgers Play .rpl', accept: '.rpl', icon: '📻' },
] as UploadFileKind[]).filter(k => SHOW_EXPERIMENTAL || !k.experimental)

function UploadModal({ onClose, onSubmit, uploaderModel }: { onClose: () => void; onSubmit: (entry: FileEntry) => void; uploaderModel: string }) {
  const [title, setTitle] = useState('')
  const [composer, setComposer] = useState('')
  const [description, setDescription] = useState('')
  const [registrationUsed, setRegistrationUsed] = useState('')
  const [videoUrl, setVideoUrl] = useState('')
  const [category, setCategory] = useState<Category>('Preludes / Offertories')
  const [tags, setTags] = useState('')
  // '' means "use my current Rodgers" (uploaderModel) -- overridable per-upload since a
  // performance may not have been played on the uploader's own instrument (e.g. a guest
  // organ that week).
  const [organModelOverride, setOrganModelOverride] = useState('')
  const [rightsConfirmed, setRightsConfirmed] = useState(false)
  // Every attachment slot (audio / MIDI / sheet music / playlist) is independently
  // optional and keyed by kind, so any combination can be filled in one upload.
  const [perfFiles, setPerfFiles] = useState<Partial<Record<'audio' | 'midi' | 'pdf' | 'rpl', File>>>({})
  const [submitted, setSubmitted] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const perfInputRefs = useRef<Partial<Record<'audio' | 'midi' | 'pdf' | 'rpl', HTMLInputElement | null>>>({})

  // rpl playlists aren't music/sheet content being redistributed -- they're a reference
  // list of track names -- so they don't require the rights checkbox below.
  const hasMusicAttachment = !!(perfFiles.audio || perfFiles.midi || perfFiles.pdf)
  const hasBackendAttachment = !!(perfFiles.audio || perfFiles.midi || perfFiles.pdf || perfFiles.rpl)

  const applyAutoTitle = (file: File) => {
    if (!title) setTitle(file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '))
  }
  const handlePerfFilePick = (kind: 'audio' | 'midi' | 'pdf' | 'rpl', file: File) => {
    setPerfFiles(prev => ({ ...prev, [kind]: file }))
    applyAutoTitle(file)
  }
  const removePerfFile = (kind: 'audio' | 'midi' | 'pdf' | 'rpl') => {
    setPerfFiles(prev => { const next = { ...prev }; delete next[kind]; return next })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedVideoUrl = videoUrl.trim()

    if (!hasBackendAttachment && !trimmedVideoUrl) {
      setUploadError(SHOW_EXPERIMENTAL
        ? 'Attach at least one file (audio, MIDI, sheet music, or a playlist), or add a video link.'
        : 'Attach at least one file (a MIDI file or a playlist).')
      return
    }
    if (hasMusicAttachment && !rightsConfirmed) {
      setUploadError('You must confirm the rights statement below before uploading.')
      return
    }

    const effectiveModel = organModelOverride || uploaderModel

    if (hasBackendAttachment) {
      const token = localStorage.getItem(TOKEN_KEY)
      if (!token) { setUploadError('You need to be signed in to upload.'); return }
      setUploading(true)
      setUploadError('')
      try {
        const finalDescription = description || 'Performance uploaded by you.'
        const record = await apiUploadFile(token, perfFiles, {
          title, composer, category, description: finalDescription, organModel: effectiveModel, tags,
        })
        const attachments: FileEntry['attachments'] = {}
        if (record.audio_filename) attachments!.audio = { format: record.audio_format === 'WAV' ? 'WAV' : 'MP3' }
        if (record.midi_filename) attachments!.midi = true
        if (record.pdf_filename) attachments!.pdf = true
        if (record.rpl_filename) attachments!.rpl = true
        const format: FileEntry['format'] = attachments!.audio ? attachments!.audio.format : attachments!.midi ? 'MIDI' : attachments!.pdf ? 'PDF' : 'RPL'
        // Not caching a presigned download URL here — it expires in 5 minutes, so it'd go
        // stale by the time someone actually clicks Download later. remoteId is enough for
        // those buttons to fetch a fresh one at the moment it's actually needed. The local
        // blob URL is still fine for immediate audio playback within this same session.
        const audioBlobUrl = perfFiles.audio ? URL.createObjectURL(perfFiles.audio) : undefined
        onSubmit({
          id: Date.now(),
          remoteId: record.id,
          ownerId: record.owner_id,
          title,
          composer,
          period: 'Contemporary',
          category,
          format,
          size: formatSize(record.audio_size_bytes + record.midi_size_bytes + record.pdf_size_bytes + record.rpl_size_bytes),
          uploader: 'You',
          organModel: effectiveModel,
          date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          downloads: 0,
          description: finalDescription,
          key: record.audio_filename || record.midi_filename || record.pdf_filename || record.rpl_filename,
          src: audioBlobUrl,
          midi: attachments!.midi ? record.midi_filename : undefined,
          hasPdf: !!attachments!.pdf,
          registrationUsed: registrationUsed || undefined,
          videoUrl: trimmedVideoUrl || undefined,
          attachments,
          tags: parseTags(tags),
        })
        setSubmitted(true)
        setTimeout(onClose, 2000)
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Upload failed. Please try again.')
      } finally {
        setUploading(false)
      }
      return
    }

    // No file attached at all -- just a video link, which the backend doesn't store yet,
    // so this stays local-only.
    onSubmit({
      id: Date.now(),
      title,
      composer,
      period: 'Contemporary',
      category,
      format: 'MP3',
      size: '—',
      uploader: 'You',
      organModel: effectiveModel,
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      downloads: 0,
      description: description || 'Contribution uploaded by you.',
      key: null,
      registrationUsed: registrationUsed || undefined,
      videoUrl: trimmedVideoUrl || undefined,
    })
    setSubmitted(true)
    setTimeout(onClose, 2000)
  }

  const labelStyle = { color: '#737373', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.18em', marginBottom: 6, display: 'block' }
  const inputStyle = { background: '#F8F7F5', border: '1px solid #D8D4CC', color: '#0B0B0B', borderRadius: 3, width: '100%', padding: '10px 14px', fontSize: 14, outline: 'none' }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4" style={{ background: 'rgba(0,0,0,0.72)' }}>
      <div className="w-full sm:max-w-xl bg-white sm:rounded overflow-hidden" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.25)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ background: '#0B0B0B', borderBottom: '3px solid #E63412' }}>
          <p className="font-bold uppercase text-white" style={{ fontSize: 13, letterSpacing: '0.18em' }}>Share a Performance</p>
          <button onClick={onClose} className="text-white opacity-50 hover:opacity-100 transition-opacity" style={{ fontSize: 24, lineHeight: 1 }}>×</button>
        </div>

        <div className="overflow-y-auto flex-1">
          {submitted ? (
            <div className="px-8 py-14 text-center">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(230,52,18,0.1)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E63412" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
              </div>
              <p className="font-bold uppercase mb-2" style={{ fontSize: 14, color: '#0B0B0B', letterSpacing: '0.14em' }}>Submitted</p>
              <p style={{ fontSize: 14, color: '#737373' }}>Your contribution will appear in the community shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label style={labelStyle}>Attachments <span style={{ color: '#a4a4a4', fontWeight: 400, textTransform: 'none' }}>(attach any combination)</span></label>
                <div className={`grid grid-cols-2 gap-3 ${UPLOAD_FILE_KINDS.length > 2 ? 'sm:grid-cols-4' : ''}`}>
                  {UPLOAD_FILE_KINDS.map(k => {
                    const picked = perfFiles[k.key]
                    return (
                      <div
                        key={k.key}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handlePerfFilePick(k.key, f) }}
                        onClick={() => perfInputRefs.current[k.key]?.click()}
                        className="border-2 border-dashed py-5 px-2 text-center cursor-pointer transition-colors"
                        style={{ borderColor: picked ? '#E63412' : '#D8D4CC', background: picked ? 'rgba(230,52,18,0.03)' : '#fafafa', borderRadius: 4 }}
                      >
                        <span style={{ fontSize: 20 }}>{k.icon}</span>
                        <p className="font-bold uppercase mt-1" style={{ fontSize: 10, color: '#0B0B0B', letterSpacing: '0.12em' }}>{k.label}</p>
                        <p style={{ fontSize: 9, color: '#B0AAA0', marginTop: 1 }}>{k.hint}</p>
                        {picked ? (
                          <>
                            <p className="font-semibold mt-1 truncate" style={{ fontSize: 11, color: '#E63412' }} title={picked.name}>✓ {picked.name}</p>
                            <button
                              type="button"
                              onClick={e => { e.stopPropagation(); removePerfFile(k.key) }}
                              className="mt-1 transition-opacity hover:opacity-70"
                              style={{ fontSize: 10, color: '#B0AAA0', textDecoration: 'underline' }}
                            >
                              Remove
                            </button>
                          </>
                        ) : (
                          <p style={{ fontSize: 10, color: '#B0AAA0', marginTop: 4 }}>+ Add</p>
                        )}
                        <input
                          ref={el => { perfInputRefs.current[k.key] = el }}
                          type="file"
                          accept={k.accept}
                          className="hidden"
                          onChange={e => { const f = e.target.files?.[0]; if (f) handlePerfFilePick(k.key, f) }}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>

              <Experimental>
                <div>
                  <label style={labelStyle}>Video URL <span style={{ color: '#a4a4a4', fontWeight: 400, textTransform: 'none' }}>(optional, YouTube or Vimeo)</span></label>
                  <input value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=…" style={inputStyle} />
                </div>
              </Experimental>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label style={labelStyle}>Title</label>
                  <input required value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Composer <span style={{ color: '#a4a4a4', fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                  <input value={composer} onChange={e => setComposer(e.target.value)} style={inputStyle} />
                </div>
              </div>

              {/* Registration used — relevant whenever a MIDI attachment is involved */}
              {perfFiles.midi && (
                <div>
                  <label style={labelStyle}>Registration Used</label>
                  <input value={registrationUsed} onChange={e => setRegistrationUsed(e.target.value)} placeholder="e.g. Principal 8', Octave 4', Mixture" style={inputStyle} />
                </div>
              )}

              <div>
                <label style={labelStyle}>Category</label>
                <select value={category} onChange={e => setCategory(e.target.value as Category)} style={inputStyle}>
                  {CATEGORIES.filter(c => c !== 'All').map(c => <option key={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Tags <span style={{ color: '#a4a4a4', fontWeight: 400, textTransform: 'none' }}>(optional, comma-separated)</span></label>
                <input value={tags} onChange={e => setTags(e.target.value)} placeholder="e.g. Easter, Wedding, Baroque" style={inputStyle} />
              </div>

              <div>
                <label style={labelStyle}>Description <span style={{ color: '#B0AAA0', fontWeight: 400 }}>(optional)</span></label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder={perfFiles.midi ? 'Notes on this MIDI file — intended tempo, registration suggestions…' : 'A short description of this contribution…'} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>

              {perfFiles.midi && (
                <div className="px-4 py-3 flex items-start gap-3" style={{ background: 'rgba(230,52,18,0.05)', border: '1px solid rgba(230,52,18,0.18)', borderRadius: 4 }}>
                  <img src={rodgersPlayLogo} alt="Rodgers Play" style={{ height: 20, objectFit: 'contain', marginTop: 2, opacity: 0.8 }} />
                  <p style={{ fontSize: 12, color: '#555', lineHeight: 1.55 }}>This MIDI file will be tagged for Rodgers Play. Community members can send it directly to their instrument.</p>
                </div>
              )}

              {hasMusicAttachment && (
                <label className="flex items-start gap-2" style={{ fontSize: 12, color: '#555', lineHeight: 1.5, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={rightsConfirmed}
                    onChange={e => setRightsConfirmed(e.target.checked)}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <span>I confirm that I own the rights to this music, or that any included sheet music is in the public domain in my location.</span>
                </label>
              )}

              <div>
                <label style={labelStyle}>Recorded On</label>
                <select value={organModelOverride} onChange={e => setOrganModelOverride(e.target.value)} style={inputStyle}>
                  <option value="">Default — {uploaderModel || 'Your Rodgers'}</option>
                  {RODGERS_MODELS.filter(m => m !== 'Not specified' && m !== 'Other / Vintage Rodgers').map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              {uploadError && <p style={{ fontSize: 12, color: '#e63412' }}>{uploadError}</p>}

              <div className="flex justify-end items-center pt-2">
                <button
                  type="submit"
                  disabled={uploading || (hasMusicAttachment && !rightsConfirmed)}
                  className="btn"
                  style={{ fontSize: 11, padding: '11px 24px', letterSpacing: '0.16em', opacity: uploading || (hasMusicAttachment && !rightsConfirmed) ? 0.6 : 1 }}
                >
                  {uploading ? 'Uploading…' : 'Share'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

// --- File Card ---
function FileCard({ file, onClick, onDelete, onViewMember, onPlay, isPlaying, onLike, liked, likeCount, comments, onAddComment, profile }: {
  file: FileEntry
  onClick: () => void
  onDelete?: () => void
  onViewMember?: (file: FileEntry) => void
  onPlay?: (file: FileEntry) => void
  isPlaying?: boolean
  onLike?: () => void
  liked?: boolean
  likeCount?: number
  comments?: SongComment[]
  onAddComment?: (text: string) => void
  profile?: Profile | null
}) {
  const member = MEMBERS[file.uploader]
  const avatarUrl = member?.avatar ?? null
  const [showComments, setShowComments] = useState(false)
  const [commentText, setCommentText] = useState('')
  const commentCount = comments?.length ?? 0

  return (
    <div className="bg-white border transition-shadow hover:shadow-md" style={{ borderColor: isPlaying ? '#e63412' : '#e0e0e0', boxShadow: isPlaying ? '0 0 0 2px #e63412' : undefined }}>
      <div style={{ height: 3, background: '#e63412' }} />
      <div className="p-6 cursor-pointer" onClick={onClick}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            <FormatBadge format={file.format} />
            {/* format above is just the primary attachment (audio > midi > pdf) -- flag
                whichever others this performance also carries. */}
            {file.attachments?.midi && file.attachments?.audio && (
              <span className="text-[9px] font-bold uppercase px-1.5 py-0.5" style={{ color: '#7C3AED', background: 'rgba(124,58,237,0.07)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: 2, letterSpacing: '0.1em' }}>+ MIDI</span>
            )}
            {file.attachments?.pdf && (file.attachments?.audio || file.attachments?.midi) && (
              <span className="text-[9px] font-bold uppercase px-1.5 py-0.5" style={{ color: '#2563EB', background: 'rgba(37,99,235,0.07)', border: '1px solid rgba(37,99,235,0.25)', borderRadius: 2, letterSpacing: '0.1em' }}>+ Sheet Music</span>
            )}
            {file.attachments?.rpl && (file.attachments?.audio || file.attachments?.midi || file.attachments?.pdf) && (
              <span className="text-[9px] font-bold uppercase px-1.5 py-0.5" style={{ color: '#7C3AED', background: 'rgba(124,58,237,0.07)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: 2, letterSpacing: '0.1em' }}>+ Playlist</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isPlayableAudio(file) && (
              <button
                onClick={e => { e.stopPropagation(); onPlay?.(file) }}
                className="w-7 h-7 rounded-full flex items-center justify-center transition-all"
                style={{ background: isPlaying ? '#e63412' : '#1a1a1a', color: '#fff', flexShrink: 0 }}
                title={isPlaying ? 'Now playing' : 'Play'}
              >
                {isPlaying
                  ? <span style={{ fontSize: 9, letterSpacing: 1 }}>■■</span>
                  : <span style={{ fontSize: 10, marginLeft: 1 }}>▶</span>
                }
              </button>
            )}
            <span className="text-xs" style={{ color: '#a4a4a4' }}>{file.date}</span>
          </div>
        </div>
        <h3 className="text-sm font-bold uppercase leading-snug mb-1" style={{ letterSpacing: '0.15em', color: '#1a1a1a' }}>{file.title}</h3>
        <p className="text-xs mb-1" style={{ color: '#e63412' }}>
          {file.composer}
          {file.key && <span style={{ color: '#a4a4a4' }} className="ml-2">{file.key}</span>}
        </p>
        <p className="text-xs leading-relaxed mt-3 mb-4 line-clamp-2" style={{ color: '#8a8a8a' }}>{file.description}</p>
        {file.tags && file.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4" style={{ marginTop: -8 }}>
            {file.tags.map(t => (
              <span key={t} className="font-semibold" style={{ fontSize: 9, color: '#737373', background: '#F5F3EF', border: '1px solid #E8E4DE', borderRadius: 2, padding: '1px 5px' }}>{t}</span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
          <span className="text-xs shrink-0" style={{ color: '#a4a4a4' }}>{file.size}</span>
          {hasRealModel(file.organModel) && (
            <span className="text-xs font-semibold uppercase truncate" style={{ color: '#8a8a8a', letterSpacing: '0.06em' }} title={file.organModel}>
              {file.organModel.replace(' Series', '')}
            </span>
          )}
          <span className="text-xs font-semibold shrink-0" style={{ color: '#8a8a8a' }}>↓ {file.downloads.toLocaleString()}</span>
        </div>
      </div>
      {/* Uploader row */}
      <div
        className="px-6 py-3 flex items-center justify-between"
        style={{ borderTop: '1px solid #f5f5f5', background: '#fafafa' }}
      >
        <button
          className="flex items-center gap-2 group"
          onClick={e => { e.stopPropagation(); onViewMember?.(file) }}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt={file.uploader} className="w-6 h-6 rounded-full object-cover" style={{ border: '1px solid #e0e0e0' }} />
          ) : (
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ background: '#e63412' }}>
              {file.uploader.charAt(0)}
            </div>
          )}
          <span className="text-xs font-semibold group-hover:underline" style={{ color: '#292929' }}>{file.uploader}</span>
        </button>
        <div className="flex items-center gap-3">
          {/* Like & Comment — pure local state, no backend behind them */}
          <Experimental>
            <button
              onClick={e => { e.stopPropagation(); onLike?.() }}
              className="flex items-center gap-1 transition-all hover:opacity-80"
              title={liked ? 'Unlike' : 'Like'}
            >
              <span style={{ fontSize: 14, color: liked ? '#e63412' : '#cacaca', lineHeight: 1 }}>{liked ? '♥' : '♡'}</span>
              {(likeCount ?? 0) > 0 && <span className="text-[10px] font-semibold tabular-nums" style={{ color: liked ? '#e63412' : '#a4a4a4' }}>{likeCount}</span>}
            </button>
            <button
              onClick={e => { e.stopPropagation(); setShowComments(v => !v) }}
              className="flex items-center gap-1 transition-all hover:opacity-80"
              title="Comments"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={showComments ? '#e63412' : '#cacaca'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              {commentCount > 0 && <span className="text-[10px] font-semibold tabular-nums" style={{ color: showComments ? '#e63412' : '#a4a4a4' }}>{commentCount}</span>}
            </button>
          </Experimental>
          {onDelete && (
            <button
              onClick={e => { e.stopPropagation(); onDelete() }}
              className="text-[10px] font-semibold uppercase transition-colors hover:opacity-70"
              style={{ color: '#e63412', letterSpacing: '0.12em' }}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Inline comment section */}
      {showComments && (
        <div style={{ borderTop: '1px solid #f0f0f0' }} onClick={e => e.stopPropagation()}>
          {/* Existing comments */}
          {commentCount > 0 && (
            <div className="flex flex-col">
              {comments!.map(c => (
                <div key={c.id} className="flex gap-3 px-5 py-3" style={{ borderBottom: '1px solid #f9f9f9' }}>
                  {c.avatar
                    ? <img src={c.avatar} alt={c.author} className="w-7 h-7 rounded-full object-cover shrink-0 mt-0.5" style={{ border: '1.5px solid #e0e0e0' }} />
                    : <div className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[9px] font-bold text-white mt-0.5" style={{ background: '#e63412' }}>{c.author.charAt(0)}</div>
                  }
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 mb-0.5">
                      <span className="text-[10px] font-bold uppercase" style={{ color: '#1a1a1a', letterSpacing: '0.1em' }}>{c.author}</span>
                      <span className="text-[10px]" style={{ color: '#d0d0d0' }}>{c.date}</span>
                    </div>
                    <p className="text-xs leading-relaxed" style={{ color: '#5a5a5a' }}>{c.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add comment */}
          <div className="px-5 py-3" style={{ background: '#fafafa' }}>
            {profile ? (
              <div className="flex gap-2.5 items-start">
                {profile.avatar
                  ? <img src={profile.avatar} alt={profile.name} className="w-7 h-7 rounded-full object-cover shrink-0 mt-1" style={{ border: '1.5px solid #e63412' }} />
                  : <div className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[9px] font-bold text-white mt-1" style={{ background: '#e63412' }}>{profile.name.charAt(0)}</div>
                }
                <div className="flex-1 flex gap-2">
                  <input
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && commentText.trim()) {
                        onAddComment?.(commentText)
                        setCommentText('')
                      }
                    }}
                    placeholder="Add a comment…"
                    className="flex-1 px-3 py-2 text-xs outline-none"
                    style={{ background: '#fff', border: '1px solid #e0e0e0', color: '#292929', borderRadius: 3 }}
                  />
                  <button
                    onClick={() => { if (commentText.trim()) { onAddComment?.(commentText); setCommentText('') } }}
                    disabled={!commentText.trim()}
                    className="px-3 py-1.5 text-[10px] font-bold uppercase shrink-0 transition-all"
                    style={{ background: commentText.trim() ? '#e63412' : '#e8e8e8', color: commentText.trim() ? '#fff' : '#bbb', letterSpacing: '0.15em', borderRadius: 3, border: 'none' }}
                  >
                    Post
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-[11px] text-center py-1" style={{ color: '#a4a4a4' }}>
                <button className="font-semibold underline" style={{ color: '#e63412' }} onClick={() => {}}>Sign in</button> to comment.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// --- Detail Panel ---
function DetailPanel({ file, onClose, onDelete, onPlay, isPlaying, onLike, liked, likeCount, comments, onAddComment, profile }: {
  file: FileEntry
  onClose: () => void
  onDelete?: () => void
  onPlay?: (file: FileEntry) => void
  isPlaying?: boolean
  onLike?: () => void
  liked?: boolean
  likeCount?: number
  comments?: SongComment[]
  onAddComment?: (text: string) => void
  profile?: Profile | null
}) {
  const [downloadState, setDownloadState] = useState<Record<'audio' | 'midi' | 'pdf' | 'rpl', 'idle' | 'loading' | 'done' | 'error'>>({ audio: 'idle', midi: 'idle', pdf: 'idle', rpl: 'idle' })
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [commentText, setCommentText] = useState('')
  const commentInputRef = useRef<HTMLTextAreaElement>(null)

  // For a real (backend-uploaded) file, fetch a fresh presigned URL for the requested
  // attachment at click time rather than reusing one from upload time — those expire in 5
  // minutes. Falls back to the local blob URL for still-mock entries (video-link-only
  // submissions, or the seeded demo content), which only ever have one file regardless of
  // `kind`.
  async function handleDownload(kind: 'audio' | 'midi' | 'pdf' | 'rpl', filename: string) {
    setDownloadState(s => ({ ...s, [kind]: 'loading' }))
    try {
      const url = file.remoteId ? await apiDownloadUrl(file.remoteId, kind) : file.src
      if (!url) throw new Error('no url')
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.target = '_blank'
      a.click()
      setDownloadState(s => ({ ...s, [kind]: 'done' }))
    } catch {
      setDownloadState(s => ({ ...s, [kind]: 'error' }))
    }
  }

  const downloadLabel = (kind: 'audio' | 'midi' | 'pdf' | 'rpl', idle: string) =>
    downloadState[kind] === 'loading' ? 'Getting link…' : downloadState[kind] === 'done' ? '✓ Downloaded' : idle

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="w-full max-w-lg bg-white flex flex-col" style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.22)', maxHeight: '92vh' }}>
        <div style={{ background: '#1a1a1a' }} className="px-8 py-5 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase mb-1" style={{ color: '#8a8a8a', letterSpacing: '0.2em' }}>{file.category} · {file.period}</p>
            <h2 className="text-white text-sm font-bold uppercase leading-snug" style={{ letterSpacing: '0.2em' }}>{file.title}</h2>
            <p className="text-xs mt-1" style={{ color: '#e63412' }}>
              {file.composer}
              {file.key && <span className="ml-2" style={{ color: '#8a8a8a' }}>{file.key}</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-white opacity-50 hover:opacity-100 transition-opacity text-2xl leading-none mt-0.5 shrink-0">×</button>
        </div>
        <div className="px-8 py-7">
          <p className="font-serif text-base leading-relaxed italic mb-5" style={{ color: '#434343', fontSize: 17 }}>{file.description}</p>
          <TripleDivider />
          <div className="grid grid-cols-3 gap-y-4 mb-6">
            {[
              ['Format', file.attachments
                ? [file.attachments.audio?.format, file.attachments.midi && 'MIDI', file.attachments.pdf && 'Sheet Music'].filter(Boolean).join(', ')
                : file.format],
              ['File Size', file.size], ['Period', file.period], ['Category', file.category], ['Downloads', file.downloads.toLocaleString()], ['Contributor', file.uploader],
              ['Rodgers Model', hasRealModel(file.organModel) ? file.organModel : ''],
            ].filter(([, value]) => value).map(([label, value]) => (
              <div key={label as string}>
                <p className="text-xs uppercase font-semibold mb-0.5" style={{ color: '#a4a4a4', letterSpacing: '0.2em' }}>{label}</p>
                <p className="text-sm font-semibold" style={{ color: '#292929' }}>{value}</p>
              </div>
            ))}
          </div>
          {file.tags && file.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-6" style={{ marginTop: -16 }}>
              {file.tags.map(t => (
                <span key={t} className="font-semibold" style={{ fontSize: 10, color: '#737373', background: '#F5F3EF', border: '1px solid #E8E4DE', borderRadius: 2, padding: '2px 7px' }}>{t}</span>
              ))}
            </div>
          )}
          {downloadState.audio === 'error' || downloadState.midi === 'error' || downloadState.pdf === 'error' ? (
            <p className="text-xs mb-3" style={{ color: '#e63412' }}>Could not get a download link. Please try again.</p>
          ) : null}
          {/* Listen keeps a solid primary fill; every Download button below is outlined
              instead, so the one playback control never reads as just another download
              option in the row. */}
          <div className="flex gap-3 flex-wrap">
            {isPlayableAudio(file) && (
              <button
                onClick={() => onPlay?.(file)}
                className="btn flex-1"
                style={isPlaying ? { background: '#292929' } : {}}
              >
                {isPlaying ? '■ Stop' : '▶ Listen'}
              </button>
            )}
          </div>
          <div className="flex gap-3 flex-wrap mt-3">
            {file.attachments ? (
              <>
                {file.attachments.audio && (
                  <button
                    onClick={() => handleDownload('audio', `${file.title}.${file.attachments!.audio!.format.toLowerCase()}`)}
                    disabled={downloadState.audio === 'loading'}
                    className="btn btn-outlined flex-1"
                    style={{ opacity: downloadState.audio === 'loading' ? 0.6 : 1 }}
                  >
                    {downloadLabel('audio', `↓ Download ${file.attachments.audio.format}`)}
                  </button>
                )}
                {file.attachments.midi && (
                  <button
                    onClick={() => handleDownload('midi', `${file.title}.mid`)}
                    disabled={downloadState.midi === 'loading'}
                    className="btn btn-outlined flex-1"
                    style={{ opacity: downloadState.midi === 'loading' ? 0.6 : 1 }}
                  >
                    {downloadLabel('midi', '↓ Download MIDI')}
                  </button>
                )}
                {file.attachments.pdf && (
                  <button
                    onClick={() => handleDownload('pdf', `${file.title}.pdf`)}
                    disabled={downloadState.pdf === 'loading'}
                    className="btn btn-outlined flex-1"
                    style={{ opacity: downloadState.pdf === 'loading' ? 0.6 : 1 }}
                  >
                    {downloadLabel('pdf', '↓ Download Sheet Music')}
                  </button>
                )}
                {file.attachments.rpl && (
                  <button
                    onClick={() => handleDownload('rpl', `${file.title}.rpl`)}
                    disabled={downloadState.rpl === 'loading'}
                    className="btn btn-outlined flex-1"
                    style={{ opacity: downloadState.rpl === 'loading' ? 0.6 : 1 }}
                  >
                    {downloadLabel('rpl', '↓ Download Playlist')}
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={() => handleDownload('audio', `${file.title}.${file.format.toLowerCase()}`)}
                disabled={downloadState.audio === 'loading'}
                className="btn btn-outlined flex-1"
                style={{ opacity: downloadState.audio === 'loading' ? 0.6 : 1 }}
              >
                {downloadLabel('audio', `↓ Download ${file.format}`)}
              </button>
            )}
            {file.videoUrl && (
              <a href={file.videoUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outlined flex-1" style={{ textAlign: 'center' }}>
                ↗ Watch Video
              </a>
            )}
            {onDelete && !confirmDelete && (
              <button onClick={() => setConfirmDelete(true)} className="btn btn-outline" style={{ borderColor: '#e63412', color: '#e63412' }}>Delete</button>
            )}
            {onDelete && confirmDelete && (
              <div className="flex gap-2 flex-1">
                <button onClick={onDelete} className="btn flex-1" style={{ background: '#e63412' }}>Confirm Delete</button>
                <button onClick={() => setConfirmDelete(false)} className="btn btn-outline">Cancel</button>
              </div>
            )}

            {/* Like row — pure local state, no backend behind it */}
            <Experimental>
              <div className="flex items-center gap-3 pt-1" style={{ borderTop: '1px solid #f0f0f0', marginTop: 4 }}>
                <button
                  onClick={onLike}
                  className="flex items-center gap-2 transition-all hover:opacity-80"
                >
                  <span style={{ fontSize: 20, color: liked ? '#e63412' : '#d0d0d0', lineHeight: 1 }}>{liked ? '♥' : '♡'}</span>
                  <span className="text-xs font-semibold" style={{ color: liked ? '#e63412' : '#a4a4a4' }}>
                    {(likeCount ?? 0) > 0 ? likeCount : ''}{liked && (likeCount ?? 0) === 0 ? '1' : ''}
                    {' '}
                    {liked ? 'Liked' : 'Like'}
                  </span>
                </button>
                <div className="flex-1" />
                <button
                  onClick={() => commentInputRef.current?.focus()}
                  className="flex items-center gap-1.5 text-xs font-semibold transition-opacity hover:opacity-70"
                  style={{ color: '#a4a4a4' }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  {(comments?.length ?? 0) > 0 ? `${comments!.length} comment${comments!.length !== 1 ? 's' : ''}` : 'Comment'}
                </button>
              </div>
            </Experimental>
          </div>
        </div>

        {/* Comments section — pure local state, no backend behind it */}
        <Experimental>
        <div className="overflow-y-auto flex-1" style={{ borderTop: '1px solid #f0f0f0' }}>
          {/* Existing comments */}
          {(comments?.length ?? 0) > 0 && (
            <div className="flex flex-col divide-y divide-[#f5f5f5]">
              {comments!.map(c => (
                <div key={c.id} className="flex gap-3 px-6 py-4">
                  {c.avatar
                    ? <img src={c.avatar} alt={c.author} className="w-8 h-8 rounded-full object-cover shrink-0 mt-0.5" style={{ border: '1.5px solid #e0e0e0' }} />
                    : <div className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-xs font-bold text-white mt-0.5" style={{ background: '#e63412' }}>{c.author.charAt(0)}</div>
                  }
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-xs font-bold uppercase" style={{ color: '#1a1a1a', letterSpacing: '0.1em' }}>{c.author}</span>
                      <span className="text-[10px]" style={{ color: '#cacaca' }}>{c.date}</span>
                    </div>
                    <p className="text-sm leading-relaxed" style={{ color: '#434343' }}>{c.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add comment */}
          <div className="px-6 py-4" style={{ background: '#fafafa', borderTop: (comments?.length ?? 0) > 0 ? '1px solid #f0f0f0' : undefined }}>
            {profile ? (
              <div className="flex gap-3">
                {profile.avatar
                  ? <img src={profile.avatar} alt={profile.name} className="w-8 h-8 rounded-full object-cover shrink-0 mt-1" style={{ border: '1.5px solid #e63412' }} />
                  : <div className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-xs font-bold text-white mt-1" style={{ background: '#e63412' }}>{profile.name.charAt(0)}</div>
                }
                <div className="flex-1">
                  <textarea
                    ref={commentInputRef}
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        if (commentText.trim()) { onAddComment?.(commentText); setCommentText('') }
                      }
                    }}
                    placeholder="Add a comment…"
                    rows={2}
                    className="w-full px-3 py-2 text-sm outline-none resize-none"
                    style={{ background: '#fff', border: '1px solid #e0e0e0', color: '#292929', borderRadius: 3 }}
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={() => { if (commentText.trim()) { onAddComment?.(commentText); setCommentText('') } }}
                      disabled={!commentText.trim()}
                      className="px-4 py-1.5 text-xs font-bold uppercase transition-all"
                      style={{ background: commentText.trim() ? '#e63412' : '#e0e0e0', color: '#fff', letterSpacing: '0.15em', borderRadius: 3, border: 'none' }}
                    >
                      Post
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-center" style={{ color: '#a4a4a4' }}>
                <button onClick={onClose} className="font-semibold underline" style={{ color: '#e63412' }}>Sign in</button> to leave a comment.
              </p>
            )}
          </div>
        </div>
        </Experimental>
      </div>
    </div>
  )
}

// --- Global Audio Player Bar ---
function AudioPlayerBar({ file, onClose, files, isPlaying, onPlayingChange }: {
  file: FileEntry; onClose: () => void; files: FileEntry[]
  // Controlled play/pause: the bar reports real playback state up rather than owning it,
  // so every Listen/Stop button elsewhere (which reads this same state) stays in sync
  // with what's actually audible, including when paused from the bar's own button.
  isPlaying: boolean
  onPlayingChange: (playing: boolean) => void
}) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [volume, setVolume] = useState(1)
  const [showVolume, setShowVolume] = useState(false)
  const [currentFile, setCurrentFile] = useState(file)
  const [resolvedSrc, setResolvedSrc] = useState<string | undefined>(undefined)

  // The bar itself is never remounted when a different file starts playing (no `key` at
  // its call sites) -- resync from the prop so switching tracks while it's already open
  // actually switches, instead of getting stuck on whatever first opened it.
  useEffect(() => {
    setCurrentFile(file)
  }, [file.id])

  const playableFiles = files.filter(isPlayableAudio)
  const currentIdx = playableFiles.findIndex(f => f.id === currentFile.id)
  const contributor = MEMBERS[currentFile.uploader]

  // Real backend files have no static src -- resolve a fresh presigned URL per track
  // rather than a static one (those expire in 5 minutes, so this deliberately re-fetches
  // every time currentFile changes rather than caching it on the file entry).
  useEffect(() => {
    let cancelled = false
    setResolvedSrc(undefined)
    resolvePlaybackSrc(currentFile).then(url => {
      if (!cancelled) setResolvedSrc(url)
    })
    return () => { cancelled = true }
  }, [currentFile.id])

  // Single effect owns every real audio.play()/pause() call, reacting to either a new
  // track loading (resolvedSrc changes, e.g. from skip/track-switch) or the controlled
  // isPlaying prop flipping (e.g. some other Listen/Stop button toggled it). Two separate
  // effects here would risk redundant/conflicting play() calls on the same transition.
  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !resolvedSrc) return
    if (isPlaying) {
      audio.play().catch(() => onPlayingChange(false))
    } else {
      audio.pause()
    }
  }, [isPlaying, resolvedSrc])

  const togglePlay = () => onPlayingChange(!isPlaying)

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    setCurrentTime(audio.currentTime)
    setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0)
  }, [])

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) setDuration(audioRef.current.duration)
  }, [])

  const handleScrub = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio || !audio.duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    audio.currentTime = ((e.clientX - rect.left) / rect.width) * audio.duration
  }

  const setVolumeFromClientX = (track: HTMLDivElement, clientX: number) => {
    const rect = track.getBoundingClientRect()
    const v = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    setVolume(v)
    if (audioRef.current) audioRef.current.volume = v
  }

  const handleVolumePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const track = e.currentTarget
    setVolumeFromClientX(track, e.clientX)
    track.setPointerCapture(e.pointerId)
    const onMove = (ev: PointerEvent) => setVolumeFromClientX(track, ev.clientX)
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const skipPrev = () => {
    if (currentIdx > 0) { setCurrentFile(playableFiles[currentIdx - 1]); setProgress(0); setCurrentTime(0) }
  }
  const skipNext = () => {
    if (currentIdx < playableFiles.length - 1) { setCurrentFile(playableFiles[currentIdx + 1]); setProgress(0); setCurrentTime(0) }
  }

  const fmt = (s: number) => {
    if (!isFinite(s)) return '0:00'
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 flex items-center gap-5 px-6"
      style={{ background: '#0B0B0B', borderTop: '3px solid #E63412', height: 72 }}
    >
      <audio
        ref={audioRef}
        src={resolvedSrc}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => onPlayingChange(false)}
      />

      {/* Contributor avatar */}
      <div className="shrink-0 w-10 h-10 overflow-hidden" style={{ background: '#1a1a1a' }}>
        {contributor?.avatar
          ? <img src={contributor.avatar} alt={currentFile.uploader} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center font-bold text-white" style={{ background: '#E63412', fontSize: 13 }}>{currentFile.uploader.charAt(0)}</div>
        }
      </div>

      {/* Track info */}
      <div className="min-w-0 shrink-0" style={{ width: 200 }}>
        <p className="font-bold text-white truncate" style={{ fontSize: 13, letterSpacing: '-0.01em' }}>{currentFile.title}</p>
        <p className="truncate" style={{ fontSize: 11, color: '#737373' }}>{currentFile.uploader}</p>
      </div>

      {/* Transport: prev / play-pause / next */}
      <div className="flex items-center gap-3 shrink-0">
        <button
          onClick={skipPrev}
          disabled={currentIdx <= 0}
          className="transition-opacity hover:opacity-70 disabled:opacity-25"
          style={{ color: '#fff' }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><path d="M2 2h1.5v10H2zM4 7L13 2v10z"/></svg>
        </button>
        <button
          onClick={togglePlay}
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-opacity hover:opacity-80"
          style={{ background: '#E63412', color: '#fff' }}
        >
          {isPlaying
            ? <span style={{ fontSize: 9, letterSpacing: 1.5 }}>■■</span>
            : <span style={{ fontSize: 13, marginLeft: 3 }}>▶</span>
          }
        </button>
        <button
          onClick={skipNext}
          disabled={currentIdx >= playableFiles.length - 1}
          className="transition-opacity hover:opacity-70 disabled:opacity-25"
          style={{ color: '#fff' }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><path d="M10.5 2H12v10h-1.5zM1 2l9 5-9 5z"/></svg>
        </button>
      </div>

      {/* Progress */}
      <div className="flex-1 flex items-center gap-3 min-w-0">
        <span className="text-xs shrink-0 tabular-nums" style={{ color: '#737373', minWidth: 32 }}>{fmt(currentTime)}</span>
        <div
          className="flex-1 h-1 cursor-pointer relative"
          style={{ background: '#2a2a2a' }}
          onClick={handleScrub}
        >
          <div className="absolute left-0 top-0 h-full" style={{ width: `${progress}%`, background: '#E63412' }} />
          <div className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-white" style={{ left: `calc(${progress}% - 5px)` }} />
        </div>
        <span className="text-xs shrink-0 tabular-nums" style={{ color: '#737373', minWidth: 32 }}>{fmt(duration)}</span>
      </div>

      {/* Volume */}
      <div className="hidden md:flex items-center gap-2 shrink-0 relative">
        <button
          onClick={() => setShowVolume(v => !v)}
          className="transition-opacity hover:opacity-70"
          style={{ color: volume === 0 ? '#737373' : '#fff' }}
        >
          <svg width="16" height="14" viewBox="0 0 16 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M1 5h3l4-3v10l-4-3H1z"/>
            {volume > 0 && <path d="M11 3.5a5 5 0 0 1 0 7"/>}
            {volume > 0.5 && <path d="M13 1.5a8 8 0 0 1 0 11"/>}
          </svg>
        </button>
        {showVolume && (
          <div
            className="absolute bottom-8 left-0 px-3 py-3"
            style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', width: 120 }}
          >
            <div
              className="relative cursor-pointer"
              style={{ height: 24, touchAction: 'none' }}
              onPointerDown={handleVolumePointerDown}
            >
              <div className="absolute left-0 top-1/2 w-full h-1" style={{ transform: 'translateY(-50%)', background: '#2a2a2a' }}>
                <div className="absolute left-0 top-0 h-full" style={{ width: `${volume * 100}%`, background: '#E63412' }} />
              </div>
              <div
                className="absolute top-1/2 w-3 h-3 rounded-full bg-white"
                style={{ left: `calc(${volume * 100}% - 6px)`, transform: 'translateY(-50%)' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Download */}
      <a
        href={resolvedSrc}
        download={`${currentFile.title}.${currentFile.format.toLowerCase()}`}
        className="hidden md:flex items-center gap-1.5 shrink-0 transition-opacity hover:opacity-70"
        style={{ color: '#737373' }}
        title="Download"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M7 2v7M4 6l3 3 3-3M2 11h10"/>
        </svg>
        <span className="font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.18em' }}>Download</span>
      </a>

      {/* Close */}
      <button
        onClick={onClose}
        className="shrink-0 transition-opacity hover:opacity-60 ml-1"
        style={{ color: '#737373', fontSize: 20, lineHeight: 1 }}
      >
        ×
      </button>
    </div>
  )
}

// --- Profile Menu (dropdown) ---
function ProfileMenu({ profile, onViewProfile, onViewPerformances, onLogout, onClose }: { profile: Profile; onViewProfile: () => void; onViewPerformances: () => void; onLogout: () => void; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end pt-14 pr-6" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-white w-64 shadow-xl" style={{ border: '1px solid #e0e0e0' }}>
        <div style={{ background: '#1a1a1a' }} className="px-5 py-4 flex items-center gap-3">
          {profile.avatar
            ? <img src={profile.avatar} alt={profile.name} className="w-10 h-10 rounded-full object-cover shrink-0" style={{ border: '2px solid #e63412' }} />
            : <div className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-xs font-bold text-white" style={{ background: '#e63412' }}>{profile.name.charAt(0)}</div>
          }
          <div className="min-w-0">
            <p className="text-white text-xs font-bold uppercase truncate" style={{ letterSpacing: '0.15em' }}>{profile.name}</p>
            <p className="text-xs truncate" style={{ color: '#8a8a8a' }}>{profile.email}</p>
          </div>
        </div>
        <div className="py-2">
          {profile.organModel && profile.organModel !== 'Not specified' && (
            <div className="px-5 py-2 border-b" style={{ borderColor: '#f0f0f0' }}>
              <p className="text-[10px] uppercase font-semibold" style={{ color: '#a4a4a4', letterSpacing: '0.2em' }}>Rodgers Model</p>
              <p className="text-xs mt-0.5 font-semibold" style={{ color: '#e63412' }}>{profile.organModel}</p>
            </div>
          )}
          <button onClick={onViewProfile} className="w-full text-left px-5 py-3 text-xs font-semibold uppercase transition-colors hover:bg-gray-50 border-b" style={{ color: '#e63412', letterSpacing: '0.15em', borderColor: '#f0f0f0' }}>
            Profile
          </button>
          <button onClick={onViewPerformances} className="w-full text-left px-5 py-3 text-xs font-semibold uppercase transition-colors hover:bg-gray-50 border-b" style={{ color: '#292929', letterSpacing: '0.15em', borderColor: '#f0f0f0' }}>
            Performances
          </button>
          <button onClick={onLogout} className="w-full text-left px-5 py-3 text-xs font-semibold uppercase transition-colors hover:bg-gray-50" style={{ color: '#292929', letterSpacing: '0.15em' }}>
            Log Out
          </button>
        </div>
      </div>
    </div>
  )
}

function EditProfileModal({
  profile, onSave, onDeleted, onClose,
}: {
  profile: Profile
  onSave: (p: Profile) => void
  onDeleted: () => void
  onClose: () => void
}) {
  const [name, setName] = useState(profile.name)
  const [musicalRole, setMusicalRole] = useState(profile.musicalRole || MUSICAL_ROLES[0])
  const [organModel, setOrganModel] = useState(profile.organModel || RODGERS_MODELS[0])
  const [location, setLocation] = useState(profile.location)
  const [church, setChurch] = useState(profile.church)
  const [yearsPlaying, setYearsPlaying] = useState(profile.yearsPlaying)
  const [interests, setInterests] = useState<string[]>(profile.interests ?? [])
  const [avatar, setAvatar] = useState(profile.avatar)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !file.type.startsWith('image/')) return
    const reader = new FileReader()
    reader.onload = ev => setAvatar(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return
    setSaving(true)
    setError('')
    try {
      const updated = await apiUpdateProfile(token, {
        display_name: name.trim() || profile.name,
        musical_role: musicalRole, organ_model: organModel, location, church, years_playing: yearsPlaying, avatar,
      })
      // interests/instruments aren't backend-tracked fields (see ProfileUpdateFields), so
      // backendUserToProfile always comes back with them blank -- carry the current values
      // forward instead of letting a profile save silently wipe them.
      onSave({ ...backendUserToProfile(updated), interests, instruments: profile.instruments })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save changes. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return
    setDeleting(true)
    try {
      await apiDeleteAccount(token)
      onDeleted()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete account. Please try again.')
      setDeleting(false)
      setConfirmingDelete(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-lg mx-4 flex flex-col" style={{ background: '#fff', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="flex items-center justify-between px-8 py-6" style={{ borderBottom: '1px solid #e8e5e1' }}>
          <h2 className="font-bold uppercase" style={{ fontSize: 15, letterSpacing: '0.12em', color: '#1a1a1a' }}>Edit Profile</h2>
          <button onClick={onClose} className="text-xs uppercase font-semibold transition-opacity hover:opacity-60" style={{ color: '#a0a0a0', letterSpacing: '0.15em' }}>✕</button>
        </div>

        <form onSubmit={handleSave} className="px-8 py-7 flex flex-col gap-5">
          <div className="flex items-center gap-5">
            <div
              className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center cursor-pointer shrink-0"
              style={{ border: '2px solid #e63412', background: '#f5f4f1' }}
              onClick={() => fileRef.current?.click()}
            >
              {avatar
                ? <img src={avatar} alt={name} className="w-full h-full object-cover" />
                : <span className="text-lg font-bold" style={{ color: '#e63412' }}>{name.charAt(0)}</span>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
            <button type="button" onClick={() => fileRef.current?.click()} className="text-xs font-bold uppercase transition-opacity hover:opacity-70" style={{ color: '#e63412', letterSpacing: '0.15em' }}>
              Change Photo
            </button>
          </div>

          <div>
            <label className="block text-[10px] uppercase font-bold mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.25em' }}>Full Name</label>
            <input required value={name} onChange={e => setName(e.target.value)}
              className="w-full px-4 py-2.5 text-sm outline-none" style={{ border: '1px solid #dddbd7', background: '#fafafa', color: '#1a1a1a' }} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase font-bold mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.25em' }}>Musical Role</label>
              <select value={musicalRole} onChange={e => setMusicalRole(e.target.value)}
                className="w-full px-4 py-2.5 text-sm outline-none" style={{ border: '1px solid #dddbd7', background: '#fafafa', color: '#1a1a1a' }}>
                {MUSICAL_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase font-bold mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.25em' }}>Rodgers Model</label>
              <select value={organModel} onChange={e => setOrganModel(e.target.value)}
                className="w-full px-4 py-2.5 text-sm outline-none" style={{ border: '1px solid #dddbd7', background: '#fafafa', color: '#1a1a1a' }}>
                {RODGERS_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase font-bold mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.25em' }}>Location</label>
              <input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Nashville, TN"
                className="w-full px-4 py-2.5 text-sm outline-none" style={{ border: '1px solid #dddbd7', background: '#fafafa', color: '#1a1a1a' }} />
            </div>
            <div>
              <label className="block text-[10px] uppercase font-bold mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.25em' }}>Church</label>
              <input value={church} onChange={e => setChurch(e.target.value)} placeholder="e.g. First Lutheran"
                className="w-full px-4 py-2.5 text-sm outline-none" style={{ border: '1px solid #dddbd7', background: '#fafafa', color: '#1a1a1a' }} />
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase font-bold mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.25em' }}>Years Playing</label>
            <input value={yearsPlaying} onChange={e => setYearsPlaying(e.target.value)} placeholder="e.g. 15"
              className="w-full px-4 py-2.5 text-sm outline-none" style={{ border: '1px solid #dddbd7', background: '#fafafa', color: '#1a1a1a' }} />
          </div>

          <div>
            <label className="block text-[10px] uppercase font-bold mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.25em' }}>Genres</label>
            <div className="flex flex-wrap gap-2">
              {MUSIC_GENRES.map(g => {
                const active = interests.includes(g)
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setInterests(prev => active ? prev.filter(x => x !== g) : [...prev, g])}
                    className="px-3 py-1.5 text-xs font-semibold transition-all"
                    style={{
                      background: active ? '#e63412' : '#fafafa',
                      color: active ? '#fff' : '#6a6a6a',
                      border: `1px solid ${active ? '#e63412' : '#dddbd7'}`,
                      borderRadius: 18,
                    }}
                  >
                    {g}
                  </button>
                )
              })}
            </div>
          </div>

          {error && <p className="text-xs" style={{ color: '#e63412' }}>{error}</p>}

          <div className="flex items-center gap-3 mt-2">
            <button type="submit" disabled={saving} className="btn flex-1" style={saving ? { opacity: 0.6 } : undefined}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button type="button" onClick={onClose} className="btn btn-outline" style={{ borderColor: '#dddbd7', color: '#8a8a8a' }}>Cancel</button>
          </div>
        </form>

        <div className="px-8 pb-8 pt-2" style={{ borderTop: '1px solid #e8e5e1' }}>
          {!confirmingDelete ? (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="text-xs font-semibold uppercase transition-opacity hover:opacity-70 mt-6"
              style={{ color: '#a0a0a0', letterSpacing: '0.12em' }}
            >
              Delete Account
            </button>
          ) : (
            <div className="mt-6 p-4" style={{ background: '#fdf1ee', border: '1px solid #e63412' }}>
              <p className="text-xs font-bold uppercase mb-1" style={{ color: '#e63412', letterSpacing: '0.1em' }}>Delete your account?</p>
              <p className="text-xs mb-4" style={{ color: '#6a6a6a' }}>
                This permanently deletes your profile and everything you've uploaded. This cannot be undone.
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="text-xs font-bold uppercase px-4 py-2"
                  style={{ background: '#e63412', color: '#fff', letterSpacing: '0.1em', opacity: deleting ? 0.6 : 1 }}
                >
                  {deleting ? 'Deleting…' : 'Yes, Delete My Account'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleting}
                  className="text-xs font-semibold uppercase"
                  style={{ color: '#8a8a8a', letterSpacing: '0.1em' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Shared profile header used by both ProfilePage and MemberProfilePage ---
function ProfileHeader({
  name, location, church, musicalRole, yearsPlaying, organModel, avatar,
  performanceCount, totalPlays, followerCount, followingCount,
  isOwn, isFollowing, onToggleFollow, onEdit, onShareScore,
}: {
  name: string; location: string; church: string; musicalRole: string
  yearsPlaying: string | number; organModel: string; avatar: string
  performanceCount: number; totalPlays: number; followerCount: number; followingCount?: number
  isOwn?: boolean; isFollowing?: boolean; onToggleFollow?: () => void
  onEdit?: () => void; onShareScore?: () => void
}) {
  const modelShort = organModel && organModel !== 'Not specified'
    ? organModel.replace(/^(Infinity|Imagine|Inspire|The American) Series /, '').replace('The American Series ', '')
    : null
  const seriesName = organModel && organModel !== 'Not specified'
    ? organModel.replace(/ \d.*$/, '')
    : null

  const artImg = organModel && organModel !== 'Not specified'
    ? Object.entries(MODEL_IMAGES).find(([k]) => k === organModel)?.[1] ?? null
    : null
  const seriesLogoImg = seriesName ? SERIES_LOGOS[seriesName.replace('The American Series', 'American').replace(' Series', '')] ?? null : null

  return (
    <div style={{ background: '#111111', overflow: 'hidden' }}>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row items-stretch" style={{ minHeight: 280 }}>

          {/* Left column: identity */}
          <div className="flex-1 px-8 py-10 flex flex-col justify-between">
            <div className="flex items-start gap-6">
              {/* Avatar */}
              <div className="shrink-0">
                {avatar
                  ? <img src={avatar} alt={name} className="w-24 h-24 rounded-full object-cover" style={{ border: '3px solid #E63412' }} />
                  : <div className="w-24 h-24 rounded-full flex items-center justify-center text-2xl font-bold text-white" style={{ background: '#E63412' }}>{name.charAt(0)}</div>
                }
              </div>

              {/* Name + meta */}
              <div className="flex-1 min-w-0 pt-1">
                <h1 className="text-white font-bold mb-1.5" style={{ fontSize: 'clamp(1.25rem,3vw,2rem)', letterSpacing: '-0.01em', lineHeight: 1.1 }}>{name}</h1>
                {(location || church) && (
                  <p className="text-sm mb-3" style={{ color: '#7a7a7a' }}>
                    {[church, location].filter(Boolean).join(' · ')}
                  </p>
                )}
                {/* Tags */}
                <div className="flex gap-1.5 flex-wrap mb-4">
                  {musicalRole && (
                    <span className="text-[10px] font-semibold uppercase px-2.5 py-1" style={{ background: '#1a1a1a', color: '#8a8a8a', border: '1px solid #2a2a2a', borderRadius: 4, letterSpacing: '0.12em' }}>{musicalRole}</span>
                  )}
                  {yearsPlaying && Number(yearsPlaying) > 0 && (
                    <span className="text-[10px] font-semibold uppercase px-2.5 py-1" style={{ background: '#1a1a1a', color: '#8a8a8a', border: '1px solid #2a2a2a', borderRadius: 4, letterSpacing: '0.12em' }}>{yearsPlaying} yrs</span>
                  )}
                  {modelShort && (
                    <span className="text-[10px] font-semibold uppercase px-2.5 py-1" style={{ background: 'rgba(230,52,18,0.1)', color: '#E63412', border: '1px solid rgba(230,52,18,0.25)', borderRadius: 4, letterSpacing: '0.12em' }}>Rodgers {modelShort}</span>
                  )}
                </div>
                {/* Actions */}
                <div className="flex gap-2.5 flex-wrap">
                  {isOwn ? (
                    <>
                      <button onClick={onShareScore} className="btn" style={{ fontSize: 11, padding: '10px 20px' }}>+ Share a Recording</button>
                      <button onClick={onEdit} className="btn btn-outline" style={{ fontSize: 11, padding: '9px 18px' }}>Edit Profile</button>
                    </>
                  ) : (
                    // Follow is local-state-only (no real social graph backend yet) — gated
                    // so a real /profile/:id page (reachable in prod) doesn't show a button
                    // that silently does nothing.
                    <Experimental>
                      <button
                        onClick={onToggleFollow}
                        className="btn"
                        style={isFollowing ? { background: 'transparent', border: '1px solid #E63412', color: '#E63412', fontSize: 11, padding: '10px 20px' } : { fontSize: 11, padding: '10px 20px' }}
                      >
                        {isFollowing ? 'Following ✓' : '+ Follow'}
                      </button>
                    </Experimental>
                  )}
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="flex gap-8 flex-wrap mt-6 pt-6" style={{ borderTop: '1px solid #1e1e1e' }}>
              {[
                [performanceCount.toString(), 'Performances'],
                [totalPlays.toLocaleString(), 'Plays'],
                // Follower/following counts are local-state-only (no real social graph
                // backend yet) — kept out of the real /profile/:id page in prod.
                ...(SHOW_EXPERIMENTAL ? [[followerCount.toString(), 'Followers']] : []),
                ...(SHOW_EXPERIMENTAL && followingCount !== undefined ? [[followingCount.toString(), 'Following']] : []),
              ].map(([n, label]) => (
                <div key={label as string}>
                  <p className="text-white font-bold text-xl" style={{ letterSpacing: '-0.01em' }}>{n}</p>
                  <p className="text-[10px] mt-0.5 uppercase" style={{ color: '#6a6a6a', letterSpacing: '0.15em' }}>{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right column: instrument photograph */}
          {artImg && (
            <div className="shrink-0 relative hidden md:block" style={{ width: 300, background: '#0a0a0a' }}>
              <img src={artImg} alt={organModel} className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.5, mixBlendMode: 'luminosity' }} />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #111111 0%, transparent 25%)' }} />
              <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
                {seriesLogoImg && (
                  <img src={seriesLogoImg} alt={seriesName ?? ''} className="object-contain w-full mb-2" style={{ maxHeight: 22, opacity: 0.7 }} />
                )}
                <p className="text-white font-bold text-lg text-center" style={{ letterSpacing: '0.1em' }}>{modelShort}</p>
              </div>
              <div className="absolute bottom-0 left-0 right-0" style={{ height: 3, background: 'linear-gradient(90deg, #E63412, #ff5533)' }} />
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

// --- Shared profile tab content ---
function ProfileTabs({
  performances, bio, musicalRole, organModel, church, location, yearsPlaying, interests,
  onSelectFile, onDeleteFile, onPlay, nowPlayingId, onShareScore, initialTab,
}: {
  performances: FileEntry[]; bio?: string
  musicalRole: string; organModel: string; church: string; location: string; yearsPlaying: string | number
  interests?: string[]
  onSelectFile: (f: FileEntry) => void; onDeleteFile?: (id: number) => void
  onPlay?: (f: FileEntry) => void; nowPlayingId?: number | null; onShareScore?: () => void
  // { tab, nonce } rather than a bare tab value, so clicking "Profile" or "Performances"
  // in the account menu always jumps to that tab even if it's already the active one
  // (a repeated identical string wouldn't re-trigger the sync effect below).
  initialTab?: { tab: 'performances' | 'about'; nonce: number }
}) {
  const [tab, setTab] = useState<'performances' | 'about'>(initialTab?.tab ?? 'performances')
  useEffect(() => { if (initialTab) setTab(initialTab.tab) }, [initialTab?.nonce])
  const mostPlayed = performances.length > 0 ? [...performances].sort((a, b) => b.downloads - a.downloads)[0] : null

  return (
    <div style={{ background: '#F4F3F0' }}>
      {/* Tab bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #D8D6D1' }}>
        <div className="max-w-5xl mx-auto px-6 flex items-center gap-0">
          {([['performances', 'Performances'], ['about', 'About']] as const).map(([t, label]) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="px-5 py-4 text-xs font-bold uppercase transition-all relative"
              style={{
                letterSpacing: '0.2em',
                color: tab === t ? '#1a1a1a' : '#a4a4a4',
                borderBottom: tab === t ? '2px solid #e63412' : '2px solid transparent',
              }}
            >
              {label}
              {t === 'performances' && performances.length > 0 && (
                <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5" style={{ background: tab === t ? '#e63412' : '#e0e0e0', color: tab === t ? '#fff' : '#a4a4a4', borderRadius: 3 }}>
                  {performances.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* ── Performances ── */}
        {tab === 'performances' && (
          <>
            {/* Most Played */}
            {mostPlayed && (
              <div className="mb-8">
                <p className="text-[10px] uppercase font-bold mb-4" style={{ color: '#a4a4a4', letterSpacing: '0.3em' }}>Most Played Performance</p>
                <div style={{ background: '#1a1a1a', border: '1px solid #292929' }} className="flex flex-col md:flex-row items-start md:items-center gap-5 p-5">
                  <div style={{ height: '100%', width: 3, background: '#e63412', flexShrink: 0, alignSelf: 'stretch', minHeight: 60 }} className="hidden md:block" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase font-bold mb-1" style={{ color: '#e63412', letterSpacing: '0.25em' }}>Most Played</p>
                    <h3 className="text-white font-bold text-base mb-0.5" style={{ letterSpacing: '-0.01em' }}>{mostPlayed.title}</h3>
                    <p className="text-sm" style={{ color: '#8a8a8a' }}>{mostPlayed.composer}{hasRealModel(mostPlayed.organModel) && <> · {mostPlayed.organModel}</>}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-center">
                      <p className="text-white font-bold text-xl">{mostPlayed.downloads.toLocaleString()}</p>
                      <p className="text-[9px] uppercase" style={{ color: '#8a8a8a', letterSpacing: '0.15em' }}>Plays</p>
                    </div>
                    {isPlayableAudio(mostPlayed) && (
                      <button
                        onClick={() => onPlay?.(mostPlayed)}
                        className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
                        style={{ background: nowPlayingId === mostPlayed.id ? '#e63412' : '#292929', color: '#fff', border: '1px solid #333' }}
                      >
                        {nowPlayingId === mostPlayed.id ? <span style={{ fontSize: 10, letterSpacing: 2 }}>■■</span> : <span style={{ fontSize: 14, marginLeft: 2 }}>▶</span>}
                      </button>
                    )}
                    <button
                      onClick={() => onSelectFile(mostPlayed)}
                      className="btn"
                      style={{ fontSize: 11, padding: '8px 16px 7px' }}
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            )}

            {performances.length === 0 ? (
              <div className="py-24 text-center">
                <TripleDivider />
                <p className="font-serif text-xl italic mb-4" style={{ color: '#8a8a8a' }}>No performances shared yet.</p>
                {onShareScore && <button onClick={onShareScore} className="btn">Share Your First Recording</button>}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {performances.map(file => (
                  <FileCard
                    key={file.id}
                    file={file}
                    onClick={() => onSelectFile(file)}
                    onDelete={onDeleteFile ? () => onDeleteFile(file.id) : undefined}
                    onViewMember={() => {}}
                    onPlay={onPlay}
                    isPlaying={nowPlayingId === file.id}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── About ── */}
        {tab === 'about' && (
          <div className="max-w-2xl">
            {[
              ['Musical Role', musicalRole],
              ['Rodgers Model', organModel !== 'Not specified' ? organModel : ''],
              ['Church / Venue', church],
              ['Location', location],
              ['Years Playing', yearsPlaying ? `${yearsPlaying} years` : ''],
            ].filter(([, v]) => v).map(([label, value]) => (
              <div key={label as string} className="flex gap-6 py-4" style={{ borderBottom: '1px solid #e8e8e8' }}>
                <p className="text-xs uppercase font-bold shrink-0" style={{ color: '#a4a4a4', letterSpacing: '0.2em', width: 140 }}>{label}</p>
                <p className="text-sm font-semibold" style={{ color: label === 'Rodgers Model' ? '#e63412' : '#1a1a1a' }}>{value}</p>
              </div>
            ))}
            {interests && interests.length > 0 && (
              <div className="py-4" style={{ borderBottom: '1px solid #e8e8e8' }}>
                <p className="text-xs uppercase font-bold mb-3" style={{ color: '#a4a4a4', letterSpacing: '0.2em' }}>Genres</p>
                <div className="flex flex-wrap gap-2">
                  {interests.map(i => (
                    <span key={i} className="text-xs font-semibold px-3 py-1" style={{ background: '#f0f0f0', color: '#1a1a1a', borderRadius: 14 }}>{i}</span>
                  ))}
                </div>
              </div>
            )}
            {bio && (
              <div className="py-4" style={{ borderBottom: '1px solid #e8e8e8' }}>
                <p className="text-xs uppercase font-bold mb-3" style={{ color: '#a4a4a4', letterSpacing: '0.2em' }}>Bio</p>
                <p className="text-sm leading-relaxed" style={{ color: '#434343' }}>{bio}</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  )
}

// --- Public Profile View (someone else's, loaded via a shared /profile/:id link) ---
function PublicProfileView({ userId, onBack }: { userId: string; onBack: () => void }) {
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [person, setPerson] = useState<PublicProfile | null>(null)
  const [recordings, setRecordings] = useState<PublicRecording[]>([])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setNotFound(false)
    apiFetchPublicProfile(userId)
      .then(p => {
        if (cancelled) return
        setPerson(p)
        return apiSearchFilesByOwner(userId).then(r => { if (!cancelled) setRecordings(r) })
      })
      .catch(() => { if (!cancelled) setNotFound(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [userId])

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0d0d0d', color: '#8a8a8a' }}>Loading profile…</div>
  }
  if (notFound || !person) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: '#0d0d0d', color: '#8a8a8a' }}>
        <p>This profile doesn't exist (or was deleted).</p>
        <button onClick={onBack} className="btn">Back to RodgersConnect</button>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ background: '#f5f5f5' }}>
      <nav style={{ background: '#111', borderBottom: '1px solid #222' }}>
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={onBack} className="text-xs font-bold uppercase" style={{ color: '#8a8a8a', letterSpacing: '0.15em' }}>← Back to RodgersConnect</button>
        </div>
      </nav>
      <ProfileHeader
        name={person.display_name}
        location={person.location}
        church={person.church}
        musicalRole={person.musical_role}
        yearsPlaying={person.years_playing}
        organModel={person.organ_model}
        avatar={person.avatar}
        performanceCount={recordings.length}
        totalPlays={0}
        followerCount={0}
        isOwn={false}
      />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="text-xs font-bold uppercase mb-4" style={{ color: '#8a8a8a', letterSpacing: '0.2em' }}>
          {recordings.length ? `${recordings.length} Shared File${recordings.length === 1 ? '' : 's'}` : 'No files shared yet'}
        </h2>
        <div className="flex flex-col gap-2">
          {recordings.map(r => (
            <div key={r.id} className="flex items-center justify-between px-4 py-3 bg-white" style={{ border: '1px solid #e8e5e1' }}>
              <div>
                <p className="text-sm font-semibold" style={{ color: '#1a1a1a' }}>{r.title}</p>
                {r.composer && <p className="text-xs" style={{ color: '#8a8a8a' }}>{r.composer}</p>}
              </div>
              <span className="text-[10px] font-bold uppercase px-2 py-1" style={{ background: '#f0f0f0', color: '#737373', letterSpacing: '0.1em' }}>{r.format}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// --- Profile Page (own) ---
function ProfilePage({
  profile,
  files,
  followingCount,
  onBack,
  onEdit,
  onShareScore,
  onSelectFile,
  onDeleteFile,
  onPlay,
  nowPlayingId,
  onAddInstrument,
  onEditInstrument,
  onViewInstrument,
  onRemoveInstrument,
  initialTab,
}: {
  profile: Profile
  files: FileEntry[]
  followingCount: number
  onBack: () => void
  onEdit: () => void
  onShareScore: () => void
  onSelectFile: (f: FileEntry) => void
  onDeleteFile: (id: number) => void
  onPlay?: (f: FileEntry) => void
  nowPlayingId?: number | null
  onAddInstrument?: () => void
  onEditInstrument?: (i: RodgersInstrument) => void
  onViewInstrument?: (i: RodgersInstrument) => void
  onRemoveInstrument?: (id: string) => void
  initialTab?: { tab: 'performances' | 'about'; nonce: number }
}) {
  const myFiles = files.filter(f => isOwnFile(f, profile.id))
  const totalPlays = myFiles.reduce((n, f) => n + f.downloads, 0)
  const MOCK_FOLLOWER_COUNT = 4

  return (
    <div className="min-h-screen" style={{ background: '#F4F3F0' }}>
      <nav style={{ background: '#111111', borderBottom: '1px solid #1e1e1e' }}>
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-xs uppercase font-semibold transition-opacity hover:opacity-70" style={{ color: '#666', letterSpacing: '0.2em' }}>
            ← Back
          </button>
          <a href="https://www.rodgersinstruments.com/" target="_blank" rel="noopener noreferrer" className="flex items-center shrink-0">
            <img src={connectLogo} alt="Rodgers Connect" className="h-7 w-auto" />
          </a>
        </div>
      </nav>

      <ProfileHeader
        name={profile.name} location={profile.location} church={profile.church}
        musicalRole={profile.musicalRole} yearsPlaying={profile.yearsPlaying}
        organModel={profile.organModel} avatar={profile.avatar}
        performanceCount={myFiles.length} totalPlays={totalPlays}
        followerCount={MOCK_FOLLOWER_COUNT} followingCount={followingCount}
        isOwn onEdit={onEdit} onShareScore={onShareScore}
      />

      <ProfileTabs
        performances={myFiles}
        bio={undefined}
        musicalRole={profile.musicalRole} organModel={profile.organModel}
        church={profile.church} location={profile.location} yearsPlaying={profile.yearsPlaying}
        interests={profile.interests}
        onSelectFile={onSelectFile} onDeleteFile={onDeleteFile}
        onPlay={onPlay} nowPlayingId={nowPlayingId} onShareScore={onShareScore}
        initialTab={initialTab}
      />

      {/* My Instruments section — entirely local Profile state (never sent to the backend;
          ProfileUpdateFields has no `instruments` field), lost on refresh. */}
      <Experimental>
      <div style={{ background: '#F4F3F0', borderTop: '1px solid #e8e5e1' }}>
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-[10px] uppercase font-bold mb-0.5" style={{ color: '#e63412', letterSpacing: '0.35em' }}>Your Rodgers</p>
              <h2 className="font-bold uppercase" style={{ fontSize: 13, letterSpacing: '0.2em', color: '#1a1a1a' }}>My Instruments</h2>
            </div>
            {onAddInstrument && (
              <button
                onClick={onAddInstrument}
                className="flex items-center gap-2 px-4 py-2 text-[10px] font-bold uppercase transition-opacity hover:opacity-80"
                style={{ background: '#e63412', color: '#fff', letterSpacing: '0.18em' }}
              >
                + Add Rodgers
              </button>
            )}
          </div>

          {(profile.instruments ?? []).length === 0 ? (
            <button
              onClick={onAddInstrument}
              className="w-full flex flex-col items-center justify-center py-12 transition-colors hover:border-[#e63412] group"
              style={{ border: '2px dashed #dddbd7', background: 'transparent' }}
            >
              <span className="w-12 h-12 flex items-center justify-center mb-3 font-bold text-white" style={{ background: '#1a1a1a', fontSize: 20 }}>+</span>
              <p className="font-bold uppercase text-xs mb-1 group-hover:text-[#e63412] transition-colors" style={{ letterSpacing: '0.18em', color: '#1a1a1a' }}>Add Your Rodgers</p>
              <p className="text-xs" style={{ color: '#9a9a9a' }}>Connect your instrument to discover compatible music</p>
            </button>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {(profile.instruments ?? []).map(inst => (
                <InstrumentCard
                  key={inst.id}
                  instrument={inst}
                  onView={() => onViewInstrument?.(inst)}
                  onEdit={() => onEditInstrument?.(inst)}
                  isOwn
                />
              ))}
              <button
                onClick={onAddInstrument}
                className="flex flex-col items-center justify-center py-8 transition-colors hover:border-[#e63412] group"
                style={{ border: '2px dashed #dddbd7', background: 'transparent', minHeight: 180 }}
              >
                <span className="w-10 h-10 flex items-center justify-center mb-2 font-bold text-white transition-colors" style={{ background: '#1a1a1a', fontSize: 18 }}>+</span>
                <p className="text-[10px] font-bold uppercase group-hover:text-[#e63412] transition-colors" style={{ letterSpacing: '0.15em', color: '#6a6a6a' }}>Add Instrument</p>
              </button>
            </div>
          )}
        </div>
      </div>
      </Experimental>

      <footer style={{ background: '#0d0d0d', borderTop: '1px solid #1a1a1a' }}>
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center text-white font-bold text-xs" style={{ background: '#e63412' }}>R</div>
            <span className="text-xs font-bold uppercase text-white" style={{ letterSpacing: '0.25em' }}>Rodgers Connect</span>
          </div>
          <p className="text-xs uppercase" style={{ color: '#8a8a8a', letterSpacing: '0.15em' }}>For the pipe organ community</p>
        </div>
      </footer>
    </div>
  )
}

// --- Member Profile Page (public view) ---
function MemberProfilePage({
  member, files, isFollowing, onToggleFollow, onBack, onSelectFile, onPlay, nowPlayingId, onViewInstrument,
}: {
  member: Member; files: FileEntry[]; isFollowing: boolean; onToggleFollow: () => void
  onBack: () => void; onSelectFile: (f: FileEntry) => void
  onPlay?: (f: FileEntry) => void; nowPlayingId?: number | null
  onViewInstrument?: (inst: RodgersInstrument, ownerName: string) => void
}) {
  const memberFiles = files.filter(f => f.uploader === member.name)
  const totalPlays = memberFiles.reduce((n, f) => n + f.downloads, 0)
  const MOCK_FOLLOWER_COUNT = 12

  // Derive a public instrument card from the member's organModel
  const derivedInstrument: RodgersInstrument | null = member.organModel && member.organModel !== 'Not specified' ? (() => {
    const modelNum = member.organModel.replace(/^.*?(\d.*)$/, '$1').trim()
    const seriesRaw = member.organModel.replace(/\s+\d.*$/, '').replace('The American Series', 'American').replace('Infinity Series', 'Infinity').replace('Imagine Series', 'Imagine').replace('Inspire Series', 'Inspire') as RodgersInstrument['series']
    return { id: `member-${member.name}`, series: seriesRaw, model: modelNum, nickname: '', church: member.church ?? '', softwareVersion: '', primary: true }
  })() : null

  return (
    <div className="min-h-screen" style={{ background: '#F4F3F0' }}>
      <nav style={{ background: '#111111', borderBottom: '1px solid #1e1e1e' }}>
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-xs uppercase font-semibold transition-opacity hover:opacity-70" style={{ color: '#666', letterSpacing: '0.2em' }}>
            ← Back
          </button>
          <a href="https://www.rodgersinstruments.com/" target="_blank" rel="noopener noreferrer" className="flex items-center shrink-0">
            <img src={connectLogo} alt="Rodgers Connect" className="h-7 w-auto" />
          </a>
        </div>
      </nav>

      <ProfileHeader
        name={member.name} location={member.location} church={member.church}
        musicalRole={member.musicalRole} yearsPlaying={member.yearsPlaying}
        organModel={member.organModel} avatar={member.avatar}
        performanceCount={memberFiles.length} totalPlays={totalPlays}
        followerCount={MOCK_FOLLOWER_COUNT}
        isFollowing={isFollowing} onToggleFollow={onToggleFollow}
      />

      {/* Public instrument cards */}
      {derivedInstrument && (
        <div style={{ background: '#0d0d0d', borderBottom: '1px solid #1a1a1a' }}>
          <div className="max-w-5xl mx-auto px-6 py-8">
            <p className="text-[9px] uppercase font-bold mb-5" style={{ color: '#3a3a3a', letterSpacing: '0.4em' }}>My Rodgers Instruments</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" style={{ maxWidth: 560 }}>
              <InstrumentCard
                instrument={derivedInstrument}
                onView={() => onViewInstrument?.(derivedInstrument, member.name)}
              />
            </div>
          </div>
        </div>
      )}

      <ProfileTabs
        performances={memberFiles}
        bio={member.bio}
        musicalRole={member.musicalRole} organModel={member.organModel}
        church={member.church} location={member.location} yearsPlaying={member.yearsPlaying}
        onSelectFile={onSelectFile}
        onPlay={onPlay} nowPlayingId={nowPlayingId}
      />

      <footer style={{ background: '#0d0d0d', borderTop: '1px solid #1a1a1a' }}>
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center text-white font-bold text-xs" style={{ background: '#e63412' }}>R</div>
            <span className="text-xs font-bold uppercase text-white" style={{ letterSpacing: '0.25em' }}>Rodgers Connect</span>
          </div>
          <p className="text-xs uppercase" style={{ color: '#8a8a8a', letterSpacing: '0.15em' }}>For the pipe organ community</p>
        </div>
      </footer>
    </div>
  )
}

// --- Hero instrument selector (logged-out) ---
function HeroModelSelector({ onShowMusic, onBrowseAll }: { onShowMusic: (model: string) => void; onBrowseAll: () => void }) {
  const [series, setSeries] = useState('')
  const [model, setModel] = useState('')

  const seriesOptions = ['Infinity', 'Imagine', 'Inspire']
  const modelsBySeries: Record<string, string[]> = {
    'Infinity': ['367', '489'],
    'Imagine': ['239', '241', '359'],
    'Inspire': ['234', '240', '352'],
  }
  const models = series ? modelsBySeries[series] ?? [] : []

  const handleSeries = (s: string) => { setSeries(s); setModel('') }

  const fullModel = series && model ? `${series} Series ${model}` : ''

  return (
    <div className="flex flex-col gap-3 w-full" style={{ maxWidth: 440 }}>
      {/* Series selector */}
      <div className="relative">
        <select
          value={series}
          onChange={e => handleSeries(e.target.value)}
          className="w-full px-4 py-3 text-sm appearance-none outline-none font-semibold"
          style={{ background: '#1c1c1c', border: `1px solid ${series ? '#E63412' : '#2e2e2e'}`, color: series ? '#fff' : '#666', borderRadius: 5, letterSpacing: '0.03em' }}
        >
          <option value="">Select your series</option>
          {seriesOptions.map(s => <option key={s} value={s}>{s} Series</option>)}
        </select>
        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#555' }}>▾</div>
      </div>
      {/* Model selector */}
      <div className="relative">
        <select
          value={model}
          onChange={e => setModel(e.target.value)}
          disabled={!series}
          className="w-full px-4 py-3 text-sm appearance-none outline-none font-semibold disabled:opacity-40"
          style={{ background: '#1c1c1c', border: `1px solid ${model ? '#E63412' : '#2e2e2e'}`, color: model ? '#fff' : '#666', borderRadius: 5, letterSpacing: '0.03em' }}
        >
          <option value="">Select your model</option>
          {models.map(m => <option key={m} value={m}>{series} {m}</option>)}
        </select>
        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#555' }}>▾</div>
      </div>
      {/* CTA */}
      <button
        onClick={() => fullModel ? onShowMusic(fullModel) : onBrowseAll()}
        className="btn w-full"
        style={{ fontSize: 12, padding: '13px 24px', letterSpacing: '0.2em', opacity: fullModel ? 1 : 0.5 }}
      >
        Show Music for My Rodgers
      </button>
      <button
        onClick={onBrowseAll}
        className="text-xs font-semibold uppercase transition-opacity hover:opacity-80 text-center"
        style={{ color: '#686868', letterSpacing: '0.15em' }}
      >
        Browse all music →
      </button>
    </div>
  )
}

// --- Model Showroom ---

type ShowroomSeries = 'ALL' | 'Infinity' | 'Imagine' | 'Inspire'

interface ShowroomModel {
  id: string
  series: ShowroomSeries
  number: string
  manuals: number
  image: string
  seriesLogo?: string
}

const SHOWROOM_MODELS: ShowroomModel[] = [
  { id: 'Inspire Series 234',      series: 'Inspire',      number: '234', manuals: 2, image: img234, seriesLogo: logoSeriesInspire },
  { id: 'Inspire Series 240',      series: 'Inspire',      number: '240', manuals: 2, image: img240, seriesLogo: logoSeriesInspire },
  { id: 'Inspire Series 352',      series: 'Inspire',      number: '352', manuals: 3, image: img352, seriesLogo: logoSeriesInspire },
  { id: 'Imagine Series 239',      series: 'Imagine',      number: '239', manuals: 2, image: img239, seriesLogo: logoSeriesImagine },
  { id: 'Imagine Series 241',      series: 'Imagine',      number: '241', manuals: 2, image: img241, seriesLogo: logoSeriesImagine },
  { id: 'Imagine Series 359',      series: 'Imagine',      number: '359', manuals: 3, image: img359, seriesLogo: logoSeriesImagine },
  { id: 'Infinity Series 367',     series: 'Infinity',     number: '367', manuals: 3, image: img367, seriesLogo: logoSeriesInfinity },
  { id: 'Infinity Series 489',     series: 'Infinity',     number: '489', manuals: 4, image: img489, seriesLogo: logoSeriesInfinity },
]

const MODEL_PRODUCT_URLS: Record<string, string> = {
  'Inspire Series 234':  'https://www.rodgersinstruments.com/inspire-series/inspire-234/',
  'Inspire Series 240':  'https://www.rodgersinstruments.com/inspire-series/inspire-240/',
  'Inspire Series 352':  'https://www.rodgersinstruments.com/inspire-series/inspire-352/',
  'Imagine Series 239':  'https://www.rodgersinstruments.com/imagine-series/imagine-239/',
  'Imagine Series 241':  'https://www.rodgersinstruments.com/imagine-series/imagine-241/',
  'Imagine Series 359':  'https://www.rodgersinstruments.com/imagine-series/imagine-359d/',
  'Infinity Series 367': 'https://www.rodgersinstruments.com/infinity-series/infinity-367/',
  'Infinity Series 489': 'https://www.rodgersinstruments.com/infinity-series/infinity-489/',
}

const SERIES_FILTER_LABELS: { key: ShowroomSeries; label: string }[] = [
  { key: 'ALL',      label: 'All Models' },
  { key: 'Inspire',  label: 'Inspire' },
  { key: 'Imagine',  label: 'Imagine' },
  { key: 'Infinity', label: 'Infinity' },
]

function ModelShowroom({
  onConfirm,
  onSelect,
  initialModel,
}: {
  onConfirm: (model: string) => void
  onSelect?: (model: string | null) => void
  initialModel?: string | null
}) {
  const [seriesFilter, setSeriesFilter] = useState<ShowroomSeries>('ALL')
  const [selected, setSelected] = useState<string | null>(initialModel ?? null)
  const [showAll, setShowAll] = useState(false)

  const allVisible = seriesFilter === 'ALL'
    ? SHOWROOM_MODELS
    : SHOWROOM_MODELS.filter(m => m.series === seriesFilter)
  const visible = showAll ? allVisible : allVisible.slice(0, 8)
  const hasMore = allVisible.length > 8 && !showAll

  const selectedModel = SHOWROOM_MODELS.find(m => m.id === selected)
  const shortName = (m: ShowroomModel) => `${m.series} ${m.number}`

  return (
    <div style={{ background: '#F5F3EF' }}>
      {/* Section intro */}
      <div className="text-center pt-20 pb-12 px-6">
        <p className="font-bold uppercase mb-4" style={{ fontSize: 10, color: '#E63412', letterSpacing: '0.55em' }}>Personalize Your Experience</p>
        <h2 className="font-bold uppercase mb-4" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.6rem)', letterSpacing: '-0.02em', color: '#0B0B0B', lineHeight: 1.05 }}>Choose Your Rodgers</h2>
        <p style={{ fontSize: 15, color: '#737373', maxWidth: 500, margin: '0 auto', lineHeight: 1.65 }}>
          Select your model to find compatible music, downloads and organists who play an instrument like yours.
        </p>
      </div>

      {/* Series filter tabs */}
      <div className="flex justify-center mb-10 px-6">
        <div className="flex items-center" style={{ borderBottom: `1px solid #D8D4CC` }}>
          {SERIES_FILTER_LABELS.map(({ key, label }) => {
            const active = seriesFilter === key
            return (
              <button
                key={key}
                onClick={() => { setSeriesFilter(key); setShowAll(false) }}
                className="font-bold uppercase transition-colors"
                style={{
                  fontSize: 11,
                  letterSpacing: '0.2em',
                  padding: '10px 20px',
                  color: active ? '#0B0B0B' : '#737373',
                  borderBottom: `2px solid ${active ? '#E63412' : 'transparent'}`,
                  marginBottom: -1,
                  background: 'transparent',
                }}
              >
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Model card grid */}
      <div className="mx-auto px-12 pb-6" style={{ maxWidth: 1280 }}>
        <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {visible.map(model => {
            const isSelected = selected === model.id
            return (
              <button
                key={model.id}
                onClick={() => {
                  const next = isSelected ? null : model.id
                  setSelected(next)
                  onSelect?.(next)
                }}
                className="group relative flex flex-col overflow-hidden text-left"
                style={{
                  background: '#0D0E0E',
                  border: `${isSelected ? '3px' : '1.5px'} solid ${isSelected ? '#E63412' : '#242424'}`,
                  height: 350,
                  transition: 'transform 0.18s, box-shadow 0.18s',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 32px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                }}
              >
                {/* Selected: red checkmark */}
                {isSelected && (
                  <div className="absolute top-3 right-3 z-10 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#E63412' }}>
                    <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                      <path d="M1 4.5L4 7.5L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}

                {/* Series wordmark */}
                <div className="px-4 pt-4 pb-0 shrink-0" style={{ height: 42 }}>
                  {model.seriesLogo ? (
                    <img src={model.seriesLogo} alt={model.series} className="object-contain object-left" style={{ maxHeight: 16, maxWidth: 100, opacity: 0.85 }} />
                  ) : (
                    <p className="font-bold uppercase text-white" style={{ fontSize: 9, letterSpacing: '0.4em', opacity: 0.8 }}>{model.series}</p>
                  )}
                </div>

                {/* Organ photo — bottom-aligned */}
                <div className="flex-1 relative overflow-hidden" style={{ minHeight: 0 }}>
                  <img
                    src={model.image}
                    alt={`Rodgers ${shortName(model)}`}
                    className="absolute inset-x-0 bottom-0 w-full transition-all duration-300"
                    style={{
                      height: '105%',
                      objectFit: 'contain',
                      objectPosition: 'bottom center',
                      filter: isSelected ? 'brightness(1.15)' : 'brightness(0.92)',
                    }}
                  />
                  {/* hover: brightness via overlay */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ background: 'rgba(255,255,255,0.04)' }} />
                  {/* hover: red bottom line */}
                  <div className="absolute bottom-0 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ height: 3, background: '#E63412' }} />
                  {/* selected: permanent red bottom line */}
                  {isSelected && <div className="absolute bottom-0 left-0 right-0" style={{ height: 3, background: '#E63412' }} />}
                </div>

                {/* Model info */}
                <div className="px-4 pt-3 pb-4 shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  {isSelected && (
                    <p className="font-bold uppercase mb-1" style={{ fontSize: 8, color: '#E63412', letterSpacing: '0.35em' }}>Your Rodgers</p>
                  )}
                  <p className="font-bold text-white" style={{ fontSize: 13, letterSpacing: '0.1em' }}>{shortName(model).toUpperCase()}</p>
                  <p className="font-bold uppercase mt-0.5" style={{ fontSize: 9, color: '#737373', letterSpacing: '0.2em' }}>{model.manuals} Manuals</p>
                </div>
              </button>
            )
          })}

        </div>
      </div>

      {/* View All Models button */}
      {hasMore && (
        <div className="flex justify-center mt-8 pb-2">
          <button
            onClick={() => setShowAll(true)}
            className="font-bold uppercase transition-opacity hover:opacity-70"
            style={{ fontSize: 11, letterSpacing: '0.2em', color: '#0B0B0B', border: `1px solid #D8D4CC`, padding: '13px 32px', minHeight: 48 }}
          >
            View All Models
          </button>
        </div>
      )}

      {/* Confirmation bar — black, immediate reward */}
      <div
        style={{
          maxHeight: selected ? 200 : 0,
          opacity: selected ? 1 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease, opacity 0.25s ease',
        }}
      >
        <div style={{ background: '#0B0B0B', borderTop: '1px solid #1a1a1a', borderBottom: '1px solid #1a1a1a', marginTop: 16 }}>
          <div className="mx-auto px-12 py-6" style={{ maxWidth: 1280 }}>
            <div className="flex items-center gap-8">
              {/* Thumbnail */}
              {selectedModel && (
                <div className="shrink-0 w-16 h-16 overflow-hidden flex items-center justify-center" style={{ background: '#141414', border: '1px solid #242424' }}>
                  <img src={selectedModel.image} alt={selectedModel.number} className="w-full h-full object-contain object-bottom" />
                </div>
              )}
              {/* Stats */}
              <div className="flex-1 min-w-0">
                <p className="font-bold uppercase mb-1" style={{ fontSize: 11, color: '#E63412', letterSpacing: '0.35em' }}>
                  {selectedModel ? `${shortName(selectedModel)} Selected` : ''}
                </p>
                <div className="flex items-center gap-6">
                  {[['28', 'compatible pieces'], ['14', 'performances'], ['9', `organists with this model`]].map(([n, label]) => (
                    <div key={label} className="flex items-baseline gap-1.5">
                      <span className="font-bold text-white" style={{ fontSize: 16 }}>{n}</span>
                      <span style={{ fontSize: 12, color: '#737373' }}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Actions */}
              <div className="flex items-center gap-5 shrink-0">
                <button
                  onClick={() => setSelected(null)}
                  className="font-bold uppercase transition-opacity hover:opacity-60"
                  style={{ fontSize: 11, color: '#737373', letterSpacing: '0.18em' }}
                >
                  Change Model
                </button>
                <button
                  onClick={() => {
                    if (!selected) return
                    onConfirm(selected)
                    setTimeout(() => {
                      document.getElementById('music-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }, 80)
                  }}
                  disabled={!selected}
                  className="btn"
                  style={{ fontSize: 11, padding: '14px 28px', letterSpacing: '0.16em', minHeight: 48, whiteSpace: 'nowrap' }}
                >
                  {selectedModel ? `View Music for ${shortName(selectedModel)}` : 'View Music'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: selected ? 0 : 80 }} />
    </div>
  )
}

// --- Reset Password (reached via a direct link from the reset email -- see App's
// resetToken below, which gates this in ahead of everything else the app normally
// renders) ---
function ResetPasswordScreen({ token }: { token: string }) {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return }
    setSubmitting(true)
    setError('')
    try {
      await apiResetPassword(token, newPassword)
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset your password.')
    } finally {
      setSubmitting(false)
    }
  }

  // A full reload (rather than just clearing local state) is deliberate -- this page is
  // reached standalone from an email link, outside the app's normal profile/auth state, so
  // there's nothing to "return to." Landing back on '/' cold is the correct, simple reset.
  const goToSignIn = () => {
    window.location.href = '/'
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0B0B0B' }}>
      <div className="w-full max-w-md" style={{ background: '#111', border: '1px solid #222', borderRadius: 4 }}>
        <div className="px-8 pt-8 pb-4">
          <p className="text-xs font-bold uppercase mb-2" style={{ color: '#e63412', letterSpacing: '0.3em' }}>RodgersConnect</p>
          <h2 className="text-white text-2xl font-bold">{done ? 'Password updated.' : 'Choose a new password'}</h2>
          {done && <p className="text-sm mt-2" style={{ color: '#8a8a8a' }}>You can now sign in with your new password.</p>}
        </div>
        {!done ? (
          <form onSubmit={handleSubmit} className="px-8 pb-8 pt-4 space-y-5">
            <div>
              <label className="block text-xs font-semibold uppercase mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.2em' }}>New Password</label>
              <input required type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="At least 8 characters"
                className="w-full px-4 py-3 text-sm outline-none" style={{ background: '#1a1a1a', border: '1px solid #333', color: '#fff', borderRadius: 3 }} />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase mb-1.5" style={{ color: '#8a8a8a', letterSpacing: '0.2em' }}>Confirm Password</label>
              <input required type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter your new password"
                className="w-full px-4 py-3 text-sm outline-none" style={{ background: '#1a1a1a', border: '1px solid #333', color: '#fff', borderRadius: 3 }} />
            </div>
            {error && <p className="text-xs" style={{ color: '#e63412' }}>{error}</p>}
            <button type="submit" disabled={submitting} className="btn w-full mt-2" style={submitting ? { opacity: 0.6 } : undefined}>
              {submitting ? 'Saving…' : 'Reset Password'}
            </button>
            <button type="button" onClick={goToSignIn} className="w-full text-center text-xs font-semibold uppercase transition-opacity hover:opacity-70" style={{ color: '#8a8a8a', letterSpacing: '0.2em' }}>
              ← Back to Sign In
            </button>
          </form>
        ) : (
          <div className="px-8 pb-8">
            <button type="button" onClick={goToSignIn} className="btn w-full">
              Go to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// --- Main App ---
export default function App() {
  // Reached via a direct link from a password-reset email (/reset-password?token=...) --
  // computed once on mount (there's no in-app way to navigate here, so it never needs to
  // react to popstate the way routeProfileId below does) and, if present, takes over the
  // entire render ahead of every other hook in this component. Stable across this
  // component instance's whole lifetime since nothing ever calls its setter, so the early
  // return below can't change which hooks get called between renders.
  const [resetToken] = useState<string | null>(() => {
    if (window.location.pathname !== '/reset-password') return null
    return new URLSearchParams(window.location.search).get('token')
  })

  const [profile, setProfile] = useState<Profile | null>(null)
  const [editingProfile, setEditingProfile] = useState(false)
  const [authIntent, setAuthIntent] = useState<'signup' | 'login'>('signup')
  const [showEditProfile, setShowEditProfile] = useState(false)

  if (resetToken) return <ResetPasswordScreen token={resetToken} />

  // Profile URLs (/profile/:id) — no router library, just enough History API to make
  // profiles shareable, matching how the rest of this app already manages view state by
  // hand rather than through a framework.
  const [routeProfileId, setRouteProfileId] = useState<string | null>(() => {
    const m = window.location.pathname.match(/^\/profile\/([^/]+)\/?$/)
    return m ? m[1] : null
  })
  useEffect(() => {
    const onPopState = () => {
      const m = window.location.pathname.match(/^\/profile\/([^/]+)\/?$/)
      setRouteProfileId(m ? m[1] : null)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])
  const goToMyProfile = useCallback(() => {
    if (!profile) return
    window.history.pushState({}, '', `/profile/${profile.id}`)
    setRouteProfileId(profile.id)
    setView('profile')
  }, [profile])
  const goHome = useCallback(() => {
    window.history.pushState({}, '', '/')
    setRouteProfileId(null)
    setView('home')
  }, [])
  // Navigate to any user's real public profile (e.g. a file's real owner_id) — same History
  // API mechanism as goToMyProfile/goHome, just for an arbitrary id rather than your own.
  const goToProfileId = useCallback((id: string) => {
    window.history.pushState({}, '', `/profile/${id}`)
    setRouteProfileId(id)
  }, [])
  const saveModelChange = useCallback(async (modelId: string) => {
    if (!profile) return
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return
    setSavingModel(true)
    setModelChangeError('')
    try {
      const updated = await apiUpdateProfile(token, {
        display_name: profile.name,
        musical_role: profile.musicalRole,
        organ_model: modelId,
        location: profile.location,
        church: profile.church,
        years_playing: profile.yearsPlaying,
        avatar: profile.avatar,
      })
      setProfile(backendUserToProfile(updated))
      setChangingModel(false)
      setSelectedShowroomModel(null)
    } catch (err) {
      setModelChangeError(err instanceof Error ? err.message : 'Could not save your model. Please try again.')
    } finally {
      setSavingModel(false)
    }
  }, [profile])

  // Restore session from a previously-issued token on load.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return
    apiFetchMe(token)
      .then(user => setProfile(backendUserToProfile(user)))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
  }, [])

  // If the URL is /profile/:id and that turns out to be our own id (e.g. a page refresh
  // or a bookmark of your own profile), show the live "My Profile" view for it once the
  // session restore above resolves — rather than requiring a click to get there.
  useEffect(() => {
    if (profile && routeProfileId === profile.id) setView('profile')
  }, [profile, routeProfileId])

  const logout = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (token) apiLogout(token)
    localStorage.removeItem(TOKEN_KEY)
    setProfile(null)
    setShowProfileMenu(false)
    setView('home')
    // Drop anything uploaded this session that has no real ownerId (the still-mock,
    // video-link-only submissions) -- otherwise it'd keep showing as "mine"
    // under whichever account logs in next, since there's no real id to tell accounts
    // apart for those. Real backend files stay, since isOwnFile() now checks ownerId.
    setFiles(prev => prev.filter(f => f.remoteId))
  }, [])
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [profileInitialTab, setProfileInitialTab] = useState<{ tab: 'performances' | 'about'; nonce: number }>({ tab: 'performances', nonce: 0 })
  const [view, setView] = useState<'home' | 'profile'>('home')
  const [activeTab, setActiveTab] = useState<'discover' | 'community' | 'monthly'>('discover')
  const [discoverView, setDiscoverView] = useState<'home' | 'library'>('home')
  const [memberSearch, setMemberSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState<Category>('All')
  const [query, setQuery] = useState('')
  const [peopleResults, setPeopleResults] = useState<PublicProfile[]>([])

  // Searching a name should surface the person too, not just recordings whose own
  // title/composer/description happens to match -- music search never looks at the
  // uploader's name at all, so "tim" would otherwise never find Tim Eastbrower.
  // Debounced so this doesn't fire on every keystroke.
  useEffect(() => {
    const q = query.trim()
    if (!q) { setPeopleResults([]); return }
    let cancelled = false
    const timer = setTimeout(() => {
      apiSearchUsers(q)
        .then(results => { if (!cancelled) setPeopleResults(results) })
        .catch(() => { if (!cancelled) setPeopleResults([]) })
    }, 300)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [query])

  const [showUpload, setShowUpload] = useState(false)
  const [selected, setSelected] = useState<FileEntry | null>(null)
  const [sortBy, setSortBy] = useState<'date' | 'downloads'>('date')
  const [files, setFiles] = useState<FileEntry[]>([])

  // Populate the Discover Library from the real backend on mount — this used to be a
  // hardcoded FILES seed array; now it's every uploader's real recordings. Merge by remoteId
  // so this doesn't fight with files this session already added locally (e.g. right after an
  // upload, before this fetch resolves).
  useEffect(() => {
    let cancelled = false
    apiSearchAllFiles()
      .then(records => {
        if (cancelled) return
        setFiles(prev => {
          const existingRemoteIds = new Set(prev.map(f => f.remoteId).filter(Boolean))
          const fetched = records.filter(r => !existingRemoteIds.has(r.id)).map(backendRecordingToFileEntry)
          return [...prev, ...fetched]
        })
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])
  const [activeModel, setActiveModel] = useState<string>('All')
  const [viewingMember, setViewingMember] = useState<string | null>(null)
  const [following, setFollowing] = useState<Set<string>>(new Set())
  const [profileGateAction, setProfileGateAction] = useState<'share' | 'download' | null>(null)
  const [showSignInPrompt, setShowSignInPrompt] = useState<string | null>(null)
  const [fbShareEntry, setFbShareEntry] = useState<FileEntry | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [savedFiles, setSavedFiles] = useState<Set<number>>(new Set())
  const [nowPlaying, setNowPlaying] = useState<FileEntry | null>(null)
  // The one real source of truth for whether audio is actually playing right now --
  // reported by AudioPlayerBar itself (a controlled component for this value), so every
  // Listen/Stop button elsewhere reflects what's genuinely audible instead of just "is
  // this the file loaded in the bar" (which stays true while paused, by design -- the bar
  // shouldn't close just because playback paused).
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  const playingFileId = isAudioPlaying ? nowPlaying?.id : undefined
  const [likedFiles, setLikedFiles] = useState<Set<number>>(new Set())
  const [likeCounts, setLikeCounts] = useState<Record<number, number>>({})
  const [comments, setComments] = useState<SongComment[]>([])
  const [openValueCard, setOpenValueCard] = useState<string | null>(null)
  const [musicFilter, setMusicFilter] = useState<string>('latest')
  const [selectedShowroomModel, setSelectedShowroomModel] = useState<string | null>(null)
  const [changingModel, setChangingModel] = useState(false)
  const [savingModel, setSavingModel] = useState(false)
  const [modelChangeError, setModelChangeError] = useState('')
  const [communitySubTab, setCommunitySubTab] = useState<'overview' | 'feed' | 'music' | 'registrations' | 'questions' | 'members' | 'discussions' | 'challenge' | 'live'>('feed')
  const [communityScope, setCommunityScope] = useState<'mymodel' | 'myseries' | 'allrodgers'>('mymodel')
  const [showCreatePost, setShowCreatePost] = useState(false)
  const [createPostType, setCreatePostType] = useState<string>('performance')
  const [showCommunityModelOverlay, setShowCommunityModelOverlay] = useState(false)
  const [feedFilter, setFeedFilter] = useState<string>('foryou')
  const [memberFilter, setMemberFilter] = useState<string>('mostactive')
  const [modelSearch, setModelSearch] = useState('')
  const [familyFilter, setFamilyFilter] = useState<string>('Imagine')
  const [homeContentTab, setHomeContentTab] = useState<'music' | 'registrations' | 'community' | 'resources'>('music')
  const [overlaySeries, setOverlaySeries] = useState<string>('Imagine')
  const [postLikes, setPostLikes] = useState<Set<string>>(new Set())
  const [postLikeCounts, setPostLikeCounts] = useState<Record<string, number>>({ f1: 14, f2: 7, f3: 31, f4: 9, f5: 19, f6: 3 })
  const [postComments, setPostComments] = useState<Record<string, { id: string; author: string; model?: string; text: string; time: string }[]>>({
    f1: [
      { id: 'c1a', author: 'Stacy Piontek', model: 'Imagine 239', text: "Gorgeous phrasing on the opening pedal statement. What division are you using for that reedy Principal sound?", time: '18 minutes ago' },
      { id: 'c1b', author: 'Catherine Reinhardt', model: 'Inspire 352', text: "This registration worked beautifully when I tried it on my 352. I reduced the Great division slightly and it sat perfectly in the mix.", time: '42 minutes ago' },
      { id: 'c1c', author: 'Jan Kraybill', model: 'Infinity 367', text: "The articulation in the manual runs is exactly right. Well done.", time: '1 hour ago' },
    ],
    f3: [
      { id: 'c3a', author: 'Anna Vogel', model: 'Inspire 352', text: "Downloaded and tested this immediately — it is excellent. The 8ft solo trumpet cuts through without dominating the accompaniment.", time: '20 hours ago' },
      { id: 'c3b', author: 'Michael Torres', model: 'Imagine 241', text: "Would this translate to the Imagine 241? The Inspire and Imagine series share some similar reed voicing.", time: '23 hours ago' },
    ],
    f4: [
      { id: 'c4a', author: 'Dan Miller', model: 'Infinity 367', text: "Inspiring entry. The acoustic at St. Mark's really suits that piece.", time: '22 hours ago' },
    ],
  })
  const [openCommentPost, setOpenCommentPost] = useState<string | null>(null)
  const [commentDraft, setCommentDraft] = useState('')
  const [copiedPost, setCopiedPost] = useState<string | null>(null)
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set())
  const [commentLikes, setCommentLikes] = useState<Set<string>>(new Set())
  const [showAddInstrument, setShowAddInstrument] = useState(false)
  const [editingInstrument, setEditingInstrument] = useState<RodgersInstrument | null>(null)
  const [viewingInstrument, setViewingInstrument] = useState<{ instrument: RodgersInstrument; ownerName: string } | null>(null)
  const [showGuidelines, setShowGuidelines] = useState(false)

  const toggleLike = (fileId: number) => {
    requireAccount(() => {
      setLikedFiles(prev => {
        const next = new Set(prev)
        const wasLiked = next.has(fileId)
        wasLiked ? next.delete(fileId) : next.add(fileId)
        setLikeCounts(counts => ({ ...counts, [fileId]: Math.max(0, (counts[fileId] ?? 0) + (wasLiked ? -1 : 1)) }))
        return next
      })
    })
  }

  const addComment = (fileId: number, text: string) => {
    if (!profile || !text.trim()) return
    setComments(prev => [...prev, {
      id: Date.now(),
      fileId,
      author: profile.name,
      avatar: profile.avatar,
      text: text.trim(),
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    }])
  }

  const handlePlay = useCallback((file: FileEntry) => {
    if (!isPlayableAudio(file)) return
    if (nowPlaying?.id === file.id) {
      // Same track already loaded (playing or paused) -- toggle play/pause instead of
      // unmounting the bar, matching what the bar's own play/pause button does.
      setIsAudioPlaying(p => !p)
    } else {
      setNowPlaying(file)
      setIsAudioPlaying(true)
    }
  }, [nowPlaying])

  const usedModels = ['All', ...Array.from(new Set(files.map(f => f.organModel).filter(Boolean)))]

  const myInstrumentModels = (profile?.instruments ?? []).map(i =>
    RODGERS_MODELS.find(m => m.includes(i.model)) ?? ''
  ).filter(Boolean)

  const filtered = files
    .filter(f => query || activeCategory === 'All' || f.category === activeCategory)  // query active: bypass category filter too, same reasoning as the model filter below
    .filter(f => {
      if (query) return true  // query active: bypass model filter so search works across all models
      if (activeModel === 'All') return true
      if (activeModel === 'my-instruments') return myInstrumentModels.some(m => f.organModel === m)
      return f.organModel === activeModel
    })
    .filter(f => {
      const q = query.toLowerCase()
      return !q
        || f.title.toLowerCase().includes(q)
        || f.composer.toLowerCase().includes(q)
        || f.description.toLowerCase().includes(q)
        || (f.tags ?? []).some(t => t.toLowerCase().includes(q))
    })
    .sort((a, b) => sortBy === 'downloads' ? b.downloads - a.downloads : b.id - a.id)

  const deleteFile = async (id: number) => {
    const target = files.find(f => f.id === id)
    if (target?.remoteId) {
      const token = localStorage.getItem(TOKEN_KEY)
      if (token) {
        try {
          await apiDeleteFile(token, target.remoteId)
        } catch {
          // Fall through and remove it locally anyway — the file might already be gone
          // server-side, and there's no dedicated error UI on this path yet.
        }
      }
    }
    setFiles(prev => prev.filter(f => f.id !== id))
    setSelected(null)
  }

  const requireProfile = (action: 'share' | 'download', fn: () => void) => {
    if (!profile) {
      setProfileGateAction(action)
      setAuthIntent('signup')
      setEditingProfile(true)
    } else {
      fn()
    }
  }

  // Gate only for actions that require an account; browsing/listening is always free
  const requireAccount = (fn: () => void) => {
    if (!profile) {
      setProfileGateAction('download')
      setAuthIntent('signup')
      setEditingProfile(true)
    } else {
      fn()
    }
  }

  function saveInstrument(inst: RodgersInstrument) {
    setProfile(prev => {
      if (!prev) return prev
      const existing = prev.instruments ?? []
      const idx = existing.findIndex(i => i.id === inst.id)
      const updated = idx >= 0
        ? existing.map((i, n) => n === idx ? inst : i)
        : [...existing, inst]
      // If marked primary, demote others
      const final = inst.primary ? updated.map(i => i.id === inst.id ? i : { ...i, primary: false }) : updated
      return { ...prev, instruments: final }
    })
  }

  function removeInstrument(id: string) {
    setProfile(prev => prev ? { ...prev, instruments: (prev.instruments ?? []).filter(i => i.id !== id) } : prev)
  }

  // A /profile/:id URL for someone other than the signed-in user (or no one signed in at
  // all) — fetch and show their real public profile rather than the live "My Profile" view.
  // Which top-level screen to show. Assigned (never early-returned) so the shared
  // overlays below -- DetailPanel, AudioPlayerBar, UploadModal, etc. -- render from a
  // single spot that every screen falls through to, instead of each screen mounting its
  // own duplicate copies. Duplicated overlays used to mean React fully unmounted and
  // remounted things like AudioPlayerBar on every navigation between screens (they're
  // structurally different subtrees), which silently killed playback when the modal
  // wasn't duplicated somewhere, or restarted it from the top when it was (AudioPlayerBar
  // assumes -- see its own comment -- that it's never remounted while a track is loaded).
  let mainContent: React.ReactNode

  if (routeProfileId && routeProfileId !== profile?.id) {
    mainContent = <PublicProfileView userId={routeProfileId} onBack={goHome} />
  } else if (viewingInstrument) {
    mainContent = (
      <InstrumentProfilePage
        instrument={viewingInstrument.instrument}
        files={files}
        ownerName={viewingInstrument.ownerName}
        isOwn={viewingInstrument.ownerName === profile?.name || viewingInstrument.ownerName === 'You'}
        onBack={() => setViewingInstrument(null)}
        onEdit={() => { setEditingInstrument(viewingInstrument.instrument); setShowAddInstrument(true); setViewingInstrument(null) }}
        onPlay={handlePlay}
        nowPlayingId={playingFileId}
        onSelectFile={f => setSelected(f)}
      />
    )
  } else if (editingProfile) {
    mainContent = (
      <ProfileSetup
        mode={authIntent}
        onComplete={p => {
          setProfile(p)
          setEditingProfile(false)
          if (profileGateAction === 'share') setShowUpload(true)
          setProfileGateAction(null)
        }}
        onSkip={() => {
          setEditingProfile(false)
          setProfileGateAction(null)
        }}
      />
    )
  } else if (viewingMember && MEMBERS[viewingMember]) {
    const member = MEMBERS[viewingMember]
    mainContent = (
      <MemberProfilePage
        member={member}
        files={files}
        isFollowing={following.has(viewingMember)}
        onToggleFollow={() => setFollowing(prev => {
          const next = new Set(prev)
          next.has(viewingMember) ? next.delete(viewingMember) : next.add(viewingMember)
          return next
        })}
        onBack={() => setViewingMember(null)}
        onSelectFile={f => setSelected(f)}
        onPlay={handlePlay}
        nowPlayingId={playingFileId}
        onViewInstrument={(inst, owner) => setViewingInstrument({ instrument: inst, ownerName: owner })}
      />
    )
  } else if (view === 'profile') {
    // No profile: can't view one -- render nothing for the main screen (shared overlays
    // below still render normally; none of them depend on being on a particular screen).
    mainContent = !profile ? null : (
      <ProfilePage
        profile={profile}
        files={files}
        followingCount={following.size}
        onBack={goHome}
        onEdit={() => setShowEditProfile(true)}
        onShareScore={() => setShowUpload(true)}
        onSelectFile={f => setSelected(f)}
        onDeleteFile={deleteFile}
        onPlay={handlePlay}
        nowPlayingId={playingFileId}
        onAddInstrument={() => { setEditingInstrument(null); setShowAddInstrument(true) }}
        onEditInstrument={i => { setEditingInstrument(i); setShowAddInstrument(true) }}
        onViewInstrument={i => setViewingInstrument({ instrument: i, ownerName: profile.name })}
        onRemoveInstrument={removeInstrument}
        initialTab={profileInitialTab}
      />
    )
  } else {
  // Hoisted so both the header's compact model indicator and the Discover Home view below
  // can share one computation -- profile instruments take precedence; the showroom
  // selection is the fallback for a visitor who hasn't signed in yet.
  const headerProfileModel = (() => {
    const inst = (profile?.instruments ?? []).find(i => i.primary) ?? (profile?.instruments ?? [])[0]
    if (inst) return `${inst.series} ${inst.model}`
    return (profile?.organModel && profile.organModel !== 'Not specified') ? profile.organModel : null
  })()
  const headerUserModel = headerProfileModel ?? selectedShowroomModel
  const headerShortModel = headerUserModel ? headerUserModel.replace(' Series', '') : null
  mainContent = (
    <div className="min-h-screen" style={{ background: '#F4F3F0' }}>
      {/* Header */}
      <header style={{ background: '#111111', borderBottom: '1px solid #1e1e1e', position: 'sticky', top: 0, zIndex: 40 }}>
        <div className="flex items-center gap-3 px-5" style={{ maxWidth: 1440, margin: '0 auto', height: 68 }}>
          {/* Logo */}
          <a href="https://www.rodgersinstruments.com/" target="_blank" rel="noopener noreferrer" className="flex items-center shrink-0 mr-2">
            <img src={connectLogo} alt="Rodgers Connect" className="h-6 w-auto" />
          </a>

          {/* Primary nav — hidden on mobile */}
          <nav className="hidden md:flex items-center gap-0 h-full shrink-0">
            {([['discover', 'Discover'], ['community', 'Community'], ['monthly', 'Monthly Offering']] as const).filter(([tab]) => SHOW_EXPERIMENTAL || tab === 'discover').map(([tab, label]) => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); if (tab === 'discover') setDiscoverView('home'); window.scrollTo({ top: 0 }) }}
                className="h-full px-3.5 text-xs font-semibold uppercase transition-colors whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-0"
                style={{
                  letterSpacing: '0.14em',
                  color: activeTab === tab ? '#ffffff' : '#686868',
                  borderBottom: activeTab === tab ? '2px solid #E63412' : '2px solid transparent',
                  marginBottom: -1, outlineColor: '#E63412',
                }}
              >{label}</button>
            ))}
          </nav>

          {/* Search */}
          <div className="hidden md:flex items-center" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderBottom: discoverView === 'library' ? '2px solid #E63412' : '1px solid #2a2a2a', borderRadius: 5, width: 220 }}>
            <svg className="ml-2.5 shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#484848" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={query} onChange={e => { setQuery(e.target.value); if (e.target.value) { setDiscoverView('library'); setActiveModel('All') } }} onKeyDown={e => { if (e.key === 'Enter' && query) { setDiscoverView('library'); setActiveModel('All') } }} placeholder="Search music…" className="flex-1 px-2.5 py-2 text-xs outline-none min-w-0" style={{ background: 'transparent', color: '#c8c8c8' }} />
            {query && <button onClick={() => setQuery('')} className="mr-2 shrink-0" style={{ color: '#484848', fontSize: 14, lineHeight: 1 }}>×</button>}
          </div>

          <div className="flex-1" />

          {/* Right actions */}
          <div className="flex items-center gap-2 shrink-0">
            {profile ? (
              <>
                <button onClick={() => setShowUpload(true)} className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase transition-all hover:opacity-80" style={{ color: '#aaa', border: '1px solid #2e2e2e', borderRadius: 5, letterSpacing: '0.12em' }}>Share</button>
                {/* Persistent "your organ" indicator -- replaces the old full-width banner
                    that used to sit in the middle of the Discover home page for every
                    returning member; this is the one place it's shown now. */}
                {headerUserModel && (
                  <div
                    className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5"
                    style={{ border: '1px solid #2a2a2a', borderRadius: 5 }}
                    title={`Your organ: ${headerShortModel}`}
                  >
                    <span className="uppercase" style={{ fontSize: 9, color: '#686868', letterSpacing: '0.1em' }}>Your Rodgers</span>
                    <span className="font-bold" style={{ fontSize: 11, color: '#fff' }}>{headerShortModel}</span>
                  </div>
                )}
                <button onClick={() => setShowProfileMenu(v => !v)} className="flex items-center gap-2 px-2.5 py-1.5 transition-all hover:opacity-80" style={{ border: '1px solid #2a2a2a', borderRadius: 5 }}>
                  {profile.avatar ? <img src={profile.avatar} alt={profile.name} className="w-6 h-6 rounded-full object-cover shrink-0" style={{ border: '1.5px solid #E63412' }} /> : <div className="w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold text-white" style={{ background: '#E63412' }}>{profile.name.charAt(0)}</div>}
                  <span className="hidden sm:block text-xs font-semibold text-white">{profile.name.split(' ')[0]}</span>
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { setAuthIntent('login'); setEditingProfile(true) }} className="hidden lg:block text-xs font-semibold uppercase transition-opacity hover:opacity-70" style={{ color: '#686868', letterSpacing: '0.12em' }}>Sign In</button>
                <button onClick={() => { setAuthIntent('signup'); setEditingProfile(true) }} className="btn hidden sm:inline-flex" style={{ padding: '8px 16px', fontSize: 10, letterSpacing: '0.1em' }}>Create Free Account</button>
              </>
            )}
            {/* Hamburger — mobile only */}
            <button
              className="md:hidden flex flex-col justify-center items-center w-10 h-10 gap-1.5 shrink-0"
              onClick={() => setMobileMenuOpen(v => !v)}
              aria-label="Menu"
            >
              <span className="block w-5 h-0.5 transition-all" style={{ background: mobileMenuOpen ? '#E63412' : '#686868' }} />
              <span className="block w-5 h-0.5 transition-all" style={{ background: mobileMenuOpen ? '#E63412' : '#686868' }} />
              <span className="block w-5 h-0.5 transition-all" style={{ background: mobileMenuOpen ? '#E63412' : '#686868' }} />
            </button>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden" style={{ background: '#111', borderTop: '1px solid #1e1e1e' }}>
            {/* Search */}
            <div className="px-5 py-3" style={{ borderBottom: '1px solid #1e1e1e' }}>
              <div className="flex items-center" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderBottom: discoverView === 'library' ? '2px solid #E63412' : '1px solid #2a2a2a', borderRadius: 5 }}>
                <svg className="ml-3 shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#484848" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input value={query} onChange={e => { setQuery(e.target.value); if (e.target.value) { setDiscoverView('library'); setActiveModel('All'); setMobileMenuOpen(false) } }} onKeyDown={e => { if (e.key === 'Enter' && query) { setDiscoverView('library'); setActiveModel('All'); setMobileMenuOpen(false) } }} placeholder="Search music…" className="flex-1 px-3 py-3 text-sm outline-none" style={{ background: 'transparent', color: '#c8c8c8' }} />
              </div>
            </div>
            {/* Nav links */}
            {([['discover', 'Discover Music'], ['community', 'Community'], ['monthly', 'Monthly Offering']] as const).filter(([tab]) => SHOW_EXPERIMENTAL || tab === 'discover').map(([tab, label]) => (
              <button key={tab} onClick={() => { setActiveTab(tab); window.scrollTo({ top: 0 }); setMobileMenuOpen(false) }} className="w-full text-left px-6 py-4 text-sm font-semibold uppercase transition-colors" style={{ letterSpacing: '0.14em', color: activeTab === tab ? '#fff' : '#686868', borderBottom: '1px solid #1a1a1a', background: activeTab === tab ? '#1a1a1a' : 'transparent' }}>{label}</button>
            ))}
            {/* Auth */}
            <div className="px-5 py-4 flex gap-3">
              {profile ? (
                <button onClick={() => { setShowProfileMenu(v => !v); setMobileMenuOpen(false) }} className="flex items-center gap-3 flex-1">
                  {profile.avatar ? <img src={profile.avatar} alt={profile.name} className="w-9 h-9 rounded-full object-cover" style={{ border: '2px solid #E63412' }} /> : <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-white" style={{ background: '#E63412' }}>{profile.name.charAt(0)}</div>}
                  <span className="text-sm font-semibold text-white">{profile.name}</span>
                </button>
              ) : (
                <>
                  <button onClick={() => { setAuthIntent('login'); setEditingProfile(true); setMobileMenuOpen(false) }} className="flex-1 py-3 text-sm font-semibold uppercase text-center" style={{ color: '#686868', border: '1px solid #2a2a2a', borderRadius: 5, letterSpacing: '0.12em' }}>Sign In</button>
                  <button onClick={() => { setAuthIntent('signup'); setEditingProfile(true); setMobileMenuOpen(false) }} className="btn flex-1 justify-center" style={{ fontSize: 11, letterSpacing: '0.1em' }}>Create Free Account</button>
                </>
              )}
            </div>
          </div>
        )}

      </header>

      {activeTab === 'discover' && <>

      {/* ── DISCOVER HOME ── */}
      {discoverView === 'home' && (() => {
        // profile instruments take precedence; showroom selection falls back for new visitors
        const profileModel = (() => {
          const inst = (profile?.instruments ?? []).find(i => i.primary) ?? (profile?.instruments ?? [])[0]
          if (inst) return `${inst.series} ${inst.model}`
          return (profile?.organModel && profile.organModel !== 'Not specified') ? profile.organModel : null
        })()
        const userModel = profileModel ?? selectedShowroomModel
        const shortModel = userModel ? userModel.replace(' Series', '') : null
        const seriesKey = userModel ? (userModel.match(/^(Infinity|Imagine|Inspire|The American)/)?.[1] ?? null) : null
        const seriesLogo = seriesKey ? SERIES_LOGOS[seriesKey] ?? null : null
        const modelLogo = shortModel ? MODEL_LOGOS[shortModel] ?? null : null
        return (<>

      {/* ══ HERO — responsive 40/60 split ══ */}
      <div style={{ background: '#0B0B0B', overflow: 'hidden' }}>
        <div className="flex flex-col md:flex-row" style={{ minHeight: 440 }}>
          {/* Copy column — takes natural width on mobile, 44% on desktop */}
          <div
            className="flex flex-col justify-center order-2 md:order-1"
            style={{
              flex: '0 0 44%',
              minWidth: 0,
              padding: 'clamp(32px, 6vw, 80px) clamp(24px, 4vw, 64px)',
              paddingLeft: 'max(24px, calc((100vw - 1280px)/2 + 48px))',
            }}
          >
            <p className="font-bold uppercase mb-4" style={{ fontSize: 11, color: '#E63412', letterSpacing: '0.5em' }}>Rodgers Connect</p>
            <h1
              className="font-bold text-white mb-5"
              style={{
                fontSize: 'clamp(2rem, 3.2vw, 3.4rem)',
                letterSpacing: '-0.02em',
                lineHeight: 1.05,
                textTransform: 'uppercase',
                whiteSpace: 'normal',
              }}
            >
              More music.<br />More possibilities.<br />For your Rodgers.
            </h1>
            <p className="mb-8" style={{ fontSize: 16, color: '#a0a0a0', lineHeight: 1.65, maxWidth: 400 }}>
              Discover music, registrations and performances selected for your Rodgers—and connect with organists who play what you play.
            </p>
            <div className="flex flex-col items-start gap-4">
              <button
                onClick={() => { if (profile) { setSelectedShowroomModel(null); setChangingModel(true) }; setTimeout(() => document.getElementById('model-selector')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50) }}
                className="btn"
                style={{ fontSize: 11, padding: '14px 36px', letterSpacing: '0.18em', minHeight: 48 }}
              >
                Find My Rodgers Organ
              </button>
              <button
                onClick={() => setDiscoverView('library')}
                className="font-bold uppercase transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{ fontSize: 11, color: '#a0a0a0', letterSpacing: '0.14em', textDecoration: 'underline', textUnderlineOffset: 4, outlineColor: '#E63412' }}
              >
                Explore Without Selecting a Model
              </button>
            </div>
          </div>
          {/* Photo column */}
          <div className="relative overflow-hidden order-1 md:order-2 md:flex-1" style={{ minHeight: 240 }}>
            <img
              src={heroOrganistPhoto}
              alt="Rodgers organ"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-y-0 left-0 w-24 hidden md:block" style={{ background: 'linear-gradient(to right, #0B0B0B, transparent)' }} />
            <div className="absolute inset-x-0 bottom-0 h-24 md:hidden" style={{ background: 'linear-gradient(to top, #0B0B0B, transparent)' }} />
          </div>
        </div>
      </div>

      {/* ══ BENEFIT PILLARS ══ */}
      <div style={{ background: '#fff', borderBottom: '1px solid #E8E4DE' }}>
        <div className="mx-auto px-6 md:px-12" style={{ maxWidth: 1280 }}>
          <p className="text-center pt-10 pb-1 font-bold uppercase" style={{ fontSize: 9, color: '#B0AAA0', letterSpacing: '0.4em' }}>Everything you need to play more</p>
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x mt-2" style={{ borderColor: '#E8E4DE' }}>
            {[
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E63412" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8" fill="#E63412" stroke="none"/></svg>
                ),
                kicker: 'Discover',
                body: 'Explore sheet music, recordings, MIDI files and registrations contributed by organists worldwide.',
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E63412" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="13" rx="2"/><path d="M8 21h8M12 17v4"/><path d="M7 9l2 2 4-4" stroke="#E63412" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                ),
                kicker: 'Play',
                body: 'Send MIDI files to your instrument via Rodgers Play. Download registrations matched to your model, then play it yourself.',
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E63412" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                ),
                kicker: 'Connect',
                body: 'Share performances and meet organists who play what you play. Ask questions, exchange ideas, grow together.',
              },
            ].map(({ icon, kicker, body }) => (
              <div key={kicker} className="flex items-start gap-4 px-6 py-8 md:px-10">
                <div className="shrink-0 mt-0.5">{icon}</div>
                <div>
                  <p className="font-bold uppercase mb-1.5" style={{ fontSize: 13, color: '#0B0B0B', letterSpacing: '0.14em' }}>{kicker}</p>
                  <p style={{ fontSize: 14, color: '#737373', lineHeight: 1.65 }}>{body}</p>
                  {kicker === 'Play' && (
                    <div className="flex items-center gap-1.5 mt-2">
                      <span style={{ fontSize: 11, color: '#B0AAA0' }}>Powered by</span>
                      <img src={rodgersPlayLogo} alt="Rodgers Play" style={{ height: 12, width: 'auto', objectFit: 'contain', opacity: 0.65 }} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══ MODEL SELECTION STEP ══ */}
      {profile && profileModel && !changingModel ? (
        /* Returning member: two clear, prominent CTAs -- share or discover. Gated on
           profileModel (the actually-saved model), not userModel -- userModel also
           includes selectedShowroomModel, which just previews a card you've clicked but
           haven't saved yet. Gating on userModel meant merely clicking a model card
           satisfied this condition and swapped away the "Which Rodgers Do You Play?"
           grid (and its Save Changes button) before saveModelChange ever ran, so the
           rest of the page looked like it had picked up your new model everywhere while
           the backend never actually got the update -- it'd revert the moment
           selectedShowroomModel reset (e.g. on reload). The "your
           organ" readout that used to fill this whole bar now lives compactly in the
           header next to the username instead (see headerUserModel above); "Change
           Model" is still reachable from the small pill in the Discover section below. */
        <div style={{ background: '#0B0B0B', borderBottom: '1px solid #1e1e1e' }}>
          <div className="mx-auto px-12 py-10" style={{ maxWidth: 1280 }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => setShowUpload(true)}
                className="btn justify-center"
                style={{ fontSize: 13, padding: '22px 32px', letterSpacing: '0.16em', minHeight: 68 }}
              >
                Share Your Performance
              </button>
              <button
                onClick={() => { setActiveModel(userModel); setDiscoverView('library') }}
                className="justify-center font-bold uppercase transition-opacity hover:opacity-80"
                style={{ fontSize: 13, padding: '22px 32px', letterSpacing: '0.16em', minHeight: 68, background: 'transparent', color: '#fff', border: '2px solid #fff', borderRadius: 4, display: 'flex', alignItems: 'center' }}
              >
                Discover Music for Your Rodgers Organ
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* New visitor: compact model selector */
        <div id="model-selector" style={{ background: '#F5F3EF', borderBottom: `1px solid #D8D4CC` }}>
          <div className="mx-auto px-12 py-14" style={{ maxWidth: 1280 }}>
            {/* Step header */}
            <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
              <div>
                <p className="font-bold uppercase mb-2" style={{ fontSize: 10, color: '#E63412', letterSpacing: '0.45em' }}>Personalize Your Experience</p>
                <h2 className="font-bold uppercase" style={{ fontSize: 'clamp(1.6rem, 2.4vw, 2.2rem)', letterSpacing: '-0.01em', color: '#0B0B0B', lineHeight: 1.0 }}>Which Rodgers Do You Play?</h2>
                <p className="mt-2" style={{ fontSize: 16, color: '#737373', maxWidth: 560 }}>Select your model to see compatible music, registrations and community activity tailored to your instrument.</p>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                {changingModel && (
                  <button onClick={() => { setChangingModel(false); setSelectedShowroomModel(null); setModelChangeError('') }} className="font-bold uppercase transition-opacity hover:opacity-70" style={{ fontSize: 11, color: '#737373', letterSpacing: '0.18em' }}>
                    Cancel
                  </button>
                )}
                <button onClick={() => setDiscoverView('library')} className="font-bold uppercase transition-opacity hover:opacity-70" style={{ fontSize: 11, color: '#737373', letterSpacing: '0.18em', borderBottom: '1px solid #B0AAA0', paddingBottom: 2 }}>
                  View All Models
                </button>
              </div>
            </div>
            {/* Search + family filters row */}
            {(() => {
              const familyList = ['Inspire', 'Imagine', 'Infinity']
              const filteredModels = SHOWROOM_MODELS.filter(m => {
                if (modelSearch) return m.id.toLowerCase().includes(modelSearch.toLowerCase())
                return m.series === familyFilter
              }).slice(0, 4)
              return (
                <>
                  <div className="flex items-center gap-3 mb-6 flex-wrap">
                    {/* Search */}
                    <div className="relative" style={{ minWidth: 220 }}>
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#B0AAA0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                      <input
                        value={modelSearch}
                        onChange={e => setModelSearch(e.target.value)}
                        placeholder="Search by model number…"
                        className="w-full pl-9 pr-3 py-2.5 text-sm"
                        style={{ border: '1px solid #D8D4CC', borderRadius: 3, outline: 'none', background: '#fff', color: '#0B0B0B' }}
                      />
                    </div>
                    {/* Family filters */}
                    <div className="flex gap-2 flex-wrap">
                      {familyList.map(f => (
                        <button
                          key={f}
                          onClick={() => { setFamilyFilter(f); setModelSearch('') }}
                          className="font-bold uppercase text-[10px] px-3 py-2.5 transition-all"
                          style={{
                            letterSpacing: '0.15em',
                            borderRadius: 3,
                            border: familyFilter === f && !modelSearch ? '1.5px solid #0B0B0B' : '1px solid #D8D4CC',
                            background: familyFilter === f && !modelSearch ? '#0B0B0B' : '#fff',
                            color: familyFilter === f && !modelSearch ? '#fff' : '#737373',
                          }}
                        >{f}</button>
                      ))}
                    </div>
                  </div>
                  {/* 4 model cards */}
                  {filteredModels.length > 0 ? (
                    <div className="grid grid-cols-4 gap-4">
                      {filteredModels.map(m => {
                        const isSelected = selectedShowroomModel === m.id
                        return (
                          <button
                            key={m.id}
                            onClick={() => setSelectedShowroomModel(isSelected ? null : m.id)}
                            className="text-left transition-all group"
                            style={{
                              border: isSelected ? '2.5px solid #E63412' : '1.5px solid #D8D4CC',
                              borderRadius: 3,
                              overflow: 'hidden',
                              background: '#fff',
                              position: 'relative',
                              boxShadow: isSelected ? '0 0 0 1px rgba(230,52,18,0.15)' : 'none',
                            }}
                          >
                            {isSelected && (
                              <div className="absolute top-2.5 right-2.5 z-10 flex items-center justify-center" style={{ width: 22, height: 22, background: '#E63412', borderRadius: '50%' }}>
                                <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              </div>
                            )}
                            {/* Image area */}
                            <div style={{ height: 150, background: '#0B0B0B', overflow: 'hidden' }}>
                              <img
                                src={m.image}
                                alt={m.id}
                                className="w-full h-full object-contain transition-all"
                                style={{
                                  padding: '6px 8px',
                                  filter: isSelected ? 'brightness(1.15)' : 'brightness(0.9)',
                                  transform: isSelected ? 'scale(1.03)' : 'scale(1)',
                                  transition: 'filter 0.2s, transform 0.2s',
                                }}
                              />
                            </div>
                            <div className="px-3 pt-2.5 pb-3" style={{ background: isSelected ? '#FFF5F3' : '#fff', borderTop: isSelected ? '2px solid #E63412' : '1px solid #F0EDE8' }}>
                              <p className="font-bold uppercase" style={{ fontSize: 8, color: isSelected ? '#E63412' : '#B0AAA0', letterSpacing: '0.3em', marginBottom: 1 }}>{m.series}</p>
                              <p className="font-bold uppercase" style={{ fontSize: 20, letterSpacing: '0.04em', color: '#0B0B0B', lineHeight: 1.05 }}>{m.number}</p>
                              <p style={{ fontSize: 10.5, color: '#B0AAA0', marginTop: 3 }}>{m.manuals} manuals · {m.series}</p>
                              {isSelected && <p className="font-bold uppercase mt-2" style={{ fontSize: 8, color: '#E63412', letterSpacing: '0.2em' }}>✓ This is my Rodgers</p>}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="py-8 text-center" style={{ border: '1px dashed #D8D4CC', borderRadius: 3 }}>
                      <p style={{ fontSize: 14, color: '#B0AAA0' }}>No models match "{modelSearch}"</p>
                    </div>
                  )}
                  {/* Confirmation bar — shown after selection */}
                  {selectedShowroomModel && (() => {
                    const selModel = SHOWROOM_MODELS.find(m => m.id === selectedShowroomModel)
                    const selShort = selectedShowroomModel.replace(/^(Infinity|Imagine|Inspire|The American) Series /, '')
                    return (
                      <div className="flex items-center justify-between gap-6 mt-6 px-6 py-5" style={{ background: '#0B0B0B', borderRadius: 3 }}>
                        <div className="flex items-center gap-4">
                          {selModel && (
                            <div className="w-12 h-12 flex items-center justify-center shrink-0" style={{ background: '#141414', border: '2px solid #E63412' }}>
                              <img src={selModel.image} alt={selShort} className="w-full h-full object-contain p-1" />
                            </div>
                          )}
                          <div>
                            <p className="font-bold uppercase" style={{ fontSize: 9, color: '#E63412', letterSpacing: '0.4em' }}>Your Rodgers: {selShort}</p>
                            <p style={{ fontSize: 13, color: '#737373', marginTop: 4, maxWidth: 400 }}>
                              {profile ? 'Save to update the music personalized for this instrument.' : 'Music and registrations are now personalized for this instrument.'}
                            </p>
                            {modelChangeError && <p style={{ fontSize: 12, color: '#ff6b52', marginTop: 4 }}>{modelChangeError}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {profile ? (
                            <>
                              <button
                                onClick={() => saveModelChange(selectedShowroomModel)}
                                disabled={savingModel}
                                className="btn"
                                style={{ fontSize: 11, padding: '12px 24px', letterSpacing: '0.2em', minHeight: 44, opacity: savingModel ? 0.6 : 1 }}
                              >
                                {savingModel ? 'Saving…' : 'Save Changes'}
                              </button>
                              <button onClick={() => { setChangingModel(false); setSelectedShowroomModel(null); setModelChangeError('') }} className="font-bold uppercase transition-opacity hover:opacity-70" style={{ fontSize: 10, color: '#aaa', letterSpacing: '0.16em' }}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <Experimental>
                                <button
                                  onClick={() => { setActiveTab('community'); window.scrollTo({ top: 0 }) }}
                                  className="btn"
                                  style={{ fontSize: 11, padding: '12px 24px', letterSpacing: '0.2em', minHeight: 44 }}
                                >
                                  Go to My Community
                                </button>
                              </Experimental>
                              <button onClick={() => setSelectedShowroomModel(null)} className="font-bold uppercase transition-opacity hover:opacity-70" style={{ fontSize: 10, color: '#aaa', letterSpacing: '0.16em' }}>
                                Change model
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )
                  })()}
                </>
              )
            })()}
          </div>
        </div>
      )}

      {/* ══ PERSONALIZED MUSIC + COMMUNITY ══ */}
      <div id="music-section" style={{ background: '#F5F3EF', borderBottom: `1px solid #D8D4CC` }}>
        <div className="mx-auto px-12 py-16" style={{ maxWidth: 1280 }}>
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <h2 className="font-bold uppercase" style={{ fontSize: 'clamp(1.6rem, 2.4vw, 2.2rem)', letterSpacing: '-0.01em', color: '#0B0B0B', lineHeight: 1.0 }}>
                Discover Music for Your Rodgers Organ
              </h2>
              {userModel && (
                <div className="flex items-center gap-2 shrink-0 px-3 py-1.5" style={{ background: '#F0EDE8', border: '1px solid #D8D4CC', borderRadius: 3 }}>
                  <span style={{ fontSize: 12, color: '#737373' }}>Your organ: <strong style={{ color: '#0B0B0B' }}>{shortModel}</strong></span>
                  <span style={{ color: '#D8D4CC' }}>·</span>
                  <button
                    onClick={() => { if (profile) { setSelectedShowroomModel(null); setChangingModel(true) }; setTimeout(() => document.getElementById('model-selector')?.scrollIntoView({ behavior: 'smooth' }), 50) }}
                    style={{ fontSize: 11, color: '#E63412', fontWeight: 600, letterSpacing: '0.08em' }}
                  >Change</button>
                </div>
              )}
            </div>
          </div>
          {(() => {
            const sortedFiles = [...files].sort((a, b) => b.id - a.id)
            const formatMap: Record<string, string[]> = { sheet: ['PDF'], audio: ['MP3', 'WAV'], midi: ['MIDI'] }
            // "Music for Your {model}" is a real filter, not just a label -- every result
            // shown here is genuinely recorded on the viewer's own organ, so there's no
            // need for (and it'd be misleading to show) a per-item model badge or caption.
            const modelFilteredFiles = userModel ? sortedFiles.filter(f => f.organModel === userModel) : sortedFiles
            const filteredHomeFiles = musicFilter === 'all' || musicFilter === 'latest'
              ? modelFilteredFiles
              : modelFilteredFiles.filter(f => (formatMap[musicFilter] ?? []).includes(f.format))
            const displayFiles = filteredHomeFiles.slice(0, 4)
            const simpleFilters = [
              { key: 'all', label: 'All' },
              { key: 'sheet', label: 'Sheet Music' },
              { key: 'audio', label: 'Audio' },
              { key: 'midi', label: 'MIDI' },
            ]
            return (
              <>
                {/* Tabs */}
                <div className="flex gap-0 mb-8" style={{ borderBottom: '2px solid #D8D4CC' }}>
                  {([
                    ['music', 'Music'],
                    ['registrations', 'Registrations'],
                    ['community', 'Community'],
                    ['resources', 'Resources'],
                  ] as ['music' | 'registrations' | 'community' | 'resources', string][])
                    .filter(([k]) => SHOW_EXPERIMENTAL || k === 'music')
                    .map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => setHomeContentTab(k)}
                      className="font-bold uppercase transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                      style={{
                        fontSize: 11, letterSpacing: '0.18em', padding: '10px 20px',
                        color: homeContentTab === k ? '#0B0B0B' : '#737373',
                        borderBottom: `2.5px solid ${homeContentTab === k ? '#E63412' : 'transparent'}`,
                        marginBottom: -2, background: 'transparent', outlineColor: '#E63412',
                      }}
                    >{label}</button>
                  ))}
                </div>
                {/* Responsive two-column */}
                <div className="flex flex-col lg:flex-row gap-6 items-start">
                  {/* Main content */}
                  <div className="w-full lg:flex-1 min-w-0">
                    {homeContentTab === 'music' && (
                      <>
                        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                          <h3 className="font-bold uppercase" style={{ fontSize: 'clamp(1.1rem, 1.6vw, 1.35rem)', letterSpacing: '-0.01em', color: '#0B0B0B' }}>
                            {userModel ? `Music for Your ${shortModel}` : 'Discover Music Made for Rodgers'}
                          </h3>
                          <button
                            onClick={() => { setActiveModel(userModel ?? 'All'); setDiscoverView('library') }}
                            className="font-bold uppercase transition-opacity hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                            style={{ fontSize: 10, color: '#0B0B0B', letterSpacing: '0.18em', borderBottom: '1px solid #0B0B0B', paddingBottom: 2, outlineColor: '#E63412' }}
                          >View All Music</button>
                        </div>
                        {/* Filter tabs */}
                        <div className="flex gap-0 mb-6" style={{ borderBottom: '1px solid #D8D4CC' }}>
                          {simpleFilters.map(({ key, label }) => {
                            const active = musicFilter === key || (key === 'all' && musicFilter === 'latest')
                            return (
                              <button key={key} onClick={() => setMusicFilter(key)} className="font-bold uppercase transition-colors focus-visible:outline focus-visible:outline-2" style={{ fontSize: 10, letterSpacing: '0.16em', padding: '9px 14px', color: active ? '#0B0B0B' : '#737373', borderBottom: `2px solid ${active ? '#E63412' : 'transparent'}`, marginBottom: -1, background: 'transparent', outlineColor: '#E63412' }}>{label}</button>
                            )
                          })}
                        </div>
                        {/* Music rows or empty state */}
                        {displayFiles.length === 0 ? (
                          <div className="py-10 text-center" style={{ border: '1px dashed #D8D4CC', borderRadius: 3 }}>
                            <p style={{ fontSize: 14, color: '#737373' }}>
                              {userModel ? `No performances recorded on your ${shortModel} yet.` : 'No files in this format yet.'}
                            </p>
                            <button onClick={() => { setActiveModel('All'); setDiscoverView('library') }} className="font-bold uppercase mt-3 inline-block" style={{ fontSize: 10, color: '#E63412', letterSpacing: '0.18em', borderBottom: '1px solid #E63412', paddingBottom: 1 }}>Browse All Performances</button>
                          </div>
                        ) : (
                          <div className="flex flex-col" style={{ borderTop: '1px solid #D8D4CC' }}>
                            {displayFiles.map((file) => {
                              const isPlaying = playingFileId === file.id
                              const canPlay = isPlayableAudio(file)
                              const isSaved = savedFiles.has(file.id)
                              return (
                                <div key={file.id} className="py-4 cursor-pointer" style={{ borderBottom: '1px solid #D8D4CC' }} onClick={() => setSelected(file)}>
                                  {/* Top row: play + info */}
                                  <div className="flex items-start gap-3 mb-2">
                                    <button
                                      onClick={e => { e.stopPropagation(); if (canPlay) handlePlay(file) }}
                                      disabled={!canPlay}
                                      className="shrink-0 flex items-center justify-center transition-all mt-0.5"
                                      style={{ width: 40, height: 40, borderRadius: '50%', background: isPlaying ? '#E63412' : '#0B0B0B', color: '#fff', opacity: canPlay ? 1 : 0.3, cursor: canPlay ? 'pointer' : 'not-allowed' }}
                                      onMouseEnter={e => { if (canPlay && !isPlaying) (e.currentTarget as HTMLElement).style.background = '#E63412' }}
                                      onMouseLeave={e => { if (!isPlaying) (e.currentTarget as HTMLElement).style.background = '#0B0B0B' }}
                                    >
                                      {isPlaying ? <span style={{ fontSize: 8, letterSpacing: 1.5 }}>■■</span> : <span style={{ fontSize: 12, marginLeft: 2 }}>▶</span>}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-bold mb-1" style={{ fontSize: 14, color: '#0B0B0B', lineHeight: 1.3 }}>{file.title}</p>
                                      <p style={{ fontSize: 11, color: '#B0AAA0', marginBottom: 3 }}>
                                        Performed by {file.uploader}
                                        {file.level && <> · {file.level}</>}
                                      </p>
                                      <p style={{ fontSize: 12, color: '#555', marginBottom: 3 }}>{file.composer}</p>
                                      {file.description && (
                                        <p className="line-clamp-2" style={{ fontSize: 12, color: '#8a8a8a', marginBottom: 6, lineHeight: 1.4 }}>{file.description}</p>
                                      )}
                                      {/* Available formats row -- purely informational: stops the
                                          click here so tapping a badge doesn't bubble up and open
                                          the detail popup the way clicking the rest of the row does. */}
                                      <div className="flex items-center gap-1.5 flex-wrap mb-1" onClick={e => e.stopPropagation()}>
                                        {canPlay && <span className="font-bold uppercase" style={{ fontSize: 8, color: '#16A34A', background: 'rgba(22,163,74,0.07)', border: '1px solid rgba(22,163,74,0.25)', borderRadius: 2, padding: '2px 5px', letterSpacing: '0.12em' }}>{file.format}</span>}
                                        {file.hasPdf && <span className="font-bold uppercase" style={{ fontSize: 8, color: '#2563EB', background: 'rgba(37,99,235,0.07)', border: '1px solid rgba(37,99,235,0.25)', borderRadius: 2, padding: '2px 5px', letterSpacing: '0.12em' }}>Sheet Music</span>}
                                        {file.midi && (
                                          <span className="inline-flex items-center gap-1.5" style={{ background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.22)', borderRadius: 2, padding: '2px 6px' }}>
                                            <img src={rodgersPlayLogo} alt="Rodgers Play" style={{ height: 10, width: 'auto', objectFit: 'contain' }} />
                                          </span>
                                        )}
                                        {file.videoUrl && <span className="font-bold uppercase" style={{ fontSize: 8, color: '#7C3AED', background: 'rgba(124,58,237,0.07)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: 2, padding: '2px 5px', letterSpacing: '0.12em' }}>Video</span>}
                                      </div>
                                      {file.tags && file.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                          {file.tags.map(t => (
                                            <span key={t} className="font-semibold" style={{ fontSize: 9, color: '#737373', background: '#F5F3EF', border: '1px solid #E8E4DE', borderRadius: 2, padding: '1px 5px' }}>{t}</span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {/* Action row */}
                                  <div className="flex items-center gap-2 pl-[52px] flex-wrap">
                                    <button
                                      onClick={e => { e.stopPropagation(); if (canPlay) handlePlay(file) }}
                                      disabled={!canPlay}
                                      className="flex items-center gap-1.5 px-3 py-1.5 font-bold uppercase transition-all hover:opacity-80"
                                      style={{ fontSize: 10, background: canPlay ? '#E63412' : '#e8e8e8', color: canPlay ? '#fff' : '#B0AAA0', borderRadius: 3, letterSpacing: '0.12em', cursor: canPlay ? 'pointer' : 'not-allowed' }}
                                    >
                                      <span style={{ fontSize: 8 }}>▶</span> {isPlaying ? 'Stop' : 'Listen'}
                                    </button>
                                    {file.midi && (
                                      <Experimental>
                                        <button
                                          onClick={e => { e.stopPropagation() }}
                                          className="flex items-center gap-2 px-3 py-1.5 font-bold uppercase transition-all hover:opacity-80"
                                          style={{ fontSize: 10, color: '#7C3AED', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 3, letterSpacing: '0.1em', background: 'rgba(124,58,237,0.05)' }}
                                          title="Open this MIDI file in Rodgers Play"
                                        >
                                          <img src={rodgersPlayLogo} alt="" style={{ height: 11, width: 'auto', objectFit: 'contain' }} />
                                          Send to Rodgers Play
                                        </button>
                                      </Experimental>
                                    )}
                                    {/* Save for later — pure local state, no backend behind it */}
                                    <Experimental>
                                      <button
                                        onClick={e => { e.stopPropagation(); setSavedFiles(prev => { const n = new Set(prev); isSaved ? n.delete(file.id) : n.add(file.id); return n }) }}
                                        className="flex items-center gap-1 px-2.5 py-1.5 font-bold uppercase transition-all hover:opacity-80"
                                        style={{ fontSize: 10, color: isSaved ? '#E63412' : '#B0AAA0', border: `1px solid ${isSaved ? 'rgba(230,52,18,0.3)' : '#e8e8e8'}`, borderRadius: 3, letterSpacing: '0.1em', background: isSaved ? 'rgba(230,52,18,0.05)' : '#fff' }}
                                        title={isSaved ? 'Remove from saved' : 'Save for later'}
                                      >
                                        {isSaved ? '♥ Saved' : '♡ Save'}
                                      </button>
                                    </Experimental>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </>
                    )}
                    {homeContentTab === 'registrations' && (
                      <Experimental>
                        <div className="py-10 text-center" style={{ border: '1px dashed #D8D4CC', borderRadius: 3 }}>
                          <p className="font-bold uppercase mb-2" style={{ fontSize: 10, color: '#E63412', letterSpacing: '0.3em' }}>Registrations</p>
                          <p style={{ fontSize: 14, color: '#737373' }}>{userModel ? `${shortModel} registrations` : 'All registrations'} — coming soon.</p>
                          <button onClick={() => setDiscoverView('library')} className="font-bold uppercase mt-4 inline-block" style={{ fontSize: 10, color: '#0B0B0B', letterSpacing: '0.18em', borderBottom: '1px solid #0B0B0B' }}>Browse Library</button>
                        </div>
                      </Experimental>
                    )}
                    {homeContentTab === 'community' && (
                      <Experimental>
                        <div>
                          <div className="flex items-center justify-between mb-5">
                            <h3 className="font-bold uppercase" style={{ fontSize: 'clamp(1.1rem, 1.6vw, 1.35rem)', letterSpacing: '-0.01em', color: '#0B0B0B' }}>
                              {userModel ? `Activity from ${shortModel} Organists` : 'Community Activity'}
                            </h3>
                            <div className="flex items-center gap-3">
                              <span style={{ fontSize: 9, color: '#B0AAA0', letterSpacing: '0.08em' }}>Prototype — sample content</span>
                              <button onClick={() => setActiveTab('community')} className="font-bold uppercase transition-opacity hover:opacity-70" style={{ fontSize: 10, color: '#0B0B0B', letterSpacing: '0.18em', borderBottom: '1px solid #0B0B0B', paddingBottom: 2 }}>
                                Visit Community
                              </button>
                            </div>
                          </div>
                          <div style={{ border: '1px solid #D8D4CC', borderRadius: 3, overflow: 'hidden' }}>
                            {[
                              { avatar: MEMBERS['Tim Eastbrower']?.avatar, name: 'Sarah Mitchell', action: 'shared a performance', badge: shortModel ?? 'Infinity 367', time: '1d ago' },
                              { avatar: MEMBERS['Dan Miller']?.avatar, name: 'Daniel Kowalski', action: 'uploaded a registration', badge: shortModel ?? 'Inspire 352', time: '3d ago' },
                              { avatar: MEMBERS['Jan Kraybill']?.avatar, name: 'Michael Torres', action: 'asked a model question', badge: shortModel ?? 'Imagine 239', time: '5d ago' },
                            ].map((item, i) => (
                              <div key={i} className="flex items-center gap-4 px-5 py-4" style={{ borderBottom: i < 2 ? '1px solid #D8D4CC' : 'none' }}>
                                {item.avatar ? <img src={item.avatar} alt={item.name} className="w-9 h-9 rounded-full object-cover shrink-0" style={{ border: '1.5px solid #D8D4CC' }} /> : <div className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center font-bold text-white" style={{ background: '#E63412', fontSize: 12 }}>{item.name.charAt(0)}</div>}
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold truncate" style={{ fontSize: 13, color: '#0B0B0B' }}>{item.name} <span className="font-normal" style={{ color: '#737373' }}>{item.action}</span></p>
                                  <span className="font-bold uppercase" style={{ fontSize: 9, color: '#737373', letterSpacing: '0.2em', border: '1px solid #D8D4CC', padding: '2px 6px' }}>{item.badge}</span>
                                </div>
                                <span style={{ fontSize: 11, color: '#B0AAA0', flexShrink: 0 }}>{item.time}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Experimental>
                    )}
                    {homeContentTab === 'resources' && (
                      <Experimental>
                        <div className="py-10 text-center" style={{ border: '1px dashed #D8D4CC', borderRadius: 3 }}>
                          <p style={{ fontSize: 14, color: '#737373' }}>Guides, tutorials and support for your Rodgers.</p>
                          <button className="font-bold uppercase mt-4 inline-block" style={{ fontSize: 10, color: '#0B0B0B', letterSpacing: '0.18em', borderBottom: '1px solid #0B0B0B' }}>Browse Resources</button>
                        </div>
                      </Experimental>
                    )}
                  </div>

                  {/* Sidebar */}
                  <div className="flex flex-col gap-4 w-full lg:w-80 lg:shrink-0">
                    {/* Community card */}
                    <Experimental>
                    <div style={{ background: '#fff', border: '1px solid #D8D4CC', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: 3, background: '#E63412' }} />
                      <div className="px-5 pt-5 pb-4">
                        <p className="font-bold uppercase mb-1" style={{ fontSize: 9, color: '#E63412', letterSpacing: '0.35em' }}>Your Community</p>
                        <p className="font-bold uppercase mb-2" style={{ fontSize: 15, letterSpacing: '0.04em', color: '#0B0B0B' }}>{userModel ? `${shortModel} Organists` : 'Rodgers Community'}</p>
                        <p style={{ fontSize: 13, color: '#555', lineHeight: 1.55, marginBottom: 14 }}>
                          {userModel
                            ? `Meet ${shortModel} organists, hear recent performances and exchange registrations and ideas.`
                            : 'Meet organists who play Rodgers, hear performances and exchange ideas.'}
                        </p>
                        {/* Member avatars */}
                        <div className="flex items-center gap-1 mb-4">
                          {[MEMBERS['Michael Bauer'], MEMBERS['Catherine Reinhardt'], MEMBERS['Sophie Lefebvre']].filter(Boolean).map((m, i) => (
                            <button key={m.name} onClick={() => setViewingMember(m.name)} className="transition-opacity hover:opacity-80" style={{ marginLeft: i > 0 ? -6 : 0, zIndex: 3 - i, position: 'relative' }}>
                              <img src={m.avatar} alt={m.name} title={m.name} className="w-9 h-9 rounded-full object-cover" style={{ border: '2px solid #fff' }} />
                            </button>
                          ))}
                          <span style={{ fontSize: 12, color: '#737373', marginLeft: 8 }}>+45 organists</span>
                        </div>
                        <button onClick={() => setActiveTab('community')} className="btn w-full justify-center" style={{ fontSize: 10, padding: '10px 0', letterSpacing: '0.16em' }}>Explore Your Community</button>
                      </div>
                      {/* Featured performance */}
                      <div style={{ borderTop: '1px solid #F0EDE8' }}>
                        <p className="px-5 pt-3 pb-0 font-bold uppercase" style={{ fontSize: 8, color: '#B0AAA0', letterSpacing: '0.3em' }}>Featured Performance</p>
                        <button className="w-full text-left px-5 py-3 group hover:bg-gray-50 transition-colors" onClick={() => setActiveTab('community')}>
                          <div className="flex items-center gap-3 mb-2">
                            <div className="relative shrink-0">
                              <img src={MEMBERS['Catherine Reinhardt']?.avatar} alt="Catherine Reinhardt" className="w-9 h-9 rounded-full object-cover" style={{ border: '1.5px solid #F0EDE8' }} />
                              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: '#E63412' }}>
                                <span className="text-white" style={{ fontSize: 6, marginLeft: 1 }}>▶</span>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold truncate" style={{ fontSize: 12, color: '#0B0B0B' }}>Widor's Toccata, Op. 42</p>
                              <p style={{ fontSize: 10, color: '#737373' }}>Catherine Reinhardt · Infinity 367</p>
                            </div>
                          </div>
                        </button>
                      </div>
                      {/* Recent discussion */}
                      <div style={{ borderTop: '1px solid #F0EDE8' }}>
                        <p className="px-5 pt-3 pb-0 font-bold uppercase" style={{ fontSize: 8, color: '#B0AAA0', letterSpacing: '0.3em' }}>Recent Discussion</p>
                        <button className="w-full text-left px-5 py-3 group hover:bg-gray-50 transition-colors" onClick={() => setActiveTab('community')}>
                          <div className="flex items-start gap-2">
                            <img src={MEMBERS['Michael Bauer']?.avatar} alt="Michael Bauer" className="w-7 h-7 rounded-full object-cover shrink-0 mt-0.5" style={{ border: '1.5px solid #F0EDE8' }} />
                            <div>
                              <p style={{ fontSize: 12, color: '#0B0B0B', lineHeight: 1.4 }}><span className="font-semibold">Michael B.</span> asked: best flute stops for Bach?</p>
                              <p style={{ fontSize: 10, color: '#B0AAA0', marginTop: 1 }}>3 replies · 5d ago</p>
                            </div>
                          </div>
                        </button>
                      </div>
                      {/* Shared registration */}
                      <div style={{ borderTop: '1px solid #F0EDE8' }}>
                        <p className="px-5 pt-3 pb-0 font-bold uppercase" style={{ fontSize: 8, color: '#B0AAA0', letterSpacing: '0.3em' }}>Shared Registration</p>
                        <button className="w-full text-left px-5 py-3 group hover:bg-gray-50 transition-colors" onClick={() => setActiveTab('community')}>
                          <div className="flex items-start gap-2">
                            <img src={MEMBERS['Dan Miller']?.avatar} alt="Dan Miller" className="w-7 h-7 rounded-full object-cover shrink-0 mt-0.5" style={{ border: '1.5px solid #F0EDE8' }} />
                            <div>
                              <p style={{ fontSize: 12, color: '#0B0B0B', lineHeight: 1.4 }}><span className="font-semibold">Dan M.</span> uploaded a registration for the Imagine 239</p>
                              <p style={{ fontSize: 10, color: '#B0AAA0', marginTop: 1 }}>Suitable for hymn accompaniment · 3d ago</p>
                            </div>
                          </div>
                        </button>
                      </div>
                      <div className="px-5 py-2.5" style={{ borderTop: '1px solid #F0EDE8' }}>
                        <p style={{ fontSize: 10, color: '#B0AAA0', letterSpacing: '0.06em' }}>Prototype — sample content</p>
                      </div>
                    </div>
                    </Experimental>
                    {/* Upload CTA */}
                    <div style={{ background: '#fff', border: '1px solid #D8D4CC', borderRadius: 3, padding: 20 }}>
                      <p className="font-bold uppercase mb-1" style={{ fontSize: 9, color: '#737373', letterSpacing: '0.25em' }}>Share with the Community</p>
                      <p className="font-bold uppercase mb-2" style={{ fontSize: 14, color: '#0B0B0B', letterSpacing: '0.04em' }}>Share a Performance</p>
                      <p style={{ fontSize: 13, color: '#555', lineHeight: 1.55, marginBottom: 16 }}>Upload a recording or registration for your {shortModel ?? 'Rodgers'}.</p>
                      <button onClick={() => requireProfile('share', () => setShowUpload(true))} className="font-bold uppercase w-full text-center py-2.5" style={{ fontSize: 10, letterSpacing: '0.16em', background: '#0B0B0B', color: '#fff', borderRadius: 2 }}>
                        Upload a Performance
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )
          })()}
        </div>
      </div>


      {/* ══ MONTHLY OFFERING ══ */}
      <Experimental>
      <div style={{ background: '#EFEFEF', borderTop: '1px solid #D8D4CC', borderBottom: '1px solid #D8D4CC' }}>
        <div className="mx-auto px-12" style={{ maxWidth: 1280 }}>
          <div className="flex flex-col md:flex-row gap-0" style={{ minHeight: 340 }}>
            {/* Image: 40% */}
            <div className="relative overflow-hidden shrink-0 md:w-[40%]" style={{ minHeight: 200 }}>
              <img src={organConsolePhoto} alt="Rodgers organ console" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.88 }} />
              <button onClick={() => { const f = files.find(isPlayableAudio); if (f) handlePlay(f) }} className="absolute inset-0 flex items-center justify-center group">
                <div className="w-14 h-14 rounded-full flex items-center justify-center transition-all group-hover:scale-105 shadow-lg" style={{ background: '#E63412' }}>
                  <span className="text-white" style={{ fontSize: 16, marginLeft: 3 }}>▶</span>
                </div>
              </button>
            </div>
            {/* Content: 60% */}
            <div className="flex flex-col justify-center px-10 py-10 md:px-14 md:py-12" style={{ flex: 1 }}>
              {/* Rodgers Play logo + section label row */}
              <div className="flex items-center gap-4 mb-6">
                <div className="flex flex-col items-start">
                  <img src={rodgersPlayLogo} alt="Rodgers Play" style={{ height: 36, width: 'auto', objectFit: 'contain' }} />
                  <p style={{ fontSize: 8, color: '#9a9a9a', letterSpacing: '0.18em', textTransform: 'uppercase', marginTop: 3 }}>Playback Technology</p>
                </div>
                <div style={{ width: 1, height: 36, background: '#D0CDC8' }} />
                <div>
                  <p className="font-bold uppercase" style={{ fontSize: 9, color: '#E63412', letterSpacing: '0.45em', marginBottom: 2 }}>Monthly Musical Offering</p>
                  <p className="font-bold uppercase" style={{ fontSize: 9, color: '#9a9a9a', letterSpacing: '0.3em' }}>September 2026</p>
                </div>
                <div className="flex-1 hidden md:block" />
                <button onClick={() => setActiveTab('monthly')} className="hidden md:block font-bold uppercase transition-opacity hover:opacity-60" style={{ fontSize: 9, color: '#9a9a9a', letterSpacing: '0.2em' }}>View All →</button>
              </div>

              <h2 className="font-bold uppercase mb-3" style={{ fontSize: 'clamp(1.3rem, 2vw, 1.75rem)', letterSpacing: '-0.01em', lineHeight: 1.1, color: '#0B0B0B' }}>
                Pachelbel's Toccata in E Minor
              </h2>
              <div className="flex flex-col gap-1 mb-5" style={{ borderLeft: '2px solid #D0CDC8', paddingLeft: 14 }}>
                <p style={{ fontSize: 13, color: '#555' }}>Johann Pachelbel</p>
                <p style={{ fontSize: 13, color: '#555' }}>Performed by Tim Eastbrower · Infinity 367</p>
              </div>
              {/* Available formats */}
              <div className="flex items-center gap-2 flex-wrap mb-6">
                {[
                  { label: 'MP3', color: '#16A34A', bg: 'rgba(22,163,74,0.07)', border: 'rgba(22,163,74,0.25)' },
                  { label: 'Sheet Music', color: '#2563EB', bg: 'rgba(37,99,235,0.07)', border: 'rgba(37,99,235,0.25)' },
                  { label: 'rodgersplay', color: '#7C3AED', bg: 'rgba(124,58,237,0.07)', border: 'rgba(124,58,237,0.25)' },
                  { label: 'Registration', color: '#B45309', bg: 'rgba(180,83,9,0.07)', border: 'rgba(180,83,9,0.25)' },
                ].map(f => (
                  f.label === 'rodgersplay'
                    ? <span key="rplay" className="inline-flex items-center" style={{ background: f.bg, border: `1px solid ${f.border}`, borderRadius: 2, padding: '3px 7px' }}>
                        <img src={rodgersPlayLogo} alt="Rodgers Play" style={{ height: 11, width: 'auto', objectFit: 'contain' }} />
                      </span>
                    : <span key={f.label} className="font-bold uppercase" style={{ fontSize: 8, color: f.color, background: f.bg, border: `1px solid ${f.border}`, borderRadius: 2, padding: '2px 6px', letterSpacing: '0.1em' }}>{f.label}</span>
                ))}
              </div>
              <div className="flex items-center gap-4">
                <button onClick={() => { const f = files.find(isPlayableAudio); if (f) handlePlay(f) }} className="btn" style={{ fontSize: 11, padding: '13px 24px', letterSpacing: '0.2em', minHeight: 44 }}>
                  ▶ Listen
                </button>
                <button onClick={() => setActiveTab('monthly')} className="font-bold uppercase transition-opacity hover:opacity-70" style={{ fontSize: 11, letterSpacing: '0.18em', color: '#555', border: '1px solid #C8C4BF', padding: '12px 22px', minHeight: 44, background: 'transparent' }}>
                  View Offering
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </Experimental>

      {/* ══ RECENT COMMUNITY PERFORMANCES ══ */}
      <Experimental>
      <div style={{ background: '#fff', borderBottom: '1px solid #E8E4DE' }}>
        <div className="mx-auto px-6 md:px-12 py-14" style={{ maxWidth: 1280 }}>
          <div className="flex items-end justify-between mb-8 gap-4">
            <div>
              <p className="font-bold uppercase mb-1.5" style={{ fontSize: 9, color: '#E63412', letterSpacing: '0.45em' }}>Community</p>
              <h2 className="font-bold uppercase" style={{ fontSize: 'clamp(1.3rem, 2vw, 1.75rem)', letterSpacing: '-0.01em', color: '#0B0B0B', lineHeight: 1.0 }}>Recent Performances</h2>
            </div>
            <button onClick={() => { setActiveTab('community'); window.scrollTo({ top: 0 }) }} className="font-bold uppercase shrink-0 transition-opacity hover:opacity-70" style={{ fontSize: 10, color: '#0B0B0B', letterSpacing: '0.18em', borderBottom: '1px solid #0B0B0B', paddingBottom: 2 }}>Visit Community</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { name: 'Catherine Reinhardt', avatar: MEMBERS['Catherine Reinhardt']?.avatar, model: 'Infinity 367', piece: "Widor's Toccata, Op. 42", time: '2 days ago', description: 'Full-throttle performance using the Infinity 367 grand chorus — sample content' },
              { name: 'Dan Miller', avatar: MEMBERS['Dan Miller']?.avatar, model: 'Imagine 359', piece: 'Voluntary in D, Op. 5 No. 1', time: '4 days ago', description: 'Crisp articulation across all principals — the 359\'s tracker touch shows here — sample content' },
              { name: 'Anna Vogel', avatar: MEMBERS['Anna Vogel']?.avatar, model: 'Inspire 352', piece: 'Lift Every Voice and Sing', time: '6 days ago', description: 'Lovely arrangement for the Inspire series string chorus and principal 8\' — sample content' },
            ].map((p, i) => (
              <button key={i} onClick={() => setActiveTab('community')} className="text-left group transition-all hover:shadow-md" style={{ background: '#F8F7F5', border: '1px solid #E8E4DE', borderRadius: 4, overflow: 'hidden' }}>
                {/* Waveform / placeholder */}
                <div className="relative flex items-center justify-center" style={{ height: 88, background: '#0B0B0B', overflow: 'hidden' }}>
                  {/* Simulated waveform bars */}
                  <div className="flex items-center gap-0.5 opacity-30">
                    {Array.from({ length: 40 }, (_, j) => (
                      <div key={j} style={{ width: 2, height: `${12 + Math.abs(Math.sin(j * 0.7 + i)) * 38}px`, background: '#E63412', borderRadius: 1, opacity: 0.7 + Math.sin(j) * 0.3 }} />
                    ))}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center transition-transform group-hover:scale-105" style={{ background: '#E63412' }}>
                      <span className="text-white" style={{ fontSize: 13, marginLeft: 2 }}>▶</span>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <p className="font-bold mb-0.5" style={{ fontSize: 13, color: '#0B0B0B', lineHeight: 1.3 }}>{p.piece}</p>
                  <p style={{ fontSize: 11, color: '#737373', marginBottom: 8 }}>{p.description}</p>
                  <div className="flex items-center gap-2">
                    {p.avatar && <img src={p.avatar} alt={p.name} className="w-6 h-6 rounded-full object-cover shrink-0" style={{ border: '1.5px solid #E8E4DE' }} />}
                    <span style={{ fontSize: 11, color: '#0B0B0B', fontWeight: 600 }}>{p.name}</span>
                    <span style={{ fontSize: 10, color: '#B0AAA0' }}>·</span>
                    <span className="font-bold uppercase" style={{ fontSize: 8, color: '#737373', letterSpacing: '0.12em', background: '#EBEBEB', padding: '2px 5px', borderRadius: 2 }}>{p.model}</span>
                    <span className="ml-auto" style={{ fontSize: 10, color: '#B0AAA0', whiteSpace: 'nowrap' }}>{p.time}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <p className="mt-5 text-center" style={{ fontSize: 11, color: '#B0AAA0' }}>Prototype — sample content</p>
        </div>
      </div>
      </Experimental>

      {/* ══ EXPLORE BY CATEGORY ══ */}
      <div style={{ background: '#F5F3EF', borderBottom: '1px solid #E8E4DE' }}>
        <div className="mx-auto px-6 md:px-12 py-14" style={{ maxWidth: 1280 }}>
          <div className="mb-8">
            <p className="font-bold uppercase mb-1.5" style={{ fontSize: 9, color: '#E63412', letterSpacing: '0.45em' }}>Browse</p>
            <h2 className="font-bold uppercase" style={{ fontSize: 'clamp(1.3rem, 2vw, 1.75rem)', letterSpacing: '-0.01em', color: '#0B0B0B', lineHeight: 1.0 }}>Explore by Category</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {(CATEGORIES.filter(c => c !== 'All') as Exclude<Category, 'All'>[]).map(cat => {
              const icon = CATEGORY_ICONS[cat]
              const count = files.filter(f => f.category === cat).length
              return (
                <button key={cat} onClick={() => { setActiveCategory(cat); setActiveModel('All'); setDiscoverView('library') }} className="flex flex-col items-center text-center gap-3 py-6 px-3 transition-all group hover:shadow-sm" style={{ background: '#fff', border: '1px solid #E8E4DE', borderRadius: 4 }}>
                  <div className="flex items-center justify-center w-10 h-10 rounded-full transition-colors" style={{ background: '#FFF1EE', color: '#E63412' }}>
                    {icon}
                  </div>
                  <div>
                    <p className="font-bold uppercase" style={{ fontSize: 11, color: '#0B0B0B', letterSpacing: '0.12em', lineHeight: 1.2 }}>{cat}</p>
                    <p style={{ fontSize: 10, color: '#737373', marginTop: 3 }}>{count} {count === 1 ? 'piece' : 'pieces'}</p>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* ══ JOIN CTA ══ */}
      {!profile && (
        <div style={{ background: '#0B0B0B' }}>
          <div className="mx-auto px-6 md:px-12 py-16 text-center" style={{ maxWidth: 700 }}>
            <p className="font-bold uppercase mb-3" style={{ fontSize: 9, color: '#E63412', letterSpacing: '0.45em' }}>Free to join</p>
            <h2 className="font-bold uppercase text-white mb-4" style={{ fontSize: 'clamp(1.4rem, 2.4vw, 2rem)', letterSpacing: '-0.01em', lineHeight: 1.05 }}>Your Digital Home as a Rodgers Organist</h2>
            <p className="mb-8 mx-auto" style={{ fontSize: 16, color: '#888', lineHeight: 1.7, maxWidth: 480 }}>Create a free account to personalize music for your instrument, save pieces, share performances and connect with organists who play what you play.</p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <button onClick={() => { setAuthIntent('signup'); setEditingProfile(true) }} className="btn" style={{ fontSize: 12, padding: '15px 40px', letterSpacing: '0.16em', minHeight: 52 }}>Create Free Account</button>
              <button onClick={() => setDiscoverView('library')} className="font-bold uppercase transition-opacity hover:opacity-70" style={{ fontSize: 11, color: '#555', letterSpacing: '0.16em', textDecoration: 'underline', textUnderlineOffset: 4 }}>Explore Without an Account</button>
            </div>
          </div>
        </div>
      )}

        </>)
      })()}

      {discoverView === 'library' && <>
      {/* Library header */}
      <div style={{ background: '#1a1a1a', borderBottom: '1px solid #222' }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-end">
          <p className="text-xs font-bold uppercase text-white" style={{ letterSpacing: '0.25em' }}>All Music</p>
        </div>
      </div>

      {/* Filters bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e0e0e0' }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col gap-3">

          {/* Row 1: Sort by Song Type */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[10px] uppercase font-bold shrink-0" style={{ color: '#a4a4a4', letterSpacing: '0.25em', minWidth: 100 }}>Song Type</span>
            <div className="flex items-center gap-1 flex-wrap">
              {CATEGORIES.map(cat => (
                <button key={cat} onClick={() => setActiveCategory(cat)} className="px-4 py-1.5 text-xs font-semibold uppercase transition-all" style={{ letterSpacing: '0.15em', borderRadius: 25, background: activeCategory === cat ? '#e63412' : 'transparent', color: activeCategory === cat ? '#fff' : '#737373', border: `1px solid ${activeCategory === cat ? '#e63412' : '#e0e0e0'}` }}>
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Row 2: Rodgers Model filter */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[10px] uppercase font-bold shrink-0" style={{ color: '#a4a4a4', letterSpacing: '0.25em', minWidth: 100 }}>Model</span>
            <div className="flex items-center gap-1 flex-wrap">
              {/* All Models */}
              <button
                onClick={() => setActiveModel('All')}
                className="px-4 py-1.5 text-xs font-semibold uppercase transition-all"
                style={{ letterSpacing: '0.15em', borderRadius: 25, background: activeModel === 'All' ? '#e63412' : 'transparent', color: activeModel === 'All' ? '#fff' : '#737373', border: `1px solid ${activeModel === 'All' ? '#e63412' : '#e0e0e0'}` }}
              >
                All Models
              </button>

              {/* My Instruments — only if logged in and has instruments (which requires the
                  gated My Instruments feature, so this chip is dev-only in practice too) */}
              {profile && (profile.instruments ?? []).length > 0 && (
                <Experimental>
                  <button
                    onClick={() => setActiveModel('my-instruments')}
                    className="px-4 py-1.5 text-xs font-semibold uppercase transition-all"
                    style={{ letterSpacing: '0.15em', borderRadius: 25, background: activeModel === 'my-instruments' ? '#1a1a1a' : 'transparent', color: activeModel === 'my-instruments' ? '#fff' : '#737373', border: `1px solid ${activeModel === 'my-instruments' ? '#1a1a1a' : '#e0e0e0'}` }}
                  >
                    My Instruments
                  </button>
                </Experimental>
              )}

              {/* Individual models that have files */}
              {RODGERS_MODELS.filter(m => m !== 'Not specified' && m !== 'Other / Vintage Rodgers' && m !== 'Rodgers Hybrid Organs' && files.some(f => f.organModel === m)).map(m => {
                const shortLabel = m.replace('Infinity Series', 'Infinity').replace('Imagine Series', 'Imagine').replace('Inspire Series', 'Inspire').replace('The American Series', 'American')
                const count = files.filter(f => f.organModel === m).length
                return (
                  <button
                    key={m}
                    onClick={() => setActiveModel(m)}
                    className="px-4 py-1.5 text-xs font-semibold uppercase transition-all"
                    style={{ letterSpacing: '0.15em', borderRadius: 25, background: activeModel === m ? '#e63412' : 'transparent', color: activeModel === m ? '#fff' : '#737373', border: `1px solid ${activeModel === m ? '#e63412' : '#e0e0e0'}` }}
                  >
                    {shortLabel} ({count})
                  </button>
                )
              })}
            </div>
          </div>

          {/* Row 3: Sort order */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[10px] uppercase font-bold shrink-0" style={{ color: '#a4a4a4', letterSpacing: '0.25em', minWidth: 100 }}>Sort By</span>
            <div className="flex items-center gap-1">
              {([['date', 'Most Recent'], ['downloads', 'Most Popular']] as const).map(([s, label]) => (
                <button key={s} onClick={() => setSortBy(s)} className="px-4 py-1.5 text-xs font-semibold uppercase transition-all" style={{ letterSpacing: '0.15em', borderRadius: 25, background: sortBy === s ? '#1a1a1a' : 'transparent', color: sortBy === s ? '#fff' : '#737373', border: `1px solid ${sortBy === s ? '#1a1a1a' : '#e0e0e0'}` }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* People matching the search query -- a name search should find the person, not
          just recordings whose own text happens to match. */}
      {query.trim() && peopleResults.length > 0 && (
        <div style={{ background: '#fff', borderBottom: '1px solid #e0e0e0' }}>
          <div className="max-w-6xl mx-auto px-6 py-5">
            <p className="text-[10px] uppercase font-bold mb-3" style={{ color: '#a4a4a4', letterSpacing: '0.25em' }}>
              People
            </p>
            <div className="flex flex-wrap gap-3">
              {peopleResults.map(person => (
                <button
                  key={person.id}
                  onClick={() => goToProfileId(person.id)}
                  className="flex items-center gap-3 px-4 py-2 transition-all hover:opacity-80"
                  style={{ border: '1px solid #e0e0e0', borderRadius: 25, background: '#fff' }}
                >
                  {person.avatar
                    ? <img src={person.avatar} alt={person.display_name} className="w-8 h-8 rounded-full object-cover" />
                    : <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-xs" style={{ background: '#E63412' }}>{person.display_name.charAt(0)}</div>
                  }
                  <div className="text-left">
                    <p className="text-sm font-semibold" style={{ color: '#0B0B0B' }}>{person.display_name}</p>
                    {(person.organ_model || person.location) && (
                      <p style={{ fontSize: 11, color: '#8a8a8a' }}>{[person.organ_model, person.location].filter(Boolean).join(' · ')}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Grid */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {filtered.length === 0 ? (
          <div className="py-24 text-center">
            <TripleDivider />
            <p className="font-serif text-xl italic" style={{ color: '#8a8a8a' }}>
              {query ? `No results for "${query}" — try clearing filters or browsing all music.` : 'No recordings found for this model.'}
            </p>
            {query && (
              <button onClick={() => { setQuery(''); setActiveModel('All') }} className="mt-4 text-xs font-semibold uppercase" style={{ color: '#E63412', letterSpacing: '0.14em' }}>Clear filters</button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(file => (
              <FileCard
                key={file.id}
                file={file}
                onClick={() => setSelected(file)}
                onDelete={isOwnFile(file, profile?.id) ? () => deleteFile(file.id) : undefined}
                onViewMember={f => f.ownerId ? goToProfileId(f.ownerId) : setViewingMember(f.uploader)}
                onPlay={handlePlay}
                isPlaying={playingFileId === file.id}
                onLike={() => toggleLike(file.id)}
                liked={likedFiles.has(file.id)}
                likeCount={likeCounts[file.id] ?? 0}
                comments={comments.filter(c => c.fileId === file.id)}
                onAddComment={text => addComment(file.id, text)}
                profile={profile}
              />
            ))}
          </div>
        )}
      </main>
      </>}

      </>}

      {/* ── COMMUNITY TAB (entirely mock/local-state, no real backend behind it) ── */}
      {activeTab === 'community' && (
        <Experimental>
        <div style={{ background: '#F5F3EF' }}>

          {/* Model Community Hero */}
          {(() => {
            const heroModel = (() => {
              const inst = (profile?.instruments ?? []).find(i => i.primary) ?? (profile?.instruments ?? [])[0]
              if (inst) return `${inst.series} ${inst.model}`
              return (profile?.organModel && profile.organModel !== 'Not specified') ? profile.organModel : (selectedShowroomModel ?? 'Imagine 239')
            })()
            const heroModelShort = heroModel.split(' ').slice(-1)[0]
            const heroModelFull = heroModel
            const heroImgKey = Object.keys(MODEL_IMAGES).find(k => k.toLowerCase().includes(heroModelShort.toLowerCase()))
            const heroImg = heroImgKey ? MODEL_IMAGES[heroImgKey] : img239

            return (
              <div style={{ background: '#0B0B0B', minHeight: 320 }}>
                <div className="max-w-6xl mx-auto px-6" style={{ paddingTop: 48, paddingBottom: 48 }}>
                  <div className="flex items-center gap-12">

                    {/* Left column */}
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase font-bold mb-3" style={{ color: '#E63412', letterSpacing: '0.35em' }}>Your Rodgers Community</p>
                      <h1 className="text-3xl font-bold uppercase text-white mb-4" style={{ letterSpacing: '0.15em', lineHeight: 1.1 }}>{heroModelFull} Community</h1>
                      <p className="text-sm mb-6 max-w-md" style={{ color: '#8a8a8a', lineHeight: 1.7 }}>
                        Connect with organists who play your model. Discover compatible music, exchange registrations and learn how others are using their {heroModelFull}.
                      </p>

                      {/* Stats row */}
                      <div className="flex gap-8 mb-7">
                        {[
                          { n: '48', label: 'organists' },
                          { n: '32', label: 'music downloads' },
                          { n: '19', label: 'registrations' },
                          { n: '14', label: 'active discussions' },
                        ].map(s => (
                          <div key={s.label}>
                            <p className="text-xl font-bold text-white" style={{ letterSpacing: '0.05em' }}>{s.n}</p>
                            <p className="text-[9px] uppercase" style={{ color: '#555', letterSpacing: '0.2em' }}>{s.label}</p>
                          </div>
                        ))}
                      </div>

                      {/* CTAs */}
                      <div className="flex items-center gap-3">
                        <button onClick={() => { requireProfile('share', () => setShowCreatePost(true)) }} className="btn" style={{ fontSize: 11 }}>Share Something</button>
                        <button onClick={() => setCommunitySubTab('music')} className="text-[11px] font-semibold uppercase" style={{ color: '#737373', letterSpacing: '0.18em' }}>Explore Music for {heroModelFull} →</button>
                      </div>
                    </div>

                    {/* Right column — organ image + model selector */}
                    <div className="shrink-0 flex flex-col items-end gap-4" style={{ width: 340 }}>
                      {/* Model selector */}
                      <button
                        onClick={() => setShowCommunityModelOverlay(true)}
                        className="flex items-center gap-3 px-4 py-2.5 transition-all self-start"
                        style={{ background: '#141414', border: '1px solid #2a2a2a', borderRadius: 3 }}
                      >
                        <div>
                          <p className="text-[8px] uppercase font-bold" style={{ color: '#E63412', letterSpacing: '0.3em' }}>Your Rodgers</p>
                          <p className="text-[12px] font-bold uppercase text-white" style={{ letterSpacing: '0.12em' }}>{heroModelFull}</p>
                        </div>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                      </button>
                      <div style={{ width: 340, height: 220, overflow: 'hidden', borderRadius: 4, background: '#111' }}>
                        <img src={heroImg} alt={heroModelFull} className="w-full h-full object-contain" style={{ opacity: 0.9 }} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Model community navigation — 4 primary tabs */}
                <div style={{ borderTop: '1px solid #1a1a1a', borderBottom: '1px solid #1a1a1a' }}>
                  <div className="max-w-6xl mx-auto px-6">
                    <div className="flex gap-0">
                      {([
                        ['feed', 'Feed'],
                        ['music', 'Music'],
                        ['questions', 'Questions'],
                        ['members', 'Members'],
                      ] as const).map(([tab, label]) => (
                        <button
                          key={tab}
                          onClick={() => setCommunitySubTab(tab)}
                          className="px-5 py-4 text-[11px] font-semibold uppercase transition-colors"
                          style={{
                            letterSpacing: '0.18em',
                            color: communitySubTab === tab ? '#fff' : '#555',
                            borderBottom: communitySubTab === tab ? '2px solid #E63412' : '2px solid transparent',
                          }}
                        >{label}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          })()}

          {/* ── COMMUNITY FEED ── */}
          {communitySubTab === 'feed' && (() => {
            const allMembers = Object.values(MEMBERS)
            const profileModel = (() => {
              const inst = (profile?.instruments ?? []).find(i => i.primary) ?? (profile?.instruments ?? [])[0]
              if (inst) return `${inst.series} ${inst.model}`
              return (profile?.organModel && profile.organModel !== 'Not specified') ? profile.organModel : null
            })()
            const userCommunityModel = profileModel ?? selectedShowroomModel

            type FeedItem = {
              id: string
              type: 'performance' | 'musicdownload' | 'registration' | 'question' | 'challenge' | 'liveannouncement' | 'update'
              member: string
              memberIsStaff?: boolean
              action: string
              model: string
              compat: boolean
              time: string
              postText: string
              title: string
              composer?: string
              duration?: string
              fileFormats?: string[]
              appreciations: number
              downloads?: number
              responses?: number
              answered?: boolean
              compat_models?: string
              liveDate?: string
              liveTime?: string
              liveRsvp?: number
              liveIsLive?: boolean
              updateVersion?: string
            }

            const FEED_ITEMS: FeedItem[] = [
              {
                id: 'f1', type: 'performance',
                member: 'Michael Bauer',
                action: 'uploaded a performance',
                model: 'Infinity 489',
                compat: userCommunityModel ? userCommunityModel.includes('489') || userCommunityModel.includes('367') : true,
                time: '38 minutes ago',
                postText: "Recorded this during Sunday Hauptgottesdienst at St. Peter's — Principal 16' through Mixture on the Great, solo Trompete 8' for the chorale melody. The 489's room modeling is uncanny at this dynamic level. Happy to share the full registration if anyone wants it.",
                title: 'Nun danket alle Gott, BWV 657',
                composer: 'J.S. Bach',
                duration: '5:14',
                appreciations: 31,
                downloads: 12,
              },
              {
                id: 'f2', type: 'performance',
                member: 'Catherine Reinhardt',
                action: 'uploaded a performance',
                model: 'Infinity 367',
                compat: userCommunityModel ? userCommunityModel.includes('367') || userCommunityModel.includes('489') : true,
                time: '2 hours ago',
                postText: "Finally happy with my approach to the Prélude — using the Flûte harmonique 8' on the Swell for the solo voice and a very restrained Great for the accompaniment. Recorded live at Grace Cathedral during the Thursday lunchtime series. The 367's Swell box response in this room is extraordinary.",
                title: "Prélude, Fugue et Variation, Op. 18",
                composer: 'César Franck',
                duration: '11:47',
                appreciations: 58,
                downloads: 19,
              },
              {
                id: 'f3', type: 'musicdownload',
                member: 'Sophie Lefebvre',
                action: 'shared sheet music',
                model: 'Imagine 359',
                compat: userCommunityModel ? userCommunityModel.includes('359') || userCommunityModel.includes('239') || userCommunityModel.includes('241') : false,
                time: '4 hours ago',
                postText: "Sharing my performance edition of the Duruflé Prélude — bowings, fingerings, and pedaling are mine, adapted from Duruflé's own markings. The edition works beautifully on any Imagine with a French Romantic sound set. Includes my registration notes for the 359.",
                title: "Prélude sur l'Introit de l'Épiphanie",
                composer: 'Maurice Duruflé',
                fileFormats: ['PDF', 'Registration'],
                appreciations: 44,
                downloads: 83,
                compat_models: 'Edition notes for Imagine 359',
              },
              {
                id: 'f4', type: 'registration',
                member: 'Stacy Piontek',
                action: 'shared a registration',
                model: 'Inspire 352',
                compat: userCommunityModel ? userCommunityModel.includes('352') || userCommunityModel.includes('234') || userCommunityModel.includes('240') : false,
                time: '1 day ago',
                postText: 'This Festival Trumpet registration has been my go-to for processionals and Reformation Sunday. Bright and full without being harsh — reduces the Great principal chorus by about 2 dB from factory and adds a hint of room depth. Tested on all three Inspire models.',
                title: 'Festival Trumpet — Processional',
                composer: 'Inspire 352',
                appreciations: 31,
                downloads: 61,
                compat_models: 'Compatible with Inspire 234, 240, 352',
              },
              {
                id: 'f5', type: 'performance',
                member: 'Marcus Kessler',
                action: 'uploaded a performance',
                model: 'Infinity 489',
                compat: userCommunityModel ? userCommunityModel.includes('489') || userCommunityModel.includes('367') : false,
                time: '1 day ago',
                postText: "From the Thomaskirche Abendmusik last Friday — I played this directly before the motet, so registration is intentionally dark and restrained. No 16' reed, just strings and Gedeckt. The 489's soft attack on the string stops is as close to a real instrument as I have heard.",
                title: 'O Mensch, bewein dein Sünde groß, BWV 622',
                composer: 'J.S. Bach',
                duration: '7:02',
                appreciations: 77,
                downloads: 24,
              },
              {
                id: 'f6', type: 'question',
                member: 'Anna Vogel',
                action: 'asked a question',
                model: 'Inspire 240',
                compat: userCommunityModel ? userCommunityModel.includes('240') || userCommunityModel.includes('234') || userCommunityModel.includes('352') : false,
                time: '5 hours ago',
                postText: "Preparing Advent music and want something more contemplative than the usual Wachet auf. Would love suggestions for lesser-known chorale preludes that work well on a two-manual instrument — ideally something playable at an intermediate level.",
                title: 'Lesser-known Advent chorale preludes for a two-manual organ?',
                appreciations: 12,
                responses: 9,
                answered: false,
              },
              {
                id: 'f7', type: 'registration',
                member: 'Thomas Brandt',
                action: 'shared a registration',
                model: 'Infinity 367',
                compat: userCommunityModel ? userCommunityModel.includes('367') || userCommunityModel.includes('489') : false,
                time: '2 days ago',
                postText: "My Plein Jeu registration for the 367 — based on the classic French Classic foundation used at St. Michaelis. Principal 8', 4', 2', Fourniture, Cymbal on the Great; Flûte 8' and Bourdon 16' on the Pedal. Suits both Buxtehude and early Couperin.",
                title: 'Plein Jeu — French Classic Foundation',
                composer: 'Infinity 367',
                appreciations: 49,
                downloads: 88,
                compat_models: 'Compatible with Infinity 367, 489',
              },
              {
                id: 'f8', type: 'performance',
                member: 'Robert Thompson',
                action: 'uploaded a performance',
                model: 'Imagine 241',
                compat: userCommunityModel ? userCommunityModel.includes('241') || userCommunityModel.includes('239') || userCommunityModel.includes('359') : false,
                time: '2 days ago',
                postText: "Processional for our annual Homecoming Sunday — the congregation was 600+ and I needed the full house to feel it. This registration pushes the 241 further than I usually go but it delivered. The Trompete on the Great carried to the balcony clearly.",
                title: 'Holy, Holy, Holy',
                composer: 'John Bacchus Dykes',
                duration: '3:15',
                appreciations: 38,
                downloads: 14,
              },
              {
                id: 'f9', type: 'challenge',
                member: 'Elizabeth Hart',
                action: 'submitted a challenge entry',
                model: 'Inspire 352',
                compat: userCommunityModel ? userCommunityModel.includes('352') || userCommunityModel.includes('240') || userCommunityModel.includes('234') : false,
                time: '3 days ago',
                postText: "September Challenge entry — recorded in the Christ Church nave at 7 AM before the choir call. I kept the registration very lean: Gedackt 8' and Rohrflöte 4' only. Entry #7 of 18.",
                title: "In dir ist Freude, BWV 615",
                composer: 'J.S. Bach',
                duration: '4:28',
                appreciations: 22,
              },
              {
                id: 'f10', type: 'liveannouncement',
                member: 'Dan Miller',
                action: 'announced a live session',
                model: 'Infinity 489',
                compat: userCommunityModel ? userCommunityModel.includes('489') || userCommunityModel.includes('367') || userCommunityModel.includes('239') : false,
                time: '3 hours ago',
                postText: "Join me this Tuesday for a deep dive into orchestral registration on the Infinity 489. We will build a full French Romantic texture from scratch — Swell, Choir, Great and Pedal — and I will answer questions live.",
                title: 'Creating Orchestral Registrations',
                liveDate: 'Tuesday, September 24, 2026',
                liveTime: '4:00 PM PT · 7:00 PM ET',
                liveRsvp: 47,
                liveIsLive: false,
                appreciations: 28,
              },
              {
                id: 'f11', type: 'musicdownload',
                member: 'Isabelle Moreau',
                action: 'shared sheet music',
                model: 'Imagine 359',
                compat: userCommunityModel ? userCommunityModel.includes('359') || userCommunityModel.includes('239') || userCommunityModel.includes('241') : false,
                time: '3 days ago',
                postText: "My scholarly edition of the Symphonie No. 1 finale — includes my fingerings and pedaling from the Saint-Denis recording sessions. I have added a second column of registration suggestions specifically for Imagine 359 players alongside the original Cavaillé-Coll specification.",
                title: "Symphonie No. 1 in C minor — Finale",
                composer: 'Charles-Marie Widor',
                fileFormats: ['PDF'],
                appreciations: 61,
                downloads: 109,
                compat_models: 'Edition notes for Imagine 359',
              },
              {
                id: 'f12', type: 'update',
                member: 'Rodgers Instruments',
                memberIsStaff: true,
                action: 'posted an official update',
                model: 'All Models',
                compat: true,
                time: '4 days ago',
                postText: "Verified Rodgers product updates, firmware releases, and service bulletins will appear here when available. This card is sample content included for layout purposes only.",
                title: 'Product Updates — Sample Content',
                updateVersion: undefined,
                appreciations: 0,
                downloads: 0,
              },
            ]

            const SUGGESTED = allMembers.filter(m => !following.has(m.name)).slice(0, 4)
            const UPCOMING_LIVE = [
              { date: 'SEP 24 · 4:00 PM PT', title: 'Creating Orchestral Registrations', artist: 'Dan Miller', model: 'Infinity 489', rsvp: 47 },
              { date: 'OCT 8 · 6:00 PM ET', title: 'French Romantic Repertoire', artist: 'Sophie Lefebvre', model: 'Imagine 359', rsvp: 29 },
            ]

            return (
              <div className="max-w-6xl mx-auto px-6 py-8">
                <div className="flex gap-8 items-start">

                  {/* LEFT: Feed */}
                  <div className="flex-1 min-w-0">

                    {/* Community Scope — 2 options */}
                    <div className="mb-4" style={{ background: '#fff', border: '1px solid #D8D4CC', borderRadius: 4, overflow: 'hidden' }}>
                      <div className="grid grid-cols-2">
                        {([
                          ['mymodel', 'My Model', userCommunityModel ? `${userCommunityModel} only` : 'Your model only'],
                          ['allrodgers', 'All Rodgers', 'The complete Rodgers community'],
                        ] as const).map(([key, label, sub], i) => (
                          <button
                            key={key}
                            onClick={() => setCommunityScope(key)}
                            className="flex flex-col items-start gap-0.5 px-5 py-3.5 transition-all text-left"
                            style={{
                              background: communityScope === key ? '#0B0B0B' : '#fff',
                              borderRight: i < 1 ? '1px solid #D8D4CC' : undefined,
                              borderLeft: communityScope === key ? '3px solid #E63412' : '3px solid transparent',
                            }}
                          >
                            <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '0.15em', color: communityScope === key ? '#fff' : '#0B0B0B' }}>{label}</span>
                            <span className="text-[9px]" style={{ color: communityScope === key ? '#737373' : '#B0AAA0', letterSpacing: '0.05em' }}>{sub}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Feed filter pills — 4 filters */}
                    <div className="flex gap-2 mb-5 flex-wrap">
                      {([
                        ['foryou', 'For You'],
                        ['performances', 'Performances'],
                        ['registrations', 'Registrations'],
                        ['questions', 'Questions'],
                      ] as const).map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => setFeedFilter(key)}
                          className="px-4 py-2 text-[10px] font-semibold uppercase transition-all"
                          style={{
                            letterSpacing: '0.15em',
                            borderRadius: 3,
                            height: 36,
                            background: feedFilter === key ? '#0B0B0B' : '#fff',
                            color: feedFilter === key ? '#fff' : '#0B0B0B',
                            border: `1px solid ${feedFilter === key ? '#0B0B0B' : '#D8D4CC'}`,
                          }}
                        >{label}</button>
                      ))}
                    </div>

                    {/* Composer card */}
                    <div className="mb-5" style={{ background: '#fff', border: '1px solid #D8D4CC', borderRadius: 4 }}>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-4">
                          {profile?.avatar
                            ? <img src={profile.avatar} alt="You" className="w-11 h-11 rounded-full object-cover shrink-0" style={{ border: '2px solid #E63412' }} />
                            : <div className="w-11 h-11 rounded-full shrink-0 flex items-center justify-center font-bold text-white text-sm" style={{ background: '#E63412' }}>{profile ? profile.name.charAt(0) : 'R'}</div>
                          }
                          <button
                            onClick={() => requireProfile('share', () => setShowCreatePost(true))}
                            className="flex-1 px-4 py-3 text-left transition-all"
                            style={{ background: '#F5F3EF', border: '1px solid #D8D4CC', borderRadius: 3, color: '#B0AAA0', fontSize: 13, fontFamily: 'inherit' }}
                          >Share with the {userCommunityModel ?? 'Rodgers'} community…</button>
                        </div>
                        <div className="flex gap-2 pt-3 border-t" style={{ borderColor: '#F0EDE8' }}>
                          {([
                            { label: 'Upload a Performance', type: 'performance', icon: (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E63412" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                            )},
                            { label: 'Share a Registration', type: 'registration', icon: (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E63412" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                            )},
                            { label: 'Ask Model Owners', type: 'question', icon: (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E63412" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                            )},
                          ]).map(opt => (
                            <button
                              key={opt.label}
                              onClick={() => requireProfile('share', () => { setCreatePostType(opt.type); setShowCreatePost(true) })}
                              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-[10px] font-semibold uppercase transition-all hover:bg-[#F5F3EF] group"
                              style={{ letterSpacing: '0.12em', color: '#4a4a4a', border: '1px solid #D8D4CC', borderRadius: 3, height: 40 }}
                            >
                              {opt.icon}
                              <span className="group-hover:text-[#E63412] transition-colors">{opt.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Feed posts */}
                    <div className="flex flex-col gap-4">
                      {FEED_ITEMS.filter(item => communityScope !== 'mymodel' || item.compat).map(item => {
                        const m = MEMBERS[item.member]
                        const appreciated = postLikes.has(item.id)
                        const appreciationCount = postLikeCounts[item.id] ?? item.appreciations
                        const commentCount = (postComments[item.id] ?? []).length
                        const totalComments = item.type === 'question' ? commentCount + (item.responses ?? 0) : commentCount

                        return (
                          <div key={item.id} style={{ background: '#fff', border: '1px solid #D8D4CC', borderRadius: 4, overflow: 'hidden' }}>

                            {/* Post header */}
                            <div className="flex items-start gap-3 p-5 pb-4">
                              <button onClick={() => item.memberIsStaff ? undefined : setViewingMember(item.member)} className="shrink-0">
                                {item.memberIsStaff ? (
                                  <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-sm" style={{ background: '#E63412', border: '2px solid #E63412' }}>R</div>
                                ) : m?.avatar
                                  ? <img src={m.avatar} alt={item.member} className="w-12 h-12 rounded-full object-cover" style={{ border: '2px solid #D8D4CC' }} />
                                  : <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-base" style={{ background: '#E63412' }}>{item.member.charAt(0)}</div>
                                }
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <button onClick={() => item.memberIsStaff ? undefined : setViewingMember(item.member)} className="text-[13px] font-bold uppercase hover:text-[#E63412] transition-colors" style={{ letterSpacing: '0.1em', color: '#0B0B0B' }}>{item.member}</button>
                                  {item.memberIsStaff && <span className="text-[8px] font-bold uppercase px-1.5 py-0.5" style={{ background: '#E63412', color: '#fff', letterSpacing: '0.15em', borderRadius: 2 }}>Official</span>}
                                  <span className="text-[12px]" style={{ color: '#737373' }}>{item.action}</span>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {item.model !== 'All Models' && <span className="text-[9px] px-2 py-0.5 font-bold uppercase" style={{ background: '#0B0B0B', color: '#fff', letterSpacing: '0.12em', borderRadius: 2 }}>{item.model}</span>}
                                  {item.compat && userCommunityModel && item.model !== 'All Models' && item.model !== userCommunityModel && (
                                    <span className="text-[9px] font-semibold" style={{ color: '#E63412', letterSpacing: '0.06em' }}>✓ Compatible with your {userCommunityModel?.replace(/^(Infinity|Imagine|Inspire|The American) Series /, '') ?? userCommunityModel}</span>
                                  )}
                                  <span className="text-[11px]" style={{ color: '#B0AAA0' }}>{item.time}</span>
                                  {item.type === 'challenge' && <span className="text-[8px] font-bold uppercase px-2 py-0.5" style={{ background: '#E63412', color: '#fff', letterSpacing: '0.15em', borderRadius: 2 }}>Challenge</span>}
                                  {item.type === 'question' && item.answered && <span className="text-[8px] font-bold uppercase px-2 py-0.5" style={{ color: '#47A855', border: '1px solid #47A855', letterSpacing: '0.15em', borderRadius: 2 }}>Answered</span>}
                                  {item.type === 'liveannouncement' && item.liveIsLive && <span className="text-[8px] font-bold uppercase px-2 py-0.5 flex items-center gap-1" style={{ background: '#E63412', color: '#fff', letterSpacing: '0.15em', borderRadius: 2 }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />Live</span>}
                                  {item.type === 'update' && item.updateVersion && <span className="text-[8px] font-bold uppercase px-2 py-0.5" style={{ background: '#242424', color: '#fff', letterSpacing: '0.15em', borderRadius: 2 }}>v{item.updateVersion}</span>}
                                </div>
                              </div>
                              <button className="shrink-0 w-8 h-8 flex items-center justify-center rounded transition-colors hover:bg-[#F5F3EF]" style={{ color: '#B0AAA0' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
                              </button>
                            </div>

                            {/* Post body text */}
                            <div className="px-5 pb-4">
                              <p style={{ fontSize: 14, lineHeight: '22px', color: '#2a2a2a' }}>{item.postText}</p>
                            </div>

                            {/* ── Media block per post type ── */}

                            {/* Performance / Challenge — 16:9 video thumbnail */}
                            {(item.type === 'performance' || item.type === 'challenge') && (
                              <div className="mx-5 mb-4" style={{ background: '#0B0B0B', borderRadius: 4, overflow: 'hidden' }}>
                                <div className="relative" style={{ paddingBottom: '42%' }}>
                                  <div className="absolute inset-0 flex items-center justify-center" style={{ background: '#111' }}>
                                    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(0deg, #222 0px, #222 1px, transparent 1px, transparent 24px)', backgroundSize: '100% 24px' }} />
                                    <button className="relative w-16 h-16 flex items-center justify-center rounded-full transition-transform hover:scale-105" style={{ background: '#E63412', boxShadow: '0 4px 32px rgba(230,52,18,0.5)' }}>
                                      <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                    </button>
                                  </div>
                                  {item.duration && <div className="absolute bottom-2 right-2 px-2 py-0.5 text-[10px] font-semibold" style={{ background: 'rgba(0,0,0,0.75)', color: '#fff', borderRadius: 2 }}>{item.duration}</div>}
                                  {item.type === 'challenge' && <div className="absolute top-2 left-2 px-2 py-0.5 text-[8px] font-bold uppercase" style={{ background: '#E63412', color: '#fff', letterSpacing: '0.15em', borderRadius: 2 }}>Entry #12</div>}
                                </div>
                                <div className="px-4 py-3 flex items-center gap-3">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[11px] font-bold uppercase text-white truncate" style={{ letterSpacing: '0.08em' }}>{item.title}</p>
                                    {item.composer && <p className="text-[10px] truncate" style={{ color: '#737373' }}>{item.composer} · {item.model}</p>}
                                  </div>
                                  {item.type === 'challenge' && (
                                    <div className="flex gap-2 shrink-0">
                                      <button className="px-3 py-1.5 text-[9px] font-bold uppercase" style={{ background: '#E63412', color: '#fff', borderRadius: 2, letterSpacing: '0.12em' }}>▶ Listen</button>
                                      <button className="px-3 py-1.5 text-[9px] font-bold uppercase" style={{ background: 'transparent', color: '#fff', border: '1px solid #333', borderRadius: 2, letterSpacing: '0.12em' }}>Vote</button>
                                    </div>
                                  )}
                                  {item.type === 'performance' && (
                                    <button onClick={() => requireProfile('download', () => {})} className="shrink-0 px-3 py-1.5 text-[9px] font-bold uppercase" style={{ background: 'transparent', color: '#737373', border: '1px solid #333', borderRadius: 2, letterSpacing: '0.12em' }}>Download</button>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Music Download — compact audio player + file card */}
                            {item.type === 'musicdownload' && (
                              <div className="mx-5 mb-4 flex flex-col gap-2">
                                {/* Audio player strip */}
                                <div className="flex items-center gap-3 px-4 py-3" style={{ background: '#0B0B0B', borderRadius: 4 }}>
                                  <button className="w-9 h-9 flex items-center justify-center rounded-full shrink-0" style={{ background: '#E63412' }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                  </button>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[11px] font-bold uppercase text-white truncate mb-1" style={{ letterSpacing: '0.08em' }}>{item.title}</p>
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 h-1 rounded-full" style={{ background: '#333' }}>
                                        <div className="h-1 rounded-full w-0" style={{ background: '#E63412' }} />
                                      </div>
                                      <span className="text-[9px] shrink-0" style={{ color: '#555' }}>0:00</span>
                                    </div>
                                  </div>
                                  <button onClick={() => requireProfile('download', () => {})} className="shrink-0 px-3 py-1.5 text-[9px] font-bold uppercase" style={{ background: 'transparent', color: '#737373', border: '1px solid #333', borderRadius: 2, letterSpacing: '0.12em' }}>Download</button>
                                </div>
                                {/* File format chips + compat */}
                                <div className="flex items-center gap-2 flex-wrap px-1">
                                  {(item.fileFormats ?? []).map(fmt => (
                                    <span key={fmt} className="text-[9px] font-bold uppercase px-2 py-0.5" style={{ background: '#F5F3EF', color: '#737373', border: '1px solid #D8D4CC', borderRadius: 2, letterSpacing: '0.12em' }}>{fmt}</span>
                                  ))}
                                  {item.compat_models && <span className="text-[9px]" style={{ color: '#737373' }}>{item.compat_models}</span>}
                                  {item.compat && userCommunityModel && <span className="text-[9px] font-semibold uppercase" style={{ color: '#E63412', letterSpacing: '0.12em' }}>✓ Compatible with your {userCommunityModel.split(' ').slice(-1)[0]}</span>}
                                </div>
                              </div>
                            )}

                            {/* Registration — file card */}
                            {item.type === 'registration' && (
                              <div className="mx-5 mb-4 p-4 flex items-center gap-4" style={{ background: '#F5F3EF', border: '1px solid #D8D4CC', borderRadius: 4 }}>
                                <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: '#0B0B0B', borderRadius: 3 }}>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E63412" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[12px] font-bold uppercase mb-0.5" style={{ letterSpacing: '0.1em', color: '#0B0B0B' }}>{item.title}</p>
                                  {item.compat_models && <p className="text-[10px]" style={{ color: '#737373' }}>{item.compat_models}</p>}
                                  {item.compat && userCommunityModel && <p className="text-[9px] font-semibold uppercase mt-0.5" style={{ color: '#E63412', letterSpacing: '0.12em' }}>✓ Compatible with your {userCommunityModel.split(' ').slice(-1)[0]}</p>}
                                </div>
                                <div className="flex gap-2 shrink-0">
                                  <button className="px-3 py-2 text-[9px] font-bold uppercase" style={{ background: '#fff', color: '#0B0B0B', border: '1px solid #D8D4CC', borderRadius: 2, letterSpacing: '0.12em' }}>Preview</button>
                                  <button onClick={() => requireProfile('download', () => {})} className="px-3 py-2 text-[9px] font-bold uppercase" style={{ background: '#E63412', color: '#fff', borderRadius: 2, letterSpacing: '0.12em' }}>Download</button>
                                </div>
                              </div>
                            )}

                            {/* Question — answer count button */}
                            {item.type === 'question' && (
                              <div className="mx-5 mb-4 flex items-center gap-3 px-4 py-3" style={{ background: '#F5F3EF', border: '1px solid #D8D4CC', borderRadius: 3 }}>
                                <div className="flex-1">
                                  <p className="text-[12px] font-bold uppercase mb-0.5" style={{ letterSpacing: '0.1em', color: '#0B0B0B' }}>{item.title}</p>
                                  <p className="text-[10px]" style={{ color: '#737373' }}>
                                    {totalComments} {totalComments === 1 ? 'answer' : 'answers'}
                                    {item.answered && <span style={{ color: '#47A855' }}> · ✓ Answered by a Rodgers Artist</span>}
                                  </p>
                                </div>
                                <button
                                  onClick={() => { if (!profile) { setShowSignInPrompt(item.id) } else { setOpenCommentPost(openCommentPost === item.id ? null : item.id) } }}
                                  className="shrink-0 px-4 py-2 text-[9px] font-bold uppercase"
                                  style={{ background: '#0B0B0B', color: '#fff', borderRadius: 2, letterSpacing: '0.12em' }}
                                >View Discussion</button>
                              </div>
                            )}

                            {/* Live Session Announcement */}
                            {item.type === 'liveannouncement' && (
                              <div className="mx-5 mb-4" style={{ background: '#0B0B0B', border: item.liveIsLive ? '2px solid #E63412' : '1px solid #1e1e1e', borderRadius: 4, overflow: 'hidden' }}>
                                {item.liveIsLive && <div style={{ height: 3, background: '#E63412' }} />}
                                <div className="p-4 flex items-center gap-4">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[9px] font-bold uppercase mb-1" style={{ color: item.liveIsLive ? '#E63412' : '#555', letterSpacing: '0.25em' }}>
                                      {item.liveIsLive ? '● Live Now' : `Live · ${item.liveDate}`}
                                    </p>
                                    <p className="text-[13px] font-bold uppercase text-white mb-0.5" style={{ letterSpacing: '0.1em', lineHeight: 1.3 }}>{item.title}</p>
                                    <p className="text-[10px]" style={{ color: '#737373' }}>with {item.member} · {item.model}</p>
                                    {item.liveTime && !item.liveIsLive && <p className="text-[10px] mt-0.5" style={{ color: '#555' }}>{item.liveTime}</p>}
                                  </div>
                                  <div className="text-right shrink-0">
                                    <button className="btn text-[9px] mb-2" style={{ padding: '8px 16px 7px', display: 'block', width: '100%' }}>
                                      {item.liveIsLive ? 'Join Live' : 'RSVP'}
                                    </button>
                                    {item.liveRsvp && <p className="text-[9px]" style={{ color: '#555' }}>{item.liveRsvp} going</p>}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Official Rodgers Update */}
                            {item.type === 'update' && (
                              <div className="mx-5 mb-4 p-4 flex items-start gap-4" style={{ background: '#F5F3EF', border: '1px solid #D8D4CC', borderRadius: 4 }}>
                                <div className="w-10 h-10 flex items-center justify-center shrink-0 font-bold text-white text-sm" style={{ background: '#E63412', borderRadius: 3 }}>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[11px] font-bold uppercase mb-0.5" style={{ letterSpacing: '0.1em', color: '#0B0B0B' }}>Firmware {item.updateVersion} · {item.title.replace(`Firmware ${item.updateVersion} — `, '')}</p>
                                  <p className="text-[10px] mb-2" style={{ color: '#737373' }}>Available now for Imagine & Infinity series</p>
                                  <button className="px-3 py-1.5 text-[9px] font-bold uppercase" style={{ background: '#0B0B0B', color: '#fff', borderRadius: 2, letterSpacing: '0.12em' }}>View Release Notes</button>
                                </div>
                                <span className="text-[9px] font-bold uppercase px-2 py-0.5 shrink-0" style={{ background: '#47A855', color: '#fff', borderRadius: 2, letterSpacing: '0.12em' }}>New</span>
                              </div>
                            )}

                            {/* Engagement counts */}
                            <div className="flex items-center gap-4 px-5 py-2.5 border-t border-b" style={{ borderColor: '#F0EDE8' }}>
                              <span className="text-[12px]" style={{ color: '#B0AAA0' }}>{appreciationCount} {appreciationCount === 1 ? 'Appreciation' : 'Appreciations'}</span>
                              <span className="text-[12px]" style={{ color: '#B0AAA0' }}>{totalComments} {totalComments === 1 ? 'Comment' : 'Comments'}</span>
                              {item.downloads !== undefined && <span className="text-[12px]" style={{ color: '#B0AAA0' }}>{item.downloads} Downloads</span>}
                            </div>

                            {/* Action bar — 4 equal columns */}
                            <div className="grid grid-cols-4">
                              {/* Appreciate */}
                              <button
                                onClick={() => {
                                  const wasAppreciated = postLikes.has(item.id)
                                  setPostLikes(prev => { const n = new Set(prev); wasAppreciated ? n.delete(item.id) : n.add(item.id); return n })
                                  setPostLikeCounts(prev => ({ ...prev, [item.id]: (prev[item.id] ?? item.appreciations) + (wasAppreciated ? -1 : 1) }))
                                }}
                                className="flex items-center justify-center gap-2 py-3 text-[10px] font-semibold uppercase transition-all border-r"
                                style={{ letterSpacing: '0.12em', borderColor: '#F0EDE8', color: appreciated ? '#E63412' : '#737373', background: appreciated ? 'rgba(230,52,18,0.04)' : 'transparent' }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill={appreciated ? '#E63412' : 'none'} stroke={appreciated ? '#E63412' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                </svg>
                                Appreciate
                              </button>

                              {/* Comment */}
                              <button
                                onClick={() => { if (!profile) { setShowSignInPrompt(item.id) } else { setOpenCommentPost(openCommentPost === item.id ? null : item.id) } }}
                                className="flex items-center justify-center gap-2 py-3 text-[10px] font-semibold uppercase transition-all border-r"
                                style={{ letterSpacing: '0.12em', borderColor: '#F0EDE8', color: openCommentPost === item.id ? '#0B0B0B' : '#737373', background: openCommentPost === item.id ? '#F5F3EF' : 'transparent' }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                                Comment
                              </button>

                              {/* Save */}
                              <button
                                className="flex items-center justify-center gap-2 py-3 text-[10px] font-semibold uppercase transition-all border-r hover:bg-[#F5F3EF]"
                                style={{ letterSpacing: '0.12em', borderColor: '#F0EDE8', color: '#737373' }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                                </svg>
                                Save
                              </button>

                              {/* Share */}
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(`https://connect.rodgersinstruments.com/community/post/${item.id}`).catch(() => {})
                                  setCopiedPost(item.id)
                                  setTimeout(() => setCopiedPost(prev => prev === item.id ? null : prev), 2000)
                                }}
                                className="flex items-center justify-center gap-2 py-3 text-[10px] font-semibold uppercase transition-all hover:bg-[#F5F3EF]"
                                style={{ letterSpacing: '0.12em', color: copiedPost === item.id ? '#47A855' : '#737373' }}
                              >
                                {copiedPost === item.id ? (
                                  <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#47A855" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                    Copied
                                  </>
                                ) : (
                                  <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                                    </svg>
                                    Share
                                  </>
                                )}
                              </button>
                            </div>

                            {/* Comment thread */}
                            {openCommentPost === item.id && (() => {
                              const allPostComments = postComments[item.id] ?? []
                              const isExpanded = expandedComments.has(item.id)
                              const visibleComments = isExpanded ? allPostComments : allPostComments.slice(0, 2)
                              const hiddenCount = allPostComments.length - 2
                              return (
                                <div style={{ borderTop: '1px solid #F0EDE8', background: '#FAFAF8' }}>
                                  {allPostComments.length > 0 && (
                                    <div className="px-5 pt-4 flex flex-col gap-3">
                                      {!isExpanded && hiddenCount > 0 && (
                                        <button
                                          onClick={() => setExpandedComments(prev => { const n = new Set(prev); n.add(item.id); return n })}
                                          className="text-[11px] font-semibold text-left"
                                          style={{ color: '#737373' }}
                                        >View all {allPostComments.length} comments</button>
                                      )}
                                      {visibleComments.map(c => {
                                        const cm = MEMBERS[c.author]
                                        const likeKey = `${item.id}-${c.id}`
                                        const cLiked = commentLikes.has(likeKey)
                                        return (
                                          <div key={c.id} className="flex gap-3">
                                            {cm?.avatar
                                              ? <img src={cm.avatar} alt={c.author} className="w-8 h-8 rounded-full object-cover shrink-0 mt-0.5" style={{ border: '1.5px solid #D8D4CC' }} />
                                              : <div className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold text-white mt-0.5" style={{ background: '#E63412' }}>{c.author.charAt(0)}</div>
                                            }
                                            <div className="flex-1 min-w-0">
                                              <div className="px-4 py-3" style={{ background: '#fff', border: '1px solid #E8E4DF', borderRadius: '0 6px 6px 6px' }}>
                                                <div className="flex items-center gap-2 mb-1.5">
                                                  <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '0.08em', color: '#0B0B0B' }}>{c.author}</span>
                                                  {c.model && <span className="text-[8px] font-bold uppercase px-1.5 py-0.5" style={{ background: '#F5F3EF', color: '#737373', border: '1px solid #D8D4CC', letterSpacing: '0.1em', borderRadius: 2 }}>{c.model}</span>}
                                                </div>
                                                <p style={{ fontSize: 13, lineHeight: '20px', color: '#3a3a3a' }}>{c.text}</p>
                                              </div>
                                              <div className="flex items-center gap-3 mt-1.5 ml-1">
                                                <span className="text-[10px]" style={{ color: '#B0AAA0' }}>{c.time}</span>
                                                <button
                                                  onClick={() => setCommentLikes(prev => { const n = new Set(prev); cLiked ? n.delete(likeKey) : n.add(likeKey); return n })}
                                                  className="flex items-center gap-1 text-[10px] font-semibold uppercase transition-colors"
                                                  style={{ letterSpacing: '0.1em', color: cLiked ? '#E63412' : '#B0AAA0' }}
                                                >
                                                  <svg width="11" height="11" viewBox="0 0 24 24" fill={cLiked ? '#E63412' : 'none'} stroke={cLiked ? '#E63412' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                                                  Appreciate
                                                </button>
                                                <button className="text-[10px] font-semibold uppercase transition-colors hover:text-[#0B0B0B]" style={{ letterSpacing: '0.1em', color: '#B0AAA0' }}>Reply</button>
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      })}
                                      {isExpanded && hiddenCount > 0 && (
                                        <button
                                          onClick={() => setExpandedComments(prev => { const n = new Set(prev); n.delete(item.id); return n })}
                                          className="text-[11px] font-semibold text-left"
                                          style={{ color: '#737373' }}
                                        >Show fewer comments</button>
                                      )}
                                    </div>
                                  )}
                                  <div className="flex gap-3 p-4 pt-3">
                                    {profile?.avatar
                                      ? <img src={profile.avatar} alt="You" className="w-8 h-8 rounded-full object-cover shrink-0 mt-0.5" style={{ border: '2px solid #E63412' }} />
                                      : <div className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-[11px] font-bold text-white mt-0.5" style={{ background: '#E63412' }}>{profile ? profile.name.charAt(0) : 'R'}</div>
                                    }
                                    <div className="flex-1 flex gap-2">
                                      <input
                                        value={commentDraft}
                                        onChange={e => setCommentDraft(e.target.value)}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter' && !e.shiftKey && commentDraft.trim()) {
                                            e.preventDefault()
                                            const authorName = profile ? profile.name : 'You'
                                            const newComment = { id: `c${Date.now()}`, author: authorName, text: commentDraft.trim(), time: 'Just now' }
                                            setPostComments(prev => ({ ...prev, [item.id]: [...(prev[item.id] ?? []), newComment] }))
                                            setCommentDraft('')
                                          }
                                        }}
                                        placeholder="Add a comment… (Enter to post)"
                                        className="flex-1 px-3 py-2.5 outline-none"
                                        style={{ background: '#fff', border: '1px solid #D8D4CC', borderRadius: 3, color: '#0B0B0B', fontFamily: 'inherit', fontSize: 13 }}
                                      />
                                      <button
                                        onClick={() => {
                                          if (!commentDraft.trim()) return
                                          const authorName = profile ? profile.name : 'You'
                                          const newComment = { id: `c${Date.now()}`, author: authorName, text: commentDraft.trim(), time: 'Just now' }
                                          setPostComments(prev => ({ ...prev, [item.id]: [...(prev[item.id] ?? []), newComment] }))
                                          setCommentDraft('')
                                        }}
                                        className="px-4 py-2 text-[10px] font-bold uppercase"
                                        style={{ background: '#E63412', color: '#fff', letterSpacing: '0.12em', borderRadius: 3, opacity: commentDraft.trim() ? 1 : 0.4 }}
                                      >Post</button>
                                    </div>
                                  </div>
                                </div>
                              )
                            })()}
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* RIGHT: Sidebar */}
                  <div className="w-72 shrink-0 flex flex-col gap-5">

                    {/* Card 1 — Your Model */}
                    <div style={{ background: '#0B0B0B', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: 3, background: '#E63412' }} />
                      <div className="p-5">
                        {userCommunityModel ? (() => {
                          const shortName = userCommunityModel.replace(/^(Infinity|Imagine|Inspire) Series /, '$1 ')
                          const productUrl = MODEL_PRODUCT_URLS[userCommunityModel] ?? 'https://www.rodgersinstruments.com/'
                          return (
                            <>
                              <p className="text-[9px] uppercase font-bold mb-1" style={{ color: '#E63412', letterSpacing: '0.3em' }}>Your Rodgers</p>
                              <p className="text-base font-bold uppercase text-white mb-4" style={{ letterSpacing: '0.1em', lineHeight: 1.2 }}>{shortName}</p>
                              <button onClick={() => setCommunityScope('mymodel')} className="btn w-full justify-center mb-3" style={{ fontSize: 10, padding: '10px 0 9px' }}>View Community Activity</button>
                              <a
                                href={productUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-1.5 w-full font-bold uppercase transition-opacity hover:opacity-80"
                                style={{ fontSize: 10, color: '#aaa', letterSpacing: '0.16em', textDecoration: 'none', padding: '8px 0' }}
                              >
                                Learn more about the {shortName}
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                              </a>
                            </>
                          )
                        })() : (
                          <>
                            <p className="text-base font-bold uppercase text-white mb-2" style={{ letterSpacing: '0.12em' }}>Choose Your Model</p>
                            <p className="text-[11px] mb-4" style={{ color: '#737373' }}>Select your Rodgers to see model-specific community activity.</p>
                            <button onClick={() => setActiveTab('discover')} className="btn w-full justify-center" style={{ fontSize: 10, padding: '10px 0 9px' }}>Find My Rodgers Organ</button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Card 2 — Upcoming Live Session */}
                    {(() => {
                      const next = UPCOMING_LIVE[0]
                      const artistMember = MEMBERS[next.artist]
                      return (
                        <div style={{ background: '#fff', border: '1px solid #D8D4CC', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: 2, background: '#E63412' }} />
                          <div className="p-4">
                            <p className="text-[9px] font-bold uppercase mb-3" style={{ color: '#E63412', letterSpacing: '0.25em' }}>Upcoming Live Session</p>
                            <p className="text-[13px] font-bold uppercase mb-3" style={{ color: '#0B0B0B', letterSpacing: '0.1em', lineHeight: 1.3 }}>{next.title}</p>
                            <div className="flex items-center gap-3 mb-3">
                              {artistMember?.avatar
                                ? <img src={artistMember.avatar} alt={next.artist} className="w-9 h-9 rounded-full object-cover shrink-0" style={{ border: '2px solid #E63412' }} />
                                : <div className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center font-bold text-white text-sm" style={{ background: '#E63412' }}>{next.artist.charAt(0)}</div>
                              }
                              <div>
                                <p className="text-[11px] font-semibold" style={{ color: '#0B0B0B' }}>with {next.artist}</p>
                                <p className="text-[9px]" style={{ color: '#737373' }}>Featuring the {next.model}</p>
                              </div>
                            </div>
                            <p className="text-[10px] mb-1" style={{ color: '#737373' }}>{next.date}</p>
                            <p className="text-[10px] mb-3" style={{ color: '#B0AAA0' }}>{next.rsvp} attending</p>
                            <button onClick={() => setCommunitySubTab('live')} className="btn w-full justify-center" style={{ fontSize: 10, padding: '9px 0 8px' }}>RSVP</button>
                          </div>
                        </div>
                      )
                    })()}

                    {/* Card 3 — Organists with Your Model */}
                    <div style={{ background: '#fff', border: '1px solid #D8D4CC', borderRadius: 4, overflow: 'hidden' }}>
                      <div className="px-4 pt-4 pb-3 border-b" style={{ borderColor: '#F0EDE8' }}>
                        <p className="text-[10px] uppercase font-bold" style={{ color: '#0B0B0B', letterSpacing: '0.2em' }}>{userCommunityModel ?? 'Rodgers'} Organists to Follow</p>
                      </div>
                      <div className="divide-y" style={{ borderColor: '#F0EDE8' }}>
                        {SUGGESTED.slice(0, 3).map(m => (
                          <div key={m.name} className="flex items-center gap-3 px-4 py-3">
                            {m.avatar
                              ? <img src={m.avatar} alt={m.name} className="w-10 h-10 rounded-full object-cover shrink-0" style={{ border: '1.5px solid #D8D4CC' }} />
                              : <div className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-xs font-bold text-white" style={{ background: '#E63412' }}>{m.name.charAt(0)}</div>
                            }
                            <div className="flex-1 min-w-0">
                              <button onClick={() => setViewingMember(m.name)} className="text-[11px] font-bold uppercase hover:text-[#E63412] transition-colors text-left leading-tight" style={{ letterSpacing: '0.08em', color: '#0B0B0B' }}>{m.name}</button>
                              {m.musicalRole && <p className="text-[9px]" style={{ color: '#737373' }}>{m.musicalRole}</p>}
                            </div>
                            <button
                              onClick={() => setFollowing(prev => { const n = new Set(prev); n.has(m.name) ? n.delete(m.name) : n.add(m.name); return n })}
                              className="text-[9px] font-bold uppercase px-2.5 py-1.5 shrink-0 transition-all"
                              style={{ letterSpacing: '0.1em', borderRadius: 2, background: following.has(m.name) ? 'transparent' : '#E63412', color: following.has(m.name) ? '#B0AAA0' : '#fff', border: following.has(m.name) ? '1px solid #D8D4CC' : '1px solid transparent' }}
                            >{following.has(m.name) ? 'Following ✓' : 'Follow'}</button>
                          </div>
                        ))}
                      </div>
                      <div className="px-4 py-2 border-t" style={{ borderColor: '#F0EDE8' }}>
                        <button onClick={() => setCommunitySubTab('members')} className="text-[9px] font-semibold uppercase" style={{ color: '#E63412', letterSpacing: '0.15em' }}>View All {userCommunityModel ?? 'Rodgers'} Community →</button>
                      </div>
                    </div>

                    {/* Card 4 — Active Discussions */}
                    <div style={{ background: '#fff', border: '1px solid #D8D4CC', borderRadius: 4, overflow: 'hidden' }}>
                      <div className="px-4 pt-4 pb-3 border-b" style={{ borderColor: '#F0EDE8' }}>
                        <p className="text-[10px] uppercase font-bold" style={{ color: '#0B0B0B', letterSpacing: '0.2em' }}>Popular in the {userCommunityModel ?? 'Rodgers'} Community</p>
                      </div>
                      <div className="divide-y" style={{ borderColor: '#F0EDE8' }}>
                        {[
                          { title: 'Favorite registrations for orchestral pieces', tag: userCommunityModel ?? 'All Models', replies: 12 },
                          { title: 'Balancing expression settings', tag: userCommunityModel ?? 'All Models', replies: 8 },
                          { title: 'Music suggestions for Advent', tag: 'Worship', replies: 6 },
                        ].map(d => (
                          <button key={d.title} onClick={() => setCommunitySubTab('discussions')} className="w-full text-left px-4 py-3 group transition-colors hover:bg-[#FAFAF8]">
                            <p className="text-[11px] font-semibold mb-1.5 group-hover:text-[#E63412] transition-colors" style={{ color: '#0B0B0B', lineHeight: 1.35 }}>{d.title}</p>
                            <div className="flex items-center gap-2">
                              <span className="text-[8px] font-bold uppercase px-1.5 py-0.5" style={{ background: '#F5F3EF', color: '#737373', border: '1px solid #D8D4CC', letterSpacing: '0.1em', borderRadius: 2 }}>{d.tag}</span>
                              <span className="text-[9px]" style={{ color: '#B0AAA0' }}>{d.replies} replies</span>
                            </div>
                          </button>
                        ))}
                      </div>
                      <div className="px-4 py-2 border-t" style={{ borderColor: '#F0EDE8' }}>
                        <button onClick={() => setCommunitySubTab('discussions')} className="text-[9px] font-semibold uppercase" style={{ color: '#E63412', letterSpacing: '0.15em' }}>View All Discussions →</button>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            )
          })()}

          {/* ── DISCUSSIONS ── */}
          {communitySubTab === 'discussions' && (() => {
            const profileModel2 = (() => {
              const inst = (profile?.instruments ?? []).find(i => i.primary) ?? (profile?.instruments ?? [])[0]
              if (inst) return `${inst.series} ${inst.model}`
              return (profile?.organModel && profile.organModel !== 'Not specified') ? profile.organModel : null
            })()
            const userDiscModel = profileModel2 ?? selectedShowroomModel
            const CATEGORIES = [
              { name: 'Music & Registrations', desc: 'Share registrations, arrangements and repertoire', posts: 142, icon: '♪' },
              { name: 'Model Help', desc: 'Ask questions about a specific Rodgers model', posts: 87, icon: '?' },
              { name: 'Performance Feedback', desc: 'Share recordings and request constructive feedback', posts: 54, icon: '▶' },
              { name: 'Worship Planning', desc: 'Discuss seasonal music and service planning', posts: 68, icon: '✝' },
              { name: 'Playing Techniques', desc: 'Exchange practice and performance advice', posts: 93, icon: '♫' },
              { name: 'Events & Meetups', desc: 'Promote concerts, workshops and organ events', posts: 31, icon: '📅' },
              { name: 'Rodgers News', desc: 'Official product, software and community updates', posts: 24, icon: 'R' },
            ]
            const FEATURED_DISCUSSIONS = [
              { title: 'Best registrations for BWV 565 on the Imagine series?', category: 'Music & Registrations', author: 'Dan Miller', model: 'Infinity 367', replies: 14, solved: true, time: '3 hours ago' },
              { title: 'How do I update firmware on the Inspire 352?', category: 'Model Help', author: 'Anna Vogel', model: 'Inspire 352', replies: 6, solved: true, time: '1 day ago' },
              { title: 'Advent & Christmas repertoire ideas — what are you playing?', category: 'Worship Planning', author: 'Catherine Reinhardt', model: 'Inspire 352', replies: 22, solved: false, time: '2 days ago' },
              { title: 'Heel-toe independence exercises that actually work', category: 'Playing Techniques', author: 'Stacy Piontek', model: 'Imagine 239', replies: 19, solved: false, time: '3 days ago' },
            ]
            return (
              <div className="max-w-6xl mx-auto px-6 py-8">
                <div className="flex gap-8 items-start">
                  <div className="flex-1 min-w-0">
                    {/* Model board */}
                    {userDiscModel && (
                      <div className="mb-6" style={{ background: '#0B0B0B', border: '2px solid #E63412' }}>
                        <div className="p-5 flex items-center justify-between">
                          <div>
                            <p className="text-[9px] uppercase font-semibold mb-1" style={{ color: '#E63412', letterSpacing: '0.25em' }}>Your Model Board</p>
                            <p className="text-base font-bold uppercase text-white" style={{ letterSpacing: '0.15em' }}>{userDiscModel} Owners</p>
                            <p className="text-[11px] mt-0.5" style={{ color: '#555' }}>18 members · 12 active discussions</p>
                          </div>
                          <button className="btn text-[10px]" style={{ padding: '9px 18px 8px' }}>View Board →</button>
                        </div>
                      </div>
                    )}
                    {/* Featured discussions */}
                    <div className="mb-6">
                      <p className="text-[10px] uppercase font-bold mb-3" style={{ color: '#737373', letterSpacing: '0.25em' }}>Active Discussions</p>
                      <div className="flex flex-col gap-2">
                        {FEATURED_DISCUSSIONS.map(d => (
                          <div key={d.title} className="flex items-start gap-4 p-4 transition-all hover:border-[#D8D4CC] cursor-pointer" style={{ background: '#fff', border: '1px solid #D8D4CC' }}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5" style={{ background: '#F5F3EF', color: '#737373', letterSpacing: '0.12em', borderRadius: 2 }}>{d.category}</span>
                                {d.solved && <span className="text-[9px] uppercase font-semibold" style={{ color: '#47A855', letterSpacing: '0.1em' }}>✓ Answered</span>}
                              </div>
                              <p className="text-sm font-semibold mb-1" style={{ color: '#0B0B0B', lineHeight: 1.4 }}>{d.title}</p>
                              <div className="flex items-center gap-3">
                                <button onClick={() => setViewingMember(d.author)} className="text-[10px] font-semibold uppercase hover:text-[#E63412] transition-colors" style={{ color: '#737373', letterSpacing: '0.08em' }}>{d.author}</button>
                                <span className="text-[9px] px-1.5 py-0.5 font-semibold uppercase" style={{ background: '#0B0B0B', color: '#fff', borderRadius: 2, letterSpacing: '0.1em' }}>{d.model}</span>
                                <span className="text-[10px]" style={{ color: '#B0AAA0' }}>{d.time}</span>
                              </div>
                            </div>
                            <div className="text-center shrink-0">
                              <p className="text-base font-bold" style={{ color: '#0B0B0B' }}>{d.replies}</p>
                              <p className="text-[9px] uppercase" style={{ color: '#B0AAA0', letterSpacing: '0.1em' }}>Replies</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Category sidebar */}
                  <div className="w-72 shrink-0">
                    <p className="text-[10px] uppercase font-bold mb-3" style={{ color: '#737373', letterSpacing: '0.25em' }}>Categories</p>
                    <div className="flex flex-col gap-2">
                      {CATEGORIES.map(cat => (
                        <button key={cat.name} className="flex items-start gap-3 p-3 text-left transition-all group" style={{ background: '#fff', border: '1px solid #D8D4CC' }}>
                          <div className="w-7 h-7 shrink-0 flex items-center justify-center text-[11px] font-bold" style={{ background: '#F5F3EF', color: '#737373', borderRadius: 2 }}>{cat.icon}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-bold uppercase group-hover:text-[#E63412] transition-colors" style={{ letterSpacing: '0.1em', color: '#0B0B0B' }}>{cat.name}</p>
                            <p className="text-[9px]" style={{ color: '#B0AAA0' }}>{cat.posts} posts</p>
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="mt-4">
                      <button onClick={() => requireProfile('share', () => {})} className="btn w-full justify-center" style={{ fontSize: 10, padding: '10px 0 9px' }}>+ Start a Discussion</button>
                    </div>
                  </div>
                </div>
              </div>
            )
          })()}

          {/* ── MEMBERS ── */}
          {communitySubTab === 'members' && (() => {
            const allMembers = Object.values(MEMBERS)
            const profileModelM = (() => {
              const inst = (profile?.instruments ?? []).find(i => i.primary) ?? (profile?.instruments ?? [])[0]
              if (inst) return `${inst.series} ${inst.model}`
              return (profile?.organModel && profile.organModel !== 'Not specified') ? profile.organModel : null
            })()
            const userMembersModel = profileModelM ?? selectedShowroomModel
            const FILTER_OPTS = [
              { key: 'mymodel', label: 'My Model' },
              { key: 'mostactive', label: 'Most Active' },
              { key: 'new', label: 'New Members' },
              { key: 'artists', label: 'Artists' },
              { key: 'all', label: 'All Members' },
            ]
            const filteredMembers = memberSearch.trim()
              ? allMembers.filter(m =>
                  m.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
                  (m.location ?? '').toLowerCase().includes(memberSearch.toLowerCase()) ||
                  (m.organModel ?? '').toLowerCase().includes(memberSearch.toLowerCase())
                )
              : allMembers

            return (
              <div className="max-w-6xl mx-auto px-6 py-8">
                {/* Recommendation block */}
                {userMembersModel && (
                  <div className="mb-8" style={{ background: '#0B0B0B', border: '1px solid #1e1e1e' }}>
                    <div style={{ height: 3, background: '#E63412' }} />
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="text-[9px] uppercase font-semibold mb-1" style={{ color: '#E63412', letterSpacing: '0.25em' }}>Organists You May Want to Follow</p>
                          <p className="text-sm font-bold uppercase text-white" style={{ letterSpacing: '0.12em' }}>These members play a {userMembersModel} and share music similar to yours.</p>
                        </div>
                        <button onClick={() => setMemberFilter('mymodel')} className="text-[10px] font-semibold uppercase shrink-0" style={{ color: '#E63412', letterSpacing: '0.15em' }}>See All →</button>
                      </div>
                      <div className="flex gap-4">
                        {allMembers.filter(m => !following.has(m.name)).slice(0, 3).map(m => (
                          <div key={m.name} className="flex-1 p-4" style={{ background: '#141414', border: '1px solid #222' }}>
                            <div className="flex items-center gap-3 mb-3">
                              {m.avatar
                                ? <img src={m.avatar} alt={m.name} className="w-10 h-10 rounded-full object-cover" style={{ border: '2px solid #E63412' }} />
                                : <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white" style={{ background: '#E63412' }}>{m.name.charAt(0)}</div>
                              }
                              <div>
                                <button onClick={() => setViewingMember(m.name)} className="text-[11px] font-bold uppercase text-white hover:text-[#E63412] transition-colors text-left" style={{ letterSpacing: '0.1em' }}>{m.name}</button>
                                <p className="text-[9px]" style={{ color: '#555' }}>{m.location ?? ''}</p>
                              </div>
                            </div>
                            <p className="text-[9px] uppercase mb-3" style={{ color: '#737373', letterSpacing: '0.1em' }}>{rodgersModelLabel(m.organModel)}</p>
                            <button
                              onClick={() => setFollowing(prev => { const n = new Set(prev); n.has(m.name) ? n.delete(m.name) : n.add(m.name); return n })}
                              className="w-full py-1.5 text-[9px] font-bold uppercase transition-all"
                              style={{ letterSpacing: '0.12em', borderRadius: 2, background: following.has(m.name) ? 'transparent' : '#E63412', color: following.has(m.name) ? '#555' : '#fff', border: following.has(m.name) ? '1px solid #333' : '1px solid transparent' }}
                            >{following.has(m.name) ? 'Following ✓' : '+ Follow'}</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Filters + search */}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex gap-0 border-b" style={{ borderColor: '#D8D4CC' }}>
                    {FILTER_OPTS.map(f => (
                      <button
                        key={f.key}
                        onClick={() => setMemberFilter(f.key)}
                        className="px-4 py-2.5 text-[10px] font-semibold uppercase transition-colors"
                        style={{
                          letterSpacing: '0.18em',
                          color: memberFilter === f.key ? '#0B0B0B' : '#737373',
                          borderBottom: memberFilter === f.key ? '2px solid #E63412' : '2px solid transparent',
                          marginBottom: -1,
                        }}
                      >{f.label}</button>
                    ))}
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #D8D4CC' }}>
                    <input
                      value={memberSearch}
                      onChange={e => setMemberSearch(e.target.value)}
                      placeholder="Search members…"
                      className="px-4 py-2.5 text-xs outline-none"
                      style={{ background: 'transparent', color: '#0B0B0B', width: 200, letterSpacing: '0.05em' }}
                    />
                  </div>
                </div>

                {/* Member grid (richer cards) */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredMembers.map(m => {
                    const roleDisplay = m.musicalRole || null
                    return (
                      <div key={m.name} className="flex flex-col" style={{ background: '#fff', border: '1px solid #D8D4CC' }}>
                        <div style={{ height: 2, background: '#E63412', flexShrink: 0 }} />
                        <div className="p-5 flex flex-col h-full">
                          <div className="flex items-start gap-3 mb-4">
                            {m.avatar
                              ? <img src={m.avatar} alt={m.name} className="w-14 h-14 rounded-full object-cover shrink-0" style={{ border: '2px solid #D8D4CC' }} />
                              : <div className="w-14 h-14 rounded-full shrink-0 flex items-center justify-center text-xl font-bold text-white" style={{ background: '#E63412' }}>{m.name.charAt(0)}</div>
                            }
                            <div className="flex-1 min-w-0 pt-1">
                              <button onClick={() => setViewingMember(m.name)} className="text-left block font-bold uppercase text-xs hover:text-[#E63412] transition-colors leading-snug mb-0.5" style={{ letterSpacing: '0.1em', color: '#0B0B0B' }}>{m.name}</button>
                              {m.location && <p className="text-[9px]" style={{ color: '#B0AAA0' }}>{m.location}</p>}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-1 mb-1 flex-wrap">
                              <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5" style={{ background: '#0B0B0B', color: '#fff', letterSpacing: '0.1em', borderRadius: 2 }}>{rodgersModelLabel(m.organModel)}</span>
                              {m.monthlyGuest && <span className="text-[8px] font-semibold uppercase px-1.5 py-0.5" style={{ color: '#E63412', border: '1px solid rgba(230,52,18,0.3)', letterSpacing: '0.08em', borderRadius: 2 }}>Guest Artist</span>}
                            </div>
                            {roleDisplay && <p className="text-[10px] mt-1" style={{ color: '#737373' }}>{roleDisplay}</p>}
                          </div>
                          <div className="flex items-center gap-2 mt-4 pt-3 border-t" style={{ borderColor: '#F0EDE8' }}>
                            <div className="flex-1 text-center">
                              <p className="text-xs font-bold" style={{ color: '#0B0B0B' }}>—</p>
                              <p className="text-[8px] uppercase" style={{ color: '#B0AAA0', letterSpacing: '0.1em' }}>Pieces</p>
                            </div>
                            <div style={{ width: 1, height: 24, background: '#F0EDE8' }} />
                            <div className="flex-1 text-center">
                              <p className="text-xs font-bold" style={{ color: '#0B0B0B' }}>—</p>
                              <p className="text-[8px] uppercase" style={{ color: '#B0AAA0', letterSpacing: '0.1em' }}>Followers</p>
                            </div>
                            <div style={{ width: 1, height: 24, background: '#F0EDE8' }} />
                            <button
                              onClick={() => setFollowing(prev => { const n = new Set(prev); n.has(m.name) ? n.delete(m.name) : n.add(m.name); return n })}
                              className="flex-1 py-1 text-[9px] font-bold uppercase transition-all"
                              style={{ letterSpacing: '0.1em', borderRadius: 2, background: following.has(m.name) ? 'transparent' : '#E63412', color: following.has(m.name) ? '#B0AAA0' : '#fff', border: following.has(m.name) ? '1px solid #D8D4CC' : '1px solid transparent' }}
                            >{following.has(m.name) ? '✓' : 'Follow'}</button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  {filteredMembers.length === 0 && (
                    <div className="col-span-full py-16 text-center">
                      <p className="font-serif text-base italic" style={{ color: '#B0AAA0' }}>No members match your search.</p>
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-center mt-6" style={{ color: '#B0AAA0', letterSpacing: '0.1em' }}>{allMembers.length} members</p>
              </div>
            )
          })()}

          {/* ── MONTHLY CHALLENGE ── */}
          {communitySubTab === 'challenge' && (
            <div className="max-w-6xl mx-auto px-6 py-8">
              {/* Challenge header */}
              <div className="mb-8 flex gap-8 items-start">
                <div className="flex-1">
                  <div className="p-8" style={{ background: '#0B0B0B', border: '1px solid #1e1e1e' }}>
                    <div style={{ height: 3, background: '#E63412', marginBottom: 24 }} />
                    <p className="text-[10px] uppercase font-semibold mb-2" style={{ color: '#E63412', letterSpacing: '0.35em' }}>September Challenge</p>
                    <h2 className="text-2xl font-bold uppercase text-white mb-2" style={{ letterSpacing: '0.15em', lineHeight: 1.1 }}>Perform the Monthly Selection</h2>
                    <p className="text-sm mb-6" style={{ color: '#737373', lineHeight: 1.7 }}>Record Toccata in D Minor, BWV 565 on your Rodgers organ and share your performance with the community.</p>
                    <div className="flex gap-6 mb-6">
                      {[
                        { n: 18, label: 'Entries' },
                        { n: 12, label: 'Days Left' },
                        { n: 94, label: 'Votes Cast' },
                      ].map(s => (
                        <div key={s.label}>
                          <p className="text-2xl font-bold text-white">{s.n}</p>
                          <p className="text-[10px] uppercase" style={{ color: '#555', letterSpacing: '0.2em' }}>{s.label}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => requireProfile('share', () => setShowUpload(true))} className="btn" style={{ fontSize: 11 }}>Upload My Performance</button>
                      <button className="btn btn-outline" style={{ fontSize: 11 }}>Add to Calendar</button>
                    </div>
                  </div>
                </div>
                {/* Previous winner */}
                <div className="w-72 shrink-0" style={{ background: '#fff', border: '1px solid #D8D4CC' }}>
                  <div style={{ height: 2, background: '#E63412' }} />
                  <div className="p-5">
                    <p className="text-[9px] uppercase font-semibold mb-3" style={{ color: '#E63412', letterSpacing: '0.25em' }}>August Winner</p>
                    {(() => { const m = MEMBERS['Stacy Piontek']; return m ? (
                      <div className="flex items-center gap-3 mb-3">
                        {m.avatar ? <img src={m.avatar} alt={m.name} className="w-12 h-12 rounded-full object-cover" style={{ border: '2px solid #E63412' }} /> : <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white" style={{ background: '#E63412' }}>{m.name.charAt(0)}</div>}
                        <div>
                          <button onClick={() => setViewingMember(m.name)} className="text-sm font-bold uppercase hover:text-[#E63412] transition-colors" style={{ letterSpacing: '0.1em', color: '#0B0B0B' }}>{m.name}</button>
                          <p className="text-[10px]" style={{ color: '#737373' }}>{rodgersModelLabel(m.organModel)}</p>
                        </div>
                      </div>
                    ) : null })()}
                    <p className="text-[11px] font-semibold mb-0.5" style={{ color: '#0B0B0B' }}>{"Widor's Toccata, Op. 42"}</p>
                    <p className="text-[10px] mb-3" style={{ color: '#737373' }}>4 votes · Staff Pick</p>
                    <div className="flex gap-2">
                      {(['Inspiring', 'Beautiful registration', 'Excellent performance'] as const).map(v => (
                        <span key={v} className="text-[8px] px-1.5 py-0.5 font-semibold uppercase" style={{ letterSpacing: '0.08em', color: '#E63412', border: '1px solid rgba(230,52,18,0.3)', borderRadius: 2 }}>{v}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Entries */}
              <p className="text-[10px] uppercase font-bold mb-4" style={{ color: '#737373', letterSpacing: '0.25em' }}>September Entries — Vote for Your Favorites</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.values(MEMBERS).slice(0, 6).map((m, i) => (
                  <div key={m.name} style={{ background: '#fff', border: '1px solid #D8D4CC' }}>
                    <div style={{ height: 2, background: i < 2 ? '#E63412' : '#E8E4DF' }} />
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        {m.avatar
                          ? <img src={m.avatar} alt={m.name} className="w-9 h-9 rounded-full object-cover" style={{ border: '1.5px solid #D8D4CC' }} />
                          : <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-white text-sm" style={{ background: '#E63412' }}>{m.name.charAt(0)}</div>
                        }
                        <div>
                          <button onClick={() => setViewingMember(m.name)} className="text-[11px] font-bold uppercase hover:text-[#E63412] transition-colors" style={{ letterSpacing: '0.1em', color: '#0B0B0B' }}>{m.name}</button>
                          <p className="text-[9px]" style={{ color: '#B0AAA0' }}>{rodgersModelLabel(m.organModel)}</p>
                        </div>
                      </div>
                      <p className="text-[11px] font-semibold mb-3" style={{ color: '#0B0B0B' }}>Toccata in D Minor, BWV 565</p>
                      <div className="flex items-center gap-2 mb-3">
                        <button className="flex items-center gap-1.5 px-3 py-2 text-[9px] font-bold uppercase" style={{ background: '#E63412', color: '#fff', letterSpacing: '0.12em', borderRadius: 2 }}>▶ Listen</button>
                        <span className="text-[10px]" style={{ color: '#B0AAA0' }}>{(i + 1) * 3 + 2} plays</span>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {(['Inspiring', 'Beautiful registration', 'Excellent performance'] as const).map(v => (
                          <button key={v} className="text-[8px] px-2 py-1 font-semibold uppercase transition-all hover:border-[#E63412] hover:text-[#E63412]" style={{ letterSpacing: '0.08em', color: '#737373', border: '1px solid #D8D4CC', borderRadius: 2 }}>{v}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Archive */}
              <div className="mt-12">
                <p className="text-[10px] uppercase font-bold mb-4" style={{ color: '#737373', letterSpacing: '0.25em' }}>Past Challenges</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    { month: 'August 2026', title: "Widor's Toccata, Op. 42", composer: 'Charles-Marie Widor', guest: 'Stacy Piontek', entries: 4 },
                    { month: 'July 2026', title: 'Lift Every Voice and Sing', composer: 'J.W. Johnson', guest: 'Tim Eastbrower', entries: 7 },
                    { month: 'June 2026', title: 'Pièce Héroïque', composer: 'César Franck', guest: 'Hector Olivera', entries: 5 },
                    { month: 'May 2026', title: 'Psalm 150', composer: 'César Franck', guest: 'Monty Musgrave', entries: 9 },
                    { month: 'April 2026', title: 'Prelude & Fugue in E minor', composer: 'J.S. Bach', guest: 'Dan Miller', entries: 11 },
                    { month: 'March 2026', title: 'Sortie in E flat major', composer: 'Lefébure-Wély', guest: 'Jan Kraybill', entries: 8 },
                  ].map(item => (
                    <div key={item.month} className="flex items-start gap-3 p-4" style={{ background: '#fff', border: '1px solid #D8D4CC' }}>
                      <div className="flex-1 min-w-0">
                        <p className="text-[9px] uppercase font-bold mb-0.5" style={{ color: '#E63412', letterSpacing: '0.2em' }}>{item.month}</p>
                        <p className="text-[11px] font-semibold mb-0.5" style={{ color: '#0B0B0B' }}>{item.title}</p>
                        <p className="text-[10px]" style={{ color: '#737373' }}>{item.composer} · Guest: {item.guest}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold" style={{ color: '#0B0B0B' }}>{item.entries}</p>
                        <p className="text-[9px] uppercase" style={{ color: '#B0AAA0', letterSpacing: '0.1em' }}>Entries</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── LIVE SESSIONS ── */}
          {communitySubTab === 'live' && (
            <div className="max-w-6xl mx-auto px-6 py-8">
              {/* Upcoming */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-5">
                  <p className="text-[10px] uppercase font-bold" style={{ color: '#737373', letterSpacing: '0.25em' }}>Upcoming Sessions</p>
                  <button onClick={() => requireProfile('share', () => {})} className="text-[10px] font-semibold uppercase" style={{ color: '#E63412', letterSpacing: '0.15em' }}>+ Propose a Session</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {[
                    { date: 'SEPTEMBER 24, 2026', time: '4:00 PM PT', title: 'Creating Orchestral Registrations', artist: 'Dan Miller', model: 'Infinity 489', topic: 'Learn how Dan builds full orchestral textures using the 489 tone generation system.', rsvp: 47, upcoming: true },
                    { date: 'OCTOBER 8, 2026', time: '6:00 PM ET', title: 'French Romantic Repertoire for Worship', artist: 'Hector Olivera', model: 'Imagine 359', topic: 'Hector shares his approach to Franck, Widor and Vierne for the liturgical calendar.', rsvp: 29, upcoming: true },
                    { date: 'OCTOBER 22, 2026', time: '5:00 PM CT', title: 'Hymn Arrangements for the Digital Organ', artist: 'Stacy Piontek', model: 'Imagine 239', topic: 'Practical arranging techniques for congregational accompaniment on any Rodgers model.', rsvp: 18, upcoming: true },
                    { date: 'NOVEMBER 5, 2026', time: '7:00 PM ET', title: 'Advent & Christmas Registration Workshop', artist: 'Jan Kraybill', model: 'Infinity 367', topic: 'Build a complete seasonal registration set in one focused session.', rsvp: 11, upcoming: false },
                  ].map(s => {
                    const artist = Object.values(MEMBERS).find(m => m.name === s.artist)
                    return (
                      <div key={s.title} style={{ background: '#fff', border: '1px solid #D8D4CC' }}>
                        <div style={{ height: 3, background: s.upcoming ? '#E63412' : '#D8D4CC' }} />
                        <div className="p-6">
                          <p className="text-[9px] uppercase font-bold mb-1" style={{ color: '#E63412', letterSpacing: '0.25em' }}>Live · {s.date} · {s.time}</p>
                          <h3 className="text-base font-bold uppercase mb-2" style={{ letterSpacing: '0.12em', color: '#0B0B0B', lineHeight: 1.2 }}>{s.title}</h3>
                          <div className="flex items-center gap-3 mb-3">
                            {artist?.avatar
                              ? <img src={artist.avatar} alt={s.artist} className="w-8 h-8 rounded-full object-cover" style={{ border: '1.5px solid #E63412' }} />
                              : <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: '#E63412' }}>{s.artist.charAt(0)}</div>
                            }
                            <div>
                              <button onClick={() => setViewingMember(s.artist)} className="text-[11px] font-bold uppercase hover:text-[#E63412] transition-colors" style={{ letterSpacing: '0.1em', color: '#0B0B0B' }}>{s.artist}</button>
                              <p className="text-[9px]" style={{ color: '#737373' }}>Featuring the {s.model}</p>
                            </div>
                          </div>
                          <p className="text-[11px] mb-4" style={{ color: '#737373', lineHeight: 1.6 }}>{s.topic}</p>
                          <div className="flex items-center gap-3">
                            <button className="btn" style={{ fontSize: 10, padding: '9px 18px 8px' }}>RSVP</button>
                            <button className="btn btn-outlined" style={{ fontSize: 10, padding: '9px 18px 8px' }}>Add to Calendar</button>
                            <span className="text-[10px] ml-auto" style={{ color: '#B0AAA0' }}>{s.rsvp} going</span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Past sessions */}
              <div>
                <p className="text-[10px] uppercase font-bold mb-4" style={{ color: '#737373', letterSpacing: '0.25em' }}>Past Sessions — Watch Replays</p>
                <div className="flex flex-col gap-3">
                  {[
                    { date: 'AUGUST 14, 2026', title: 'Advanced Piston Programming for the Infinity 367', artist: 'Dan Miller', model: 'Infinity 367', views: 312, questions: 18 },
                    { date: 'JULY 30, 2026', title: 'Building a Romantic Registration from Scratch', artist: 'Hector Olivera', model: 'Imagine 359', views: 247, questions: 22 },
                    { date: 'JULY 10, 2026', title: 'Organ Maintenance and Care for the Home Organist', artist: 'Tedde Gibson', model: 'Inspire 352', views: 189, questions: 11 },
                  ].map(s => {
                    const artist = Object.values(MEMBERS).find(m => m.name === s.artist)
                    return (
                      <div key={s.title} className="flex items-center gap-5 p-5" style={{ background: '#fff', border: '1px solid #D8D4CC' }}>
                        <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: '#F5F3EF', borderRadius: 3 }}>
                          <span style={{ color: '#E63412', fontSize: 16 }}>▶</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[9px] uppercase font-semibold" style={{ color: '#B0AAA0', letterSpacing: '0.15em' }}>{s.date}</span>
                            <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5" style={{ background: '#0B0B0B', color: '#fff', letterSpacing: '0.1em', borderRadius: 2 }}>{s.model}</span>
                          </div>
                          <p className="text-sm font-semibold" style={{ color: '#0B0B0B', lineHeight: 1.4 }}>{s.title}</p>
                          <p className="text-[10px]" style={{ color: '#737373' }}>with {s.artist}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-[10px]" style={{ color: '#B0AAA0' }}>{s.views} views</p>
                          <p className="text-[10px]" style={{ color: '#B0AAA0' }}>{s.questions} Q&A</p>
                        </div>
                        <button className="btn btn-outlined shrink-0" style={{ fontSize: 10, padding: '8px 16px 7px', color: '#0B0B0B', borderColor: '#D8D4CC' }}>Watch Replay</button>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

        </div>
        </Experimental>
      )}

      {/* ── MONTHLY TAB ── */}
      {activeTab === 'monthly' && (() => {
        const MONTHLY = {
          month: 'September 2026',
          title: "Pachelbel's Toccata in E Minor",
          composer: 'Johann Pachelbel',
          description: "This month's selection is Pachelbel's Toccata in E Minor — a stunning, less-familiar masterwork that showcases the organ's expressive range. We invite every member to listen, learn, and share their own interpretation.",
          curatorIntro: "I chose this Toccata because it sits in that sweet spot between accessibility and challenge. It's not the Canon — most listeners won't recognize it — but it has the same Pachelbel clarity. The Infinity 367's principal chorus at 8' and 4' makes it sing.",
          challenge: "Record your own performance and upload it by September 30th to be featured in the Monthly Showcase.",
          deadline: 'September 30, 2026',
          curator: 'Tim Eastbrower',
          curatorAvatar: danMillerPhoto,
          tags: ['Baroque', 'Pachelbel', 'Toccata'],
          difficulty: 'Intermediate',
          duration: 'approx. 4–6 min',
          liturgicalSeason: 'Ordinary Time · Pentecost',
          recommendedRegistration: 'Principal 8\', Octave 4\', Mixture · no reeds',
          compatibleModels: ['Infinity 367', 'Infinity 489', 'Imagine 359'],
          sheetUrl: 'https://imslp.org/wiki/Toccata_in_E_minor_(Pachelbel,_Johann)',
          downloads: [
            { label: 'Listen to Performance', url: '#', icon: '▶', note: 'MP3 · Tim Eastbrower on Infinity 367', action: 'listen' },
            { label: 'Download Sheet Music', url: 'https://imslp.org/wiki/Toccata_in_E_minor_(Pachelbel,_Johann)', icon: '📄', note: 'PDF via IMSLP · Free', action: 'download' },
            { label: 'Send MIDI to Rodgers Play', url: '#', icon: '🎹', note: 'Rodgers Play · .mid · Infinity 367', action: 'rodgersplay' },
            { label: 'Get the Registration', url: '#', icon: '🎛', note: 'Principal 8\', Octave 4\', Mixture · .reg', action: 'download' },
          ],
          organ: 'Rodgers Infinity 367',
        }
        const curatorMember = MEMBERS[MONTHLY.curator]
        const ARCHIVE = [
          { month: 'September 2026', title: "Pachelbel's Toccata in E Minor", composer: 'Johann Pachelbel', guest: 'Tim Eastbrower', entries: 0, current: true },
          { month: 'August 2026', title: "Toccata in D minor, BWV 565", composer: 'J.S. Bach', guest: 'Dan Miller', entries: 0, current: false },
          { month: 'July 2026', title: "Widor's Toccata, Op. 42", composer: 'Charles-Marie Widor', guest: 'Stacy Piontek', entries: 4, current: false },
          { month: 'June 2026', title: 'Lift Every Voice and Sing', composer: 'James Weldon Johnson / J. Rosamond Johnson', guest: 'Tim Eastbrower', entries: 7, current: false },
          { month: 'May 2026', title: 'Pièce Héroïque', composer: 'César Franck', guest: 'Hector Olivera', entries: 5, current: false },
          { month: 'April 2026', title: 'Psalm 150', composer: 'César Franck', guest: 'Monty Musgrave', entries: 9, current: false },
          { month: 'March 2026', title: 'Prelude & Fugue in E minor, BWV 548', composer: 'J.S. Bach', guest: 'Dan Miller', entries: 11, current: false },
        ]
        return (
          <Experimental>
          <div style={{ background: '#EFEFEF' }}>
            {/* Hero strip */}
            <div style={{ background: '#E8E6E2', borderBottom: '3px solid #e63412' }}>
              <div className="max-w-6xl mx-auto px-6 py-10">
                {/* Branding bar */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="flex flex-col items-start">
                    <img src={rodgersPlayLogo} alt="Rodgers Play" style={{ height: 28, width: 'auto', objectFit: 'contain' }} />
                    <p style={{ fontSize: 8, color: '#9a9a9a', letterSpacing: '0.16em', textTransform: 'uppercase', marginTop: 3 }}>Playback Technology</p>
                  </div>
                  <div style={{ width: 1, height: 28, background: '#C8C4BF' }} />
                  <div>
                    <p style={{ fontSize: 10, color: '#e63412', letterSpacing: '0.3em', fontWeight: 700, textTransform: 'uppercase' }}>Monthly Musical Offering</p>
                    <p style={{ fontSize: 10, color: '#9a9a9a', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>{MONTHLY.month}</p>
                  </div>
                  <div className="flex-1 h-px hidden md:block" style={{ background: '#C8C4BF' }} />
                  <div className="hidden md:block px-3 py-1 font-bold uppercase" style={{ fontSize: 9, background: '#0B0B0B', color: '#fff', letterSpacing: '0.2em', borderRadius: 2 }}>
                    This Month's Selection
                  </div>
                </div>

                {/* Main grid */}
                <div className="grid md:grid-cols-5 gap-8 items-start">
                  {/* Left col: 3/5 — audio player + piece info */}
                  <div className="md:col-span-3">
                    {/* Large audio player area */}
                    <div className="relative mb-6 overflow-hidden" style={{ background: '#0B0B0B', borderRadius: 4, minHeight: 180 }}>
                      <img src={organConsolePhoto} alt="Rodgers organ" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                      <div className="relative p-6">
                        <p style={{ fontSize: 10, color: '#E63412', letterSpacing: '0.3em', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>September Selection</p>
                        <h3 className="font-bold text-white mb-1" style={{ fontSize: 'clamp(1.2rem, 2vw, 1.6rem)', textTransform: 'none', letterSpacing: '-0.01em', lineHeight: 1.2 }}>{MONTHLY.title}</h3>
                        <p className="mb-2" style={{ fontSize: 14, color: '#E63412' }}>{MONTHLY.composer}</p>
                        <p className="mb-5" style={{ fontSize: 13, color: '#888', lineHeight: 1.55 }}>Listen to the featured performance, download the music and experience it on your Rodgers with Rodgers Play.</p>
                        {/* Waveform visualization */}
                        <div className="flex items-center gap-0.5 mb-5 opacity-40">
                          {Array.from({ length: 60 }, (_, j) => (
                            <div key={j} style={{ width: 2, height: `${8 + Math.abs(Math.sin(j * 0.4) * Math.cos(j * 0.13)) * 32}px`, background: '#E63412', borderRadius: 1 }} />
                          ))}
                        </div>
                        {/* Player controls */}
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => { const f = files.find(isPlayableAudio); if (f) handlePlay(f) }}
                            className="flex items-center gap-3 btn"
                            style={{ fontSize: 11, padding: '12px 24px', letterSpacing: '0.18em', minHeight: 44 }}
                          >
                            <span style={{ fontSize: 11 }}>▶</span> Listen to Tim's Performance
                          </button>
                          <span style={{ fontSize: 11, color: '#555' }}>{MONTHLY.duration}</span>
                        </div>
                      </div>
                    </div>

                    {/* Tim's intro */}
                    <div className="mb-6 px-5 py-4" style={{ background: '#fff', border: '1px solid #D8D4CC', borderRadius: 4, borderLeft: '3px solid #E63412' }}>
                      <button className="flex items-center gap-3 mb-3 group w-full text-left" onClick={() => setViewingMember(MONTHLY.curator)}>
                        <img src={MONTHLY.curatorAvatar} alt={MONTHLY.curator} className="w-10 h-10 rounded-full object-cover shrink-0" style={{ border: '2px solid #e63412' }} />
                        <div>
                          <p className="font-bold uppercase group-hover:text-[#e63412] transition-colors" style={{ fontSize: 11, color: '#0B0B0B', letterSpacing: '0.14em' }}>{MONTHLY.curator}</p>
                          <p style={{ fontSize: 10, color: '#9a9a9a' }}>{curatorMember?.musicalRole || 'Guest Organist'}</p>
                        </div>
                      </button>
                      <p style={{ fontSize: 14, color: '#444', lineHeight: 1.65, fontStyle: 'italic' }}>"{MONTHLY.curatorIntro}"</p>
                    </div>

                    {/* Downloads */}
                    <div className="flex flex-col gap-2 mb-5">
                      {MONTHLY.downloads.map(dl => {
                        const isRodgersPlay = dl.action === 'rodgersplay'
                        const isListen = dl.action === 'listen'
                        return (
                        <a key={dl.label} href={dl.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 px-4 py-3 transition-all hover:shadow-sm"
                          style={{ background: isRodgersPlay ? 'rgba(124,58,237,0.04)' : '#fff', border: `1px solid ${isRodgersPlay ? 'rgba(124,58,237,0.3)' : '#D8D4CC'}`, textDecoration: 'none', borderRadius: 3 }}
                        >
                          {isRodgersPlay
                            ? <img src={rodgersPlayLogo} alt="Rodgers Play" style={{ height: 18, width: 'auto', objectFit: 'contain', flexShrink: 0 }} />
                            : <span style={{ fontSize: 16, flexShrink: 0 }}>{dl.icon}</span>
                          }
                          <div className="flex-1">
                            <p className="font-semibold" style={{ fontSize: 13, color: isRodgersPlay ? '#7C3AED' : '#0B0B0B' }}>{dl.label}</p>
                            <p style={{ fontSize: 10, color: '#9a9a9a' }}>{dl.note}</p>
                          </div>
                          {isRodgersPlay
                            ? <span className="font-bold uppercase" style={{ fontSize: 8, color: '#7C3AED', letterSpacing: '0.12em' }}>Send →</span>
                            : isListen
                              ? <span style={{ fontSize: 11, color: '#E63412' }}>▶</span>
                              : <span className="font-bold" style={{ fontSize: 11, color: '#e63412' }}>↓</span>
                          }
                        </a>
                        )
                      })}
                    </div>
                    <button
                      onClick={() => requireProfile('share', () => setShowUpload(true))}
                      className="btn w-full"
                      style={{ fontSize: 12, padding: '13px 24px', letterSpacing: '0.18em' }}
                    >
                      Share Your Performance
                    </button>
                  </div>

                  {/* Right col: 2/5 — metadata + challenge */}
                  <div className="md:col-span-2">
                    {/* Piece metadata */}
                    <div className="mb-5 px-5 py-5" style={{ background: '#fff', border: '1px solid #D8D4CC', borderRadius: 4 }}>
                      <p className="font-bold uppercase mb-4" style={{ fontSize: 9, color: '#9a9a9a', letterSpacing: '0.3em' }}>About this piece</p>
                      {[
                        { label: 'Difficulty', value: MONTHLY.difficulty },
                        { label: 'Performance length', value: MONTHLY.duration },
                        { label: 'Liturgical use', value: MONTHLY.liturgicalSeason },
                        { label: 'Recommended registration', value: MONTHLY.recommendedRegistration },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex justify-between items-start py-2" style={{ borderBottom: '1px solid #F0EDE8' }}>
                          <p style={{ fontSize: 11, color: '#9a9a9a', flexShrink: 0, marginRight: 12 }}>{label}</p>
                          <p className="font-semibold text-right" style={{ fontSize: 12, color: '#0B0B0B' }}>{value}</p>
                        </div>
                      ))}
                      <div className="pt-3">
                        <p style={{ fontSize: 11, color: '#9a9a9a', marginBottom: 6 }}>Compatible models</p>
                        <div className="flex flex-wrap gap-1">
                          {MONTHLY.compatibleModels.map(m => (
                            <span key={m} className="font-bold uppercase" style={{ fontSize: 8, background: '#F0EDE8', color: '#555', padding: '3px 6px', borderRadius: 2, letterSpacing: '0.1em' }}>{m}</span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Challenge */}
                    <div className="mb-4 px-5 py-5" style={{ background: 'rgba(230,52,18,0.05)', border: '1px solid rgba(230,52,18,0.18)', borderRadius: 4 }}>
                      <p className="font-bold uppercase mb-2" style={{ fontSize: 9, color: '#e63412', letterSpacing: '0.25em' }}>Monthly Challenge</p>
                      <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6 }}>{MONTHLY.challenge}</p>
                      <p className="font-bold mt-3" style={{ fontSize: 11, color: '#0B0B0B' }}>Deadline: <span style={{ color: '#e63412' }}>{MONTHLY.deadline}</span></p>
                    </div>

                    {/* Community submissions */}
                    <div className="px-5 py-5" style={{ background: '#fff', border: '1px solid #D8D4CC', borderRadius: 4 }}>
                      <p className="font-bold uppercase mb-1" style={{ fontSize: 9, color: '#9a9a9a', letterSpacing: '0.25em' }}>Community Submissions</p>
                      <p style={{ fontSize: 13, color: '#737373', lineHeight: 1.55 }}>Be the first to share your performance of this month's offering.</p>
                      <button onClick={() => requireProfile('share', () => setShowUpload(true))} className="font-bold uppercase mt-3 transition-opacity hover:opacity-70" style={{ fontSize: 10, color: '#e63412', letterSpacing: '0.16em', borderBottom: '1px solid #e63412', paddingBottom: 1 }}>
                        Share my interpretation →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Archive */}
            <div style={{ borderBottom: '1px solid #D8D4CC' }}>
              <div className="max-w-6xl mx-auto px-6 py-12">
                <div className="flex items-center gap-4 mb-8">
                  <div>
                    <p className="text-[10px] uppercase font-semibold mb-0.5" style={{ color: '#e63412', letterSpacing: '0.3em' }}>History</p>
                    <h2 className="font-bold text-lg" style={{ color: '#0B0B0B', textTransform: 'none', letterSpacing: '-0.01em' }}>Archive</h2>
                  </div>
                  <div className="flex-1 h-px" style={{ background: '#D8D4CC' }} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {ARCHIVE.map(item => (
                    <button
                      key={item.month}
                      onClick={() => { /* navigate to monthly tab */ setActiveTab('monthly'); window.scrollTo({ top: 0 }) }}
                      className="text-left group transition-all hover:shadow-md focus-visible:outline focus-visible:outline-2"
                      style={{ background: item.current ? '#fff' : '#EFEDE9', border: `1.5px solid ${item.current ? '#e63412' : '#D8D4CC'}`, borderRadius: 3, overflow: 'hidden', outlineColor: '#e63412' }}
                    >
                      <div style={{ height: 3, background: item.current ? '#e63412' : '#C8C4BF' }} />
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-bold uppercase" style={{ fontSize: 9, color: item.current ? '#e63412' : '#737373', letterSpacing: '0.2em' }}>{item.month}</span>
                          {item.current
                            ? <span className="font-bold uppercase" style={{ fontSize: 8, background: '#e63412', color: '#fff', borderRadius: 2, padding: '2px 6px', letterSpacing: '0.12em' }}>Active</span>
                            : <span style={{ fontSize: 11, color: item.entries > 0 ? '#0B0B0B' : '#9a9a9a', fontWeight: item.entries > 0 ? 600 : 400 }}>
                                {item.entries > 0 ? `${item.entries} performances` : 'Be the first to share'}
                              </span>
                          }
                        </div>
                        <p className="font-bold leading-snug mb-1 group-hover:text-[#e63412] transition-colors" style={{ fontSize: 14, color: item.current ? '#0B0B0B' : '#333', textTransform: 'none', letterSpacing: 'normal' }}>{item.title}</p>
                        <p style={{ fontSize: 12, color: item.current ? '#e63412' : '#555', marginBottom: 12 }}>{item.composer}</p>
                        <div className="flex items-center gap-2">
                          <span className="font-bold uppercase" style={{ fontSize: 8, color: '#9a9a9a', letterSpacing: '0.15em' }}>Guest</span>
                          <span style={{ fontSize: 11, color: item.current ? '#0B0B0B' : '#555', fontWeight: 500 }}>{item.guest}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          </Experimental>
        )
      })()}

      {/* Prototype notice */}
      <Experimental>
        <div className="flex items-center justify-center px-6 py-2.5 text-center" style={{ background: '#1a1a1a', borderTop: '1px solid #252525' }}>
          <p style={{ fontSize: 11, color: '#555', letterSpacing: '0.04em', lineHeight: 1.5 }}>
            <span style={{ color: '#737373', fontWeight: 600 }}>Presentation prototype</span> · Community activity, events, and compatibility examples are sample content.
          </p>
        </div>
      </Experimental>
      {/* Footer */}
      <footer style={{ background: '#0B0B0B', borderTop: '1px solid #1a1a1a' }}>
        <div className="mx-auto px-12 py-6" style={{ maxWidth: 1280 }}>
          <div className="flex items-center gap-8 flex-wrap">
            <img src={connectLogo} alt="Rodgers Connect" className="object-contain shrink-0" style={{ maxHeight: 24, filter: 'brightness(0) invert(1)', opacity: 0.7 }} />
            <div className="flex items-center gap-6 flex-wrap flex-1">
              {[{ label: 'Discover Music', tab: 'discover' as const, action: () => setActiveTab('discover') }, { label: 'Community', tab: 'community' as const, action: () => setActiveTab('community') }, { label: 'Monthly Offering', tab: 'monthly' as const, action: () => setActiveTab('monthly') }].filter(t => SHOW_EXPERIMENTAL || t.tab === 'discover').map(({ label, action }) => (
                <button key={label} onClick={action} className="transition-opacity hover:opacity-70" style={{ fontSize: 13, color: '#555' }}>{label}</button>
              ))}
              {/* Support/Privacy point at the real pages on the main site -- there's no
                  RodgersConnect-specific version of either. There's deliberately no "Terms"
                  link here: rodgersinstruments.com has no Terms of Service/Terms of Use page
                  to point at (checked -- its footer only has a copyright notice, a privacy
                  statement, and a product warranty page, none of which is "terms"), and
                  writing one from scratch isn't a call to make without real legal review. */}
              {[
                { label: 'Support', href: 'https://www.rodgersinstruments.com/support' },
                { label: 'Privacy', href: 'https://www.rodgersinstruments.com/privacy-statement' },
              ].map(({ label, href }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-70" style={{ fontSize: 13, color: '#555', textDecoration: 'none' }}>{label}</a>
              ))}
              <button onClick={() => setShowGuidelines(true)} className="transition-opacity hover:opacity-70" style={{ fontSize: 13, color: '#555' }}>Community Guidelines</button>
              <a href="https://www.rodgersinstruments.com" target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-70" style={{ fontSize: 13, color: '#555', textDecoration: 'none' }}>Rodgers Instruments ↗</a>
            </div>
            <p style={{ fontSize: 11, color: '#2a2a2a' }}>© 2026 Rodgers Instruments</p>
          </div>
        </div>
      </footer>

      {/* Sign-in prompt (comment gate) */}
      {showSignInPrompt && !profile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setShowSignInPrompt(null)}>
          <div className="relative w-full max-w-[360px] mx-4 text-center" style={{ background: '#fff', borderRadius: 6, padding: '32px 28px' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowSignInPrompt(null)} className="absolute top-3 right-4 text-xl font-light" style={{ color: '#aaa', lineHeight: 1 }}>×</button>
            <svg className="mx-auto mb-4" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#E63412" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <h3 className="font-bold uppercase mb-2" style={{ fontSize: 13, letterSpacing: '0.2em' }}>Join the conversation</h3>
            <p style={{ fontSize: 14, color: '#737373', lineHeight: 1.55, marginBottom: 24 }}>
              Sign in to leave comments, ask questions, and connect with other Rodgers organists.
            </p>
            <button
              onClick={() => { setShowSignInPrompt(null); setAuthIntent('login'); setEditingProfile(true) }}
              className="btn w-full justify-center mb-3"
              style={{ fontSize: 11, letterSpacing: '0.16em', padding: '12px 0' }}
            >
              Sign In / Join Free
            </button>
            <button onClick={() => setShowSignInPrompt(null)} style={{ fontSize: 12, color: '#aaa' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Create Post Modal */}
      {showCreatePost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.72)' }} onClick={() => setShowCreatePost(false)}>
          <div className="relative w-full max-w-[620px] mx-4" style={{ background: '#fff', borderRadius: 4, padding: 28 }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <p className="text-[11px] font-bold uppercase" style={{ letterSpacing: '0.3em', color: '#0B0B0B' }}>Share with Your Community</p>
              <button onClick={() => setShowCreatePost(false)} style={{ color: '#737373', fontSize: 20, lineHeight: 1 }}>×</button>
            </div>
            {/* Post type selector */}
            <div className="flex gap-2 mb-5">
              {([
                ['performance', 'Performance'],
                ['registration', 'Registration'],
                ['question', 'Question'],
                ['music', 'Music'],
                ['tip', 'Playing Tip'],
              ] as const).map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setCreatePostType(k)}
                  className="text-[10px] font-bold uppercase px-3 py-2 transition-all"
                  style={{
                    letterSpacing: '0.15em',
                    borderRadius: 3,
                    border: createPostType === k ? '1.5px solid #E63412' : '1px solid #D8D4CC',
                    background: createPostType === k ? '#FFF5F3' : '#fff',
                    color: createPostType === k ? '#E63412' : '#737373',
                  }}
                >{label}</button>
              ))}
            </div>
            {/* Fields */}
            <div className="flex flex-col gap-3 mb-5">
              <div>
                <label className="block text-[9px] uppercase font-bold mb-1" style={{ letterSpacing: '0.2em', color: '#737373' }}>Post Title</label>
                <input className="w-full px-3 py-2.5 text-sm" style={{ border: '1px solid #D8D4CC', borderRadius: 3, outline: 'none', color: '#0B0B0B' }} placeholder="Give your post a title…" />
              </div>
              <div>
                <label className="block text-[9px] uppercase font-bold mb-1" style={{ letterSpacing: '0.2em', color: '#737373' }}>Description</label>
                <textarea rows={3} className="w-full px-3 py-2.5 text-sm resize-none" style={{ border: '1px solid #D8D4CC', borderRadius: 3, outline: 'none', color: '#0B0B0B' }} placeholder="Tell the community about this…" />
              </div>
              {(createPostType === 'performance' || createPostType === 'music') && (
                <div>
                  <label className="block text-[9px] uppercase font-bold mb-1" style={{ letterSpacing: '0.2em', color: '#737373' }}>Upload Media or File</label>
                  <div className="flex items-center justify-center gap-3 py-6" style={{ border: '1.5px dashed #D8D4CC', borderRadius: 3, background: '#FAFAF8' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B0AAA0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    <span className="text-[11px]" style={{ color: '#B0AAA0' }}>Drag a file here or click to browse</span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[9px] uppercase font-bold mb-1" style={{ letterSpacing: '0.2em', color: '#737373' }}>Created On</label>
                  <div className="px-3 py-2.5 text-sm" style={{ border: '1px solid #D8D4CC', borderRadius: 3, color: '#0B0B0B', background: '#fff' }}>
                    {profile?.organModel ?? 'Imagine 239'}
                  </div>
                </div>
                <div>
                  <label className="block text-[9px] uppercase font-bold mb-1" style={{ letterSpacing: '0.2em', color: '#737373' }}>Musical Category</label>
                  <select className="w-full px-3 py-2.5 text-sm" style={{ border: '1px solid #D8D4CC', borderRadius: 3, outline: 'none', color: '#0B0B0B', background: '#fff' }}>
                    <option>Sacred / Liturgical</option>
                    <option>Classical</option>
                    <option>Contemporary Worship</option>
                    <option>Orchestral</option>
                    <option>Hymns</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[9px] uppercase font-bold mb-1" style={{ letterSpacing: '0.2em', color: '#737373' }}>Compatible Models</label>
                <input className="w-full px-3 py-2.5 text-sm" style={{ border: '1px solid #D8D4CC', borderRadius: 3, outline: 'none', color: '#0B0B0B' }} placeholder="e.g. Imagine 239, Imagine 241…" />
              </div>
              <div>
                <label className="block text-[9px] uppercase font-bold mb-1" style={{ letterSpacing: '0.2em', color: '#737373' }}>Tags</label>
                <input className="w-full px-3 py-2.5 text-sm" style={{ border: '1px solid #D8D4CC', borderRadius: 3, outline: 'none', color: '#0B0B0B' }} placeholder="e.g. Baroque, Registration Help, Pedal…" />
              </div>
            </div>
            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: '#F0EDE8' }}>
              <button onClick={() => setShowCreatePost(false)} className="text-[10px] font-bold uppercase px-4 py-2.5" style={{ letterSpacing: '0.2em', color: '#737373' }}>Cancel</button>
              <button
                onClick={() => setShowCreatePost(false)}
                className="text-[10px] font-bold uppercase px-5 py-2.5"
                style={{ letterSpacing: '0.18em', background: '#E63412', color: '#fff', borderRadius: 3 }}
              >
                Post to {profile?.organModel ?? 'Imagine 239'} Community
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Rodgers Model Overlay */}
      {showCommunityModelOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.8)' }} onClick={() => setShowCommunityModelOverlay(false)}>
          <div className="relative w-full max-w-[860px] mx-4 max-h-[90vh] overflow-y-auto" style={{ background: '#fff', borderRadius: 4 }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 z-10 px-8 py-6 border-b flex items-start justify-between" style={{ borderColor: '#D8D4CC', background: '#fff' }}>
              <div>
                <p className="text-[9px] uppercase font-bold mb-1" style={{ color: '#E63412', letterSpacing: '0.3em' }}>Switch Community</p>
                <p className="text-xl font-bold uppercase" style={{ letterSpacing: '0.12em', color: '#0B0B0B' }}>Choose Your Rodgers Community</p>
                <p className="text-[12px] mt-1.5" style={{ color: '#737373', maxWidth: 480 }}>Switch communities to explore music, conversations and organists connected to another Rodgers model.</p>
              </div>
              <button onClick={() => setShowCommunityModelOverlay(false)} style={{ color: '#737373', fontSize: 22, lineHeight: 1, marginTop: 4 }}>×</button>
            </div>
            {/* Series tabs */}
            {(() => {
              const OVERLAY_SERIES = ['My Instruments', 'Inspire', 'Imagine', 'Infinity'] as const
              const seriesModels: Record<string, typeof SHOWROOM_MODELS> = {
                'My Instruments': SHOWROOM_MODELS.filter(m => profile?.instruments?.some(i => i.model.includes(m.number))).length > 0
                  ? SHOWROOM_MODELS.filter(m => profile?.instruments?.some(i => i.model.includes(m.number)))
                  : SHOWROOM_MODELS.filter(m => m.series === 'Imagine'),
                'Inspire': SHOWROOM_MODELS.filter(m => m.series === 'Inspire'),
                'Imagine': SHOWROOM_MODELS.filter(m => m.series === 'Imagine'),
                'Infinity': SHOWROOM_MODELS.filter(m => m.series === 'Infinity'),
              }
              const activeModels = seriesModels[overlaySeries] ?? []
              const selectedOrganModel = profile?.organModel ?? 'Imagine 239'

              return (
                <div className="px-8 pb-8">
                  <div className="flex gap-0 border-b mb-6" style={{ borderColor: '#D8D4CC' }}>
                    {OVERLAY_SERIES.map(s => (
                      <button
                        key={s}
                        onClick={() => setOverlaySeries(s)}
                        className="text-[10px] font-bold uppercase px-4 py-3.5 transition-colors"
                        style={{
                          letterSpacing: '0.15em',
                          color: overlaySeries === s ? '#E63412' : '#737373',
                          borderBottom: overlaySeries === s ? '2.5px solid #E63412' : '2.5px solid transparent',
                          marginBottom: -1,
                        }}
                      >{s}</button>
                    ))}
                  </div>
                  {activeModels.length === 0 ? (
                    <div className="py-8 text-center">
                      <p className="text-sm" style={{ color: '#B0AAA0' }}>Legacy model communities coming soon.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-4">
                      {activeModels.map(m => {
                        const isSelected = selectedOrganModel.includes(m.number)
                        return (
                          <button
                            key={m.id}
                            onClick={() => setShowCommunityModelOverlay(false)}
                            className="text-left transition-all"
                            style={{
                              border: isSelected ? '3px solid #E63412' : '1.5px solid #D8D4CC',
                              borderRadius: 4,
                              overflow: 'hidden',
                              position: 'relative',
                            }}
                          >
                            {isSelected && (
                              <>
                                <div className="absolute top-2 left-2 z-10 flex items-center gap-1 px-1.5 py-0.5" style={{ background: '#E63412', borderRadius: 2 }}>
                                  <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                  <span className="text-[8px] font-bold uppercase text-white" style={{ letterSpacing: '0.15em' }}>Your Rodgers</span>
                                </div>
                              </>
                            )}
                            {m.image ? (
                              <div style={{ height: 140, background: '#0B0B0B', overflow: 'hidden' }}>
                                <img src={m.image} alt={m.id} className="w-full h-full object-contain" style={{ padding: 12 }} />
                              </div>
                            ) : (
                              <div style={{ height: 140, background: '#1A1A1A' }} />
                            )}
                            <div className="px-4 py-3" style={{ background: isSelected ? '#FFF5F3' : '#fff' }}>
                              <p className="text-[9px] uppercase font-bold mb-0.5" style={{ letterSpacing: '0.25em', color: isSelected ? '#E63412' : '#737373' }}>{m.series}</p>
                              <p className="text-base font-bold uppercase" style={{ letterSpacing: '0.1em', color: '#0B0B0B' }}>{m.number}</p>
                              <p className="text-[9px] mt-0.5" style={{ color: '#B0AAA0' }}>{m.manuals} Manual{m.manuals !== 1 ? 's' : ''}</p>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )}
                  <div className="mt-6 pt-5 border-t flex items-center justify-between" style={{ borderColor: '#F0EDE8' }}>
                    <button className="text-[10px] font-bold uppercase" style={{ letterSpacing: '0.2em', color: '#E63412' }}>
                      + Add Another Instrument
                    </button>
                    <button onClick={() => setShowCommunityModelOverlay(false)} className="text-[10px] font-bold uppercase px-5 py-2.5" style={{ letterSpacing: '0.15em', background: '#0B0B0B', color: '#fff', borderRadius: 3 }}>
                      Done
                    </button>
                  </div>
                </div>
              )
            })()}
          </div>
        </div>
      )}

      {/* Profile gate nudge — shown when a non-profiled action is triggered (shouldn't happen since we redirect, but safety net) */}
      {profileGateAction && !profile && null}

      {/* ── STICKY MOBILE BOTTOM BAR ── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 flex" style={{ background: '#111', borderTop: '1px solid #1e1e1e', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <button
          onClick={() => { setDiscoverView('library'); setActiveTab('discover'); window.scrollTo({ top: 0 }) }}
          className="flex-1 flex flex-col items-center justify-center gap-1 py-3 transition-opacity active:opacity-60"
          style={{ color: activeTab === 'discover' ? '#E63412' : '#686868' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Search</span>
        </button>
        <button
          onClick={() => { setActiveTab('discover'); setDiscoverView('home'); window.scrollTo({ top: 0 }); const el = document.getElementById('model-selector'); setTimeout(() => el?.scrollIntoView({ behavior: 'smooth' }), 100) }}
          className="flex-1 flex flex-col items-center justify-center gap-1 py-3 transition-opacity active:opacity-60"
          style={{ color: '#686868' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 21h8M12 17v4"/><path d="M7 9l2 2 4-4"/></svg>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>My Rodgers</span>
        </button>
        <button
          onClick={() => requireProfile('share', () => setShowUpload(true))}
          className="flex-1 flex flex-col items-center justify-center gap-1 py-3 transition-opacity active:opacity-60"
          style={{ color: '#686868' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Share</span>
        </button>
        <Experimental>
          <button
            onClick={() => { setActiveTab('community'); window.scrollTo({ top: 0 }) }}
            className="flex-1 flex flex-col items-center justify-center gap-1 py-3 transition-opacity active:opacity-60"
            style={{ color: activeTab === 'community' ? '#E63412' : '#686868' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Community</span>
          </button>
        </Experimental>
      </div>
    </div>
  )
  }

  // Shared overlays -- rendered exactly once regardless of which screen mainContent
  // holds above, so navigating between screens never unmounts/remounts them. See the
  // comment on mainContent's declaration for why that matters.
  return (
    <>
      {mainContent}
      {showUpload && profile && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          uploaderModel={profile.organModel ?? ''}
          onSubmit={entry => {
            setFiles(prev => [entry, ...prev])
            setSortBy('date')
            setActiveCategory('All')
            setActiveModel('All')
            setShowUpload(false)
            // The Facebook share prompt implies a "community sharing" feature that doesn't
            // really exist yet — only offer it on experimental builds.
            if (SHOW_EXPERIMENTAL) setFbShareEntry(entry)
          }}
        />
      )}
      {selected && (
        <DetailPanel
          file={selected}
          onClose={() => setSelected(null)}
          onDelete={isOwnFile(selected, profile?.id) ? () => deleteFile(selected.id) : undefined}
          onPlay={handlePlay}
          isPlaying={playingFileId === selected.id}
          onLike={() => toggleLike(selected.id)}
          liked={likedFiles.has(selected.id)}
          likeCount={likeCounts[selected.id] ?? 0}
          comments={comments.filter(c => c.fileId === selected.id)}
          onAddComment={text => addComment(selected.id, text)}
          profile={profile}
        />
      )}
      {nowPlaying && (
        <AudioPlayerBar
          file={nowPlaying}
          onClose={() => { setNowPlaying(null); setIsAudioPlaying(false) }}
          files={files}
          isPlaying={isAudioPlaying}
          onPlayingChange={setIsAudioPlaying}
        />
      )}
      {showProfileMenu && profile && (
        <ProfileMenu
          profile={profile}
          onViewProfile={() => { setShowProfileMenu(false); setProfileInitialTab({ tab: 'about', nonce: Date.now() }); goToMyProfile() }}
          onViewPerformances={() => { setShowProfileMenu(false); setProfileInitialTab({ tab: 'performances', nonce: Date.now() }); goToMyProfile() }}
          onLogout={logout}
          onClose={() => setShowProfileMenu(false)}
        />
      )}
      {showEditProfile && profile && (
        <EditProfileModal
          profile={profile}
          onSave={setProfile}
          onDeleted={() => { setShowEditProfile(false); logout() }}
          onClose={() => setShowEditProfile(false)}
        />
      )}
      {showAddInstrument && (
        <AddInstrumentModal
          existing={editingInstrument ?? undefined}
          onSave={saveInstrument}
          onClose={() => { setShowAddInstrument(false); setEditingInstrument(null) }}
        />
      )}
      {showGuidelines && <CommunityGuidelinesModal onClose={() => setShowGuidelines(false)} />}
      {/* Facebook share prompt — shown after upload */}
      {fbShareEntry && (() => {
        const shareText = `Just shared "${fbShareEntry.title}" by ${fbShareEntry.composer} on Rodgers Connect — performed on my ${(profile?.organModel ?? 'Rodgers organ').replace(' Series', '')}. Listen and download at rodgersconnect.com`
        const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://rodgersconnect.com')}&quote=${encodeURIComponent(shareText)}`
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.65)' }} onClick={() => setFbShareEntry(null)}>
            <div className="relative w-full max-w-[420px] mx-4" style={{ background: '#fff', borderRadius: 6, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>

              {/* Green success bar */}
              <div className="flex items-center gap-3 px-6 py-4" style={{ background: '#1a7a3a' }}>
                <div className="flex items-center justify-center shrink-0" style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.18)', borderRadius: '50%' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
                <div>
                  <p className="font-bold text-white" style={{ fontSize: 13, letterSpacing: '0.08em' }}>Upload successful!</p>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 1 }}>Your music is now live on Rodgers Connect.</p>
                </div>
                <button onClick={() => setFbShareEntry(null)} className="ml-auto" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 20, lineHeight: 1 }}>×</button>
              </div>

              {/* Share body */}
              <div className="px-8 py-7">
                <div className="flex items-center gap-3 mb-5">
                  <div className="shrink-0 flex items-center justify-center" style={{ width: 44, height: 44, background: '#1877F2', borderRadius: 10 }}>
                    {/* Facebook F */}
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff">
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold" style={{ fontSize: 14, color: '#0B0B0B' }}>Share to Facebook</p>
                    <p style={{ fontSize: 12, color: '#737373' }}>Let your community hear what you shared</p>
                  </div>
                </div>

                {/* Preview card */}
                <div className="mb-6 p-4" style={{ background: '#F5F3EF', border: '1px solid #E0DDD8', borderRadius: 6 }}>
                  <p className="font-bold" style={{ fontSize: 13, color: '#0B0B0B', marginBottom: 2 }}>{fbShareEntry.title}</p>
                  <p style={{ fontSize: 12, color: '#737373', marginBottom: 4 }}>{fbShareEntry.composer}</p>
                  <p style={{ fontSize: 11, color: '#aaa', lineHeight: 1.5 }}>{shareText.slice(0, 100)}…</p>
                </div>

                <a
                  href={fbUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn w-full justify-center mb-3"
                  style={{ background: '#1877F2', fontSize: 12, letterSpacing: '0.12em', padding: '13px 0', textDecoration: 'none', display: 'flex' }}
                  onClick={() => setFbShareEntry(null)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" className="mr-2 shrink-0">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                  </svg>
                  Share on Facebook
                </a>
                <button
                  onClick={() => setFbShareEntry(null)}
                  className="w-full text-center py-2"
                  style={{ fontSize: 12, color: '#aaa', letterSpacing: '0.08em' }}
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </>
  )
}
